import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
import { Link, useSearchParams } from 'react-router-dom';
/*
 * ⚠️ **`plannedProductLines` IS THE MAPPER, AND THERE IS NO SECOND ONE.** It
 * decides the only question turning a plan into a form can get wrong — whether
 * a planned decimal quantity can be a line quantity — and answers `null` rather
 * than rounding when it cannot. Mapping `row.products` by hand here would put
 * that decision in a page.
 */
import { api, plannedProductLines } from '@/lib/api';
/*
 * ⚠️ **THE DOCUMENT HALF OF THIS MODULE, AND ONLY THAT HALF.** The label, the
 * title and the `#id` fallback are shared with the UP dashboard's own
 * line-projects column deliberately — the module's header forbids a
 * document-specific twin, because two copies would be identical the day they
 * were written and drift on the first correction, on cells a reader compares.
 *
 * ⛔ **`PROJECTS_OF_INTEREST_LABEL` AND THE WRITE HELPERS ARE NOT IMPORTED AND
 * MUST NOT BE.** A company's projects of interest are what an ACCOUNT is being
 * chased for; these columns say what a DOCUMENT sells. Neither is a fallback for
 * the other, and this page renders no company row for the first to belong to.
 */
import {
  PROJECTS_ON_DOCUMENT_LABEL,
  companyProjectsPills,
  companyProjectsTitle,
  projectDisplayName,
  type CompanyProject,
} from '@/lib/company-projects';
import { documentCustomerName } from '@/lib/company-names';
import { cx } from '@/lib/cx';
import { formatDateOnly, formatDateTime } from '@/lib/format';
import { NAV_SURFACE_MODULE, denialReason, futureBillablesDenialReason } from '@/lib/permissions';
import { KIND_NOUNS, raisableAs } from '@/lib/tracker-kind';
/*
 * ⚠️ `isAdminTier` and `ApiError` were imported here for the **Sync now**
 * button, which the owner removed on 2026-09-02 ("we only support auto sync").
 * Both went with it. `POST /accounts-dashboard/sheet/sync` still exists on the
 * server as an operator path — the CONTROL was removed, not the endpoint — so
 * do not read their absence as the route having gone.
 */
import { preferencesStore } from '@/lib/preferences-store';
import { useAuth } from '@/lib/use-auth';
import { useSavedFilterApply, useSavedFilters } from '@/lib/use-saved-filters';
import { useProjectOptions } from '@/lib/use-project-options';
import { useUserOptions } from '@/lib/use-user-options';
import { useQuoteStageOptions } from '@/lib/use-quote-options';
import {
  currencyOptions,
  useCurrencyOptions,
  useInvoiceStatusOptions,
} from '@/lib/use-invoice-options';
import { useTableFilters, type ColumnSpec } from '@/lib/use-table-filters';
/*
 * ⚠️ **THREE LAYOUTS, THREE SCOPES, AND THEY CANNOT BE ONE.** The scope string
 * keys a branch of the account's preferences document, and a layout is an
 * ordering plus a hidden set over a NAMED COLUMN LIST — so a single scope shared
 * across these three tables would mean `subject` on quotes, `subject` on
 * invoices and nothing at all on Future billables addressing one stored entry.
 * Hiding Sub total on quotes would hide it on invoices, and every key one table
 * does not declare would be dropped by `applyOrder` on the next save — silently
 * deleting the other two tables' arrangement. See `use-column-layout.ts`.
 */
import { useColumnLayout, useFrozenOffsets } from '@/lib/use-column-layout';
/*
 * ⚠️ **ONE CALL, AT THE PAGE, AND THE MAP IS PASSED DOWN.** Both endpoints are
 * parameterless and the hook fetches once per mount, so calling it again inside
 * each section would be three requests for two answers — against a limit that
 * is per-IP and shared across the office. The page holds it because
 * the page is what mounts the cards; the sections take `overdue` as a prop.
 *
 * ⚠️ *SUPERSEDED: "against a 120-per-60s limit."* It is **12,000/60s** (raised
 * 2026-09-04, corrected 2026-09-17). Three requests for two answers is still
 * two requests too many — the argument is the duplication, not the budget.
 */
import { useAccountsStats } from '@/lib/use-accounts-stats';
import { usePageLayout } from '@/lib/use-page-layout';
import { LayoutGrid, LayoutToolbar } from '@/components/layout';
/*
 * ⚠️ **MOUNTED ONCE, AT THE PAGE, AND FOR THE SAME REASON `useAccountsStats` IS.**
 * The applied filter view's id is what the frame's link hangs off and what the
 * frame's `key` is built from, so it has to live above the sheet branch — and
 * `selectCard` has to be able to apply from up here, in the same act that writes
 * the card's flag into the URL.
 */
import {
  useAccountsSheetFilterView,
  type AccountsSheetFilterSection,
  type AccountsSheetFilterViewApi,
} from '@/lib/use-accounts-sheet-filter-view';
import { isRealDate } from '@/components/catalog/form-utils';
import type {
  AccountsSheetFilterCriteria,
  Invoice,
  InvoiceListParams,
  InvoiceListSort,
  InvoiceTrackerRow,
  InvoiceTrackerSort,
  ListOrder,
  OverdueSummary,
  Paginated,
  Quote,
  QuoteListParams,
  QuoteListSort,
} from '@/lib/types';
import {
  Badge,
  Button,
  ColumnManager,
  Combobox,
  EmptyState,
  Field,
  FilterableTH,
  FilterSheet,
  Input,
  MultiCombobox,
  PageHeader,
  Panel,
  PanelFooter,
  PanelHeader,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
  ValuePills,
} from '@/components/ui';
import type {
  ColumnFilterOption,
  ComboboxOption,
  FilterableTHProps,
  FrozenSide,
} from '@/components/ui';
import { InvoiceMoney, StatusBadge } from '@/components/invoices/InvoiceBits';
import { StageBadge } from '@/components/quotes/QuoteBits';
/*
 * ⚠️ **MOUNTED, NOT COPIED — and both were GREPPED FOR LIVE CALLERS FIRST.**
 * This repo has shipped a feature built into a modal nothing mounts, so an
 * import here is not evidence the component is reachable anywhere else.
 * Measured 2026-09-02: `QuoteFormModal` is mounted by `QuotesPage`,
 * `QuoteDetailPage` and `DealDetailPage`; `InvoiceFormModal` by `QuotesPage`,
 * `QuoteDetailPage`, `InvoicesPage`, `InvoiceDetailPage` and `DealDetailPage`.
 * Both are live surfaces with other callers, so a change made in either is
 * reviewed by five other pages rather than by this one alone.
 */
import { QuoteFormModal } from '@/components/quotes/QuoteFormModal';
import { InvoiceFormModal } from '@/components/invoices/InvoiceFormModal';
/*
 * ⚠️ **ONE PREVIEW HOST FOR THE WHOLE PAGE (owner, 2026-10-25).** The hook is
 * called once in `AccountsDashboardPage` and its `open` is passed down: to the
 * quotes section's actions lane and phone cards, and to the Future billables
 * section's raise flow ("Create and preview"). A host per row would be N viewers
 * in the tree; a host per section would be two for one page.
 */
import {
  PreviewQuotePdfButton,
  useQuotePdfPreview,
  type QuotePdfPreviewTarget,
} from '@/components/quotes/QuotePdfPreview';
import { TrackerSheetPanel } from '@/components/tracker-sheet/TrackerSheetPanel';
import { ExternalTabIcon } from '@/components/tracker-sheet/OpenTrackerSheet';
/*
 * ⚠️ ANOTHER AGENT'S FILES, both mounted rather than rewritten. The cards own
 * their own markup, tone and failure strip; the chip owns every word inside the
 * bracket (`@/lib/overdue-label`). Nothing here restates either.
 */
import {
  AccountsStatCards,
  accountsStatKeysFor,
  accountsStatLabel,
  accountsStatSection,
  accountsStatWarns,
  type AccountsStatKey,
  type AccountsStatSection,
} from '@/components/dashboards/AccountsStatCards';
import { AccountsFilterSummaryBar } from '@/components/dashboards/AccountsFilterSummaryBar';
import { Notice } from '@/components/ui/Notice';
import { TextLink } from '@/components/ui/TextLink';
import { OverdueChip } from '@/components/accounts-dashboard/OverdueChip';
/*
 * ⚠️ ANOTHER AGENT'S FILE. Imported against the contract
 * `{ open, onClose, row?, onCreated }` and neither created nor edited here.
 *
 * ⚠️ **IT IS MOUNTED TWICE, AND THE TWO MOUNTS ARE TWO MODES OF ONE DIALOG.**
 * The page mounts it with no `row` — the create path behind *Add future
 * billables* — and `FutureSection` mounts it with the row somebody pressed
 * *Edit* on. Both are unconditional and closed: the dialog unmounts its own
 * form while `open` is false, so neither fetches anything on an ordinary load,
 * and an edit is seeded from `row` on OPENING rather than re-seeded while open.
 */
import { FutureDocumentDialog } from '@/components/accounts-dashboard/FutureDocumentDialog';
/*
 * ⚠️ **THE SHARED "+" MENU, MOUNTED RATHER THAN RE-IMPLEMENTED.** It is the same
 * component `/companies`, `/deals`, `/quotes` and the University Partnerships
 * dashboard already mount, and the reason it is imported rather than copied is
 * the one its own header gives: two copies agree the day they are written and
 * drift on the first correction, and what would drift is the wording, the
 * keyboard handling and the portalled placement of a control that starts a real
 * revenue document.
 *
 * ⚠️ **`onSelect` IS WHY THIS WORKS AT ALL.** A planned row's basket cannot
 * travel in a URL — a line is `POST /{quotes|invoices}/{id}/products` and cannot
 * exist before its document — so the item is HANDLED here, against the section's
 * own modals, and the `href` beside it is the honest destination if that
 * interception is ever removed.
 */
import {
  RecordCreateMenu,
  type RecordCreateMenuItem,
} from '@/components/records/RecordCreateMenu';

/*
 * THE ACCOUNTS DASHBOARD — three sections over the money side of the CRM.
 *
 *   Quotes            every quote, wide, with the columns a finance reader needs
 *   Invoices          every invoice, the same way
 *   Future documents  work somebody has PLANNED and nobody has raised yet
 *
 * ── IT IS THE CLIENT DELIVERABLES / INVOICE TRACKER SKELETON, DELIBERATELY ──
 *
 * Same chrome, same controls, same words: a URL-backed filter bank with a "More
 * filters" disclosure, per-column funnels through `useTableFilters`, a
 * `lg:hidden` card list beside the desktop table, a `PanelFooter` pager, and an
 * `EmptyStateSlot` that tells error / column-filters-hid-everything / no rows
 * apart. The owner asked for this to look like the Client Deliverables tracker
 * and the Invoice Tracker was built from that page hours before this one; a
 * third layout would make three finance screens that each behave slightly
 * differently.
 *
 * ── WHAT THIS PAGE WRITES, AND WHAT IT ONLY READS ──────────────────────────
 *
 * The Quotes and Invoices sections are READ-ONLY LISTS with the ordinary
 * create/edit modals mounted over them — `QuoteFormModal` and
 * `InvoiceFormModal`, the same ones the deals flow uses. Nothing here has its
 * own form: a document created from this page and one created from `/quotes` is
 * the same request, the same refusals and the same Xero behaviour.
 *
 * The Future section reads `GET /invoice-tracker?view=pending&source=standalone`
 * — the rows somebody typed, narrowed SERVER-SIDE so the count and the pager
 * describe what is on screen. Its per-row action opens one of those same two
 * modals prefilled from the planned row, and on save it calls
 * `POST /invoice-tracker/{id}/raised` to ATTACH the plan to the document it
 * produced: the row keeps its id and its comment, changes population from
 * standalone to attached, and thereafter shows against that quote or invoice
 * instead of here. **Nothing is deleted.**
 *
 * ⚠️ **RAISING CREATES A REAL FINANCIAL DOCUMENT AND THE SAVE PUSHES IT TO
 * XERO, AND XERO DOES NOT ENFORCE INVOICE-NUMBER UNIQUENESS.** A second press
 * is not a harmless repeat — it is a second live demand for money against one
 * customer, and nothing downstream refuses it. See `ConvertPlannedAction` for
 * the things that hold that off.
 *
 * ⚠️ **AND A FAILED ATTACH IS NOT A FAILED RAISE.** The document exists by the
 * time that call is made, so its failure is never worded as one and never
 * re-enables the raise — see `UnlinkedRaiseBanner`.
 *
 * ── THERE IS NO OWNER COLUMN, AND THAT IS THE OWNER'S OWN RULING ───────────
 *
 * ⚠️ Owner was retired from companies, contacts, deals, quotes and invoices on
 * 2026-08-31, in favour of the company's account lead. `/quotes` and
 * `/invoices` both dropped their Owner column in that pass. Re-adding one on a
 * new page is re-adding what was removed, in a place nobody reviewed — so this
 * table shows **Account lead** and no owner. If an owner column is wanted it
 * goes back to the owner as a decision.
 *
 * ── THE XERO REFERENCE IS A REFERENCE, NEVER A STATUS ──────────────────────
 *
 * ⚠️ `xeroQuoteNumber` / `xeroInvoiceNumber` are rendered as what they are: the
 * finance system's own reference, for a person to match against. **A dash means
 * "no number recorded" and nothing more.** It must never be labelled "Not in
 * Xero": the number is written best-effort by a push and can be absent on a
 * linked document or present after an unlink, and the `/quotes` Xero column was
 * removed on the owner's instruction with an explicit "do not compensate by
 * putting an indicator somewhere else". The authoritative answer lives on the
 * record's own `XeroPanel`.
 */

/** The server defaults `perPage`; every section is read a page at a time. */
const PER_PAGE = 50;
/** Fewer than a full page: enough to read as a list, not a wall of shimmer. */
const SKELETON_ROWS = 6;
/** One page of picker options. Companies are paged in whole; see `useCompanyOptions`. */
const OPTION_PER_PAGE = 100;

/** A positive integer id as the URL spells it. */
const ID_PATTERN = /^[1-9]\d*$/;

/**
 * An id filter as the URL spells it: one id or a comma-separated list (owner
 * 2026-10-28, every filter takes several). Each member is validated on its own
 * and duplicates collapse, so `?companyId=3,abc,3` narrows on `3` rather than
 * forwarding a 422. `''` = no filter. A single id (every old bookmark) reads
 * back unchanged.
 */
function readIdList(raw: string | null): string {
  if (!raw) return '';
  const ids = raw
    .split(',')
    .map((part) => part.trim())
    .filter((part) => ID_PATTERN.test(part));
  return [...new Set(ids)].join(',');
}

/** The members of a {@link readIdList} value. */
function idListValues(value: string): string[] {
  return value === '' ? [] : value.split(',');
}

/**
 * An id-list filter on the wire: a number for one id (the request every old
 * bookmark already sent), the CSV itself for several.
 */
function idListParam(value: string): number | string {
  return value.includes(',') ? value : Number(value);
}

/**
 * The account lead on `/quotes` and `/invoices`: one id stays
 * `accountLeadId`; several go as `accountLeadIds` (CSV of resolved-lead ids,
 * never sent with `accountLeadId`).
 */
function accountLeadParam(value: string): { accountLeadId?: number; accountLeadIds?: string } {
  if (value === '') return {};
  return value.includes(',') ? { accountLeadIds: value } : { accountLeadId: Number(value) };
}
/** `^[A-Z]{3}$` server-side — "usd" is a 422 rather than a case-insensitive match. */
const CURRENCY_PATTERN = /^[A-Z]{3}$/;
/** A plain `YYYY-MM-DD`; `isRealDate` then rejects 2026-02-30, which a regex accepts. */
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
/** Money as text. Never `Number()` — a decimal string is exact and a float is not. */
const AMOUNT_PATTERN = /^\d+(\.\d{1,2})?$/;

/**
 * How an ON flag filter is spelled in the query string, and the ONLY value any
 * of the three is read back from.
 *
 * ⚠️ **`awaitingSignature`, `unsettled` and `overdue` are typed on the wire as
 * the LITERAL `true`, never `boolean`** — see `QuoteListParams.awaitingSignature`
 * for the reasoning. `listQuery()` drops `undefined`, `null` and `''` but keeps
 * `false`, so a `false` would go out as `?overdue=false` against a `.strict()`
 * schema. Declaring the literal makes the omission structural rather than a
 * convention somebody has to remember, which is why the params builders below
 * all read `...(on ? { key: true } : {})`. **Do not widen those to `boolean`.**
 *
 * ⚠️ **The reader accepts EXACTLY this string and nothing else** — not `1`, not
 * `yes`, not any truthy value. A URL carrying `?overdue=maybe` narrows nothing,
 * which is the same treatment `readQuoteFilters` already gives a half-typed
 * date: the alternative is a 422 that empties the table and reads as an outage.
 */
const FLAG_ON = 'true';

/**
 * The two options behind a flag filter's picker. One real value, and the
 * `emptyOptionLabel` supplies the "any" row — the same shape every other
 * `FilterField` on this page uses, so a flag reads and clears like an id.
 */
const FLAG_OPTIONS: ComboboxOption[] = [{ value: FLAG_ON, label: 'Only these' }];

/* -------------------------------------------------------------------------
 * The section
 * --------------------------------------------------------------------- */

/**
 * ⚠️ **AN ALIAS OF THE CARD COMPONENT'S UNION, NOT A SECOND LIST OF THE SAME
 * THREE STRINGS.** Since the stat cards became per-section, `AccountsStatCards`
 * carries a `section` on every card and an exhaustive column-count record keyed
 * by this union — and both of those are only exhaustive over ONE union. Two
 * identical declarations would agree the day they were written; the day they
 * stopped, `tsc` would go on passing and a section would simply draw no cards.
 *
 * The union lives there rather than here because this page imports that
 * component and not the other way round. Everything a section MEANS on this page
 * — its label, its chip, how it is read out of the URL — is still declared below.
 */
type Section = AccountsStatSection;

/**
 * ⚠️ **THE LABEL IS "Future Billables"; THE KEY IS STILL `future`.** Owner's
 * rename, asked for twice — 2026-09-02 as "Future Billable" and again as the
 * PLURAL "Future Billables" — and it is a rename of the WORD ON SCREEN only.
 * `future` is in `?section=`, in every link anybody has shared, in the
 * saved-filter scope names below and in the Google Sheets tab the backend
 * mirrors — so renaming the value would break links and quietly re-scope saved
 * filters, while renaming the label costs nothing. If the server key ever has
 * to move, that is a contract change and belongs to the API, not to this
 * constant.
 *
 * ⚠️ **`SECTION_LABEL.future` IS THE ONLY SPELLING. Do not compose a longer
 * heading out of it.** The panel title used to read
 * "Future Billable quotes and invoices"; the owner asked for the chip and the
 * heading to say the same two words, so the heading is the label alone.
 */
const SECTION_OPTIONS: readonly { value: Section; label: string }[] = [
  { value: 'quotes', label: 'Quotes' },
  { value: 'invoices', label: 'Invoices' },
  { value: 'future', label: 'Future Billables' },
];

/** The one place the section's user-visible name is spelled. */
const SECTION_LABEL: Readonly<Record<Section, string>> = {
  quotes: 'Quotes',
  invoices: 'Invoices',
  future: 'Future Billables',
};

/**
 * ⚠️ **`quotes` is the default and is spelled by the key's ABSENCE.**
 * `?section=invoices` and `?section=future` are the only recognised non-defaults,
 * so junk, `?section=quotes` and no param at all are one and the same URL — one
 * canonical address per section, and a mistyped section is the quotes table
 * rather than a 404. Same rule `?view=` follows on the Invoice Tracker.
 */
function readSection(params: URLSearchParams): Section {
  const raw = params.get('section');
  return raw === 'invoices' || raw === 'future' ? raw : 'quotes';
}

/* -------------------------------------------------------------------------
 * Table / Sheet
 * --------------------------------------------------------------------- */

/**
 * 🔴 **THE SHEET IS THE DEFAULT — OWNER'S INSTRUCTION, 2026-09-02:** *"the sheet
 * view will be a primary view"*. `?view=table` is the opt-in, exactly as on
 * `ClientDeliverablesPage`, which he asked this page to match.
 *
 * ⚠️ *SUPERSEDED, and it was a real objection rather than an oversight:* "**THE
 * TABLE IS THE DEFAULT AND MUST STAY THE DEFAULT.** It is the view that filters,
 * and — the half that is easy to forget — the only one that works for a reader
 * with no Google access at all. The mirror is a convenience over the same data;
 * opening onto it would put a document somebody may not be able to see in front
 * of them instead of the page they navigated to."
 *
 * ⚠️ **THE COST IT NAMED IS REAL, IS NOT REMOVED, AND CANNOT BE DETECTED FROM
 * HERE.** A cross-origin iframe reports NOTHING about whether it painted — no
 * usable `onError`, no readable status — so a reader whose Google account cannot
 * see this spreadsheet lands on a blank rectangle and this app will never know.
 * Two things are therefore load-bearing rather than decorative, and neither may
 * be moved behind a working frame:
 *
 * - the **Table** chip in `VIEW_OPTIONS`, which is the way back out and is
 *   rendered exactly when the sheet can be;
 * - `TrackerSheetPanel`'s unconditional *"Not showing above? Open the sheet in
 *   Google Sheets"* line, which sits OUTSIDE the frame and is the only route out
 *   of a blank one.
 *
 * ⚠️ **`canShowSheet` still gates the resolution**, so a deployment with no
 * mirror configured is unaffected by this change and lands on the table.
 */
type TrackerView = 'table' | 'sheet';

const VIEW_OPTIONS: readonly { value: TrackerView; label: string }[] = [
  { value: 'table', label: 'Table' },
  { value: 'sheet', label: 'Sheet' },
];

/**
 * Where the Accounts Dashboard's Google Sheets mirror lives.
 *
 * ⚠️ **A LOCAL READER RATHER THAN `useTrackerSheetLink`, AND THAT IS A FORK
 * RATHER THAN A PREFERENCE.** That hook is hard-wired to
 * `api.clientDeliverableSheet()` and its module-level cache holds ONE answer for
 * the whole tab, so pointing this page at it would serve the Client
 * Deliverables spreadsheet under an Accounts heading — a wrong document, framed,
 * with nothing failing. Parameterising it is an edit to a file this change does
 * not own. `TrackerSheetPanel` IS reused verbatim, because it takes its two URLs
 * as props and knows nothing about which mirror it is showing.
 *
 * Every failure degrades to "no link" and the caller renders nothing, exactly as
 * the shared reader does: this is an optional way to reach a document, and a red
 * banner about a mirror the reader may not use would be louder than the thing it
 * reports. A 404 is not even a failure — it is what a server predating the route
 * answers.
 */
interface SheetState {
  loading: boolean;
  link: string | null;
  previewLink: string | null;
  /**
   * The Google `gid` of each section's tab, as a string ready for a URL
   * fragment. **A section absent from this map has an UNKNOWN gid**, which is
   * not the same as "no tab" and is never to be papered over with a default —
   * see `sheetHrefFor`.
   */
  tabs: Partial<Record<Section, string>>;
  /**
   * ⚠️ **THREE STATES, AND TWO OF THEM LOOK ALIKE ON SCREEN IF YOU LET THEM.**
   *
   * ```
   * known: false            the server did not send the key at all — this app
   *                         deploys separately and can be newer than the API
   * known: true,  at: null  the key came back null: the mirror has NEVER RUN
   * known: true,  at: '…'   an ISO instant
   * ```
   *
   * The whole reason this field exists is an owner report: a planned row was
   * added, the spreadsheet opened, the row was not there, and the mirror was
   * reported broken. It was not — the worker ticks every ten minutes and had
   * not yet. So **neither absent nor null may render as a blank, and neither
   * may render as "just now"**: a missing timestamp read as freshness is the
   * very defect this is here to close, rebuilt one layer up.
   */
  lastSyncedKnown: boolean;
  lastSyncedAt: string | null;
}

const EMPTY_SHEET_STATE: SheetState = {
  loading: false,
  link: null,
  previewLink: null,
  tabs: {},
  lastSyncedKnown: false,
  lastSyncedAt: null,
};

/**
 * A gid, normalised to the string a URL fragment wants.
 *
 * ⚠️ **`0` IS A VALID GID** — it is what Google gives the first tab of a new
 * spreadsheet, which on this mirror is `Quotes`. So this tests the TYPE and
 * never the truthiness; `if (!gid)` here would silently drop the deep link for
 * exactly one section and leave it landing on the first tab, which is the
 * failure this whole change is about and would look correct on that one tab.
 */
function normaliseGid(value: unknown): string | null {
  if (typeof value === 'number') return Number.isInteger(value) ? String(value) : null;
  if (typeof value === 'string' && /^\d+$/.test(value)) return value;
  return null;
}

function asSection(value: unknown): Section | null {
  return value === 'quotes' || value === 'invoices' || value === 'future' ? value : null;
}

/**
 * The per-section gids, read STRUCTURALLY off the response rather than through
 * a declared type.
 *
 * ⚠️ **THIS IS A DELIBERATE SEAM AND IT IS TEMPORARY.** `AccountsDashboardSheetLink`
 * lives in `src/lib/types.ts`, which this change does not own and which another
 * agent is editing right now to carry these fields. Reading through `unknown`
 * means this compiles against the type as it stands today AND starts working
 * the moment the API sends the data — where a typed read would have to land in
 * the same commit as somebody else's file.
 *
 * **TODO (owner of `src/lib/types.ts`):** once `AccountsDashboardSheetLink`
 * declares the per-section gids and `lastSyncedAt`, delete both readers and
 * read the typed fields. Until then this is the only thing standing between a
 * renamed wire key and a silently wrong tab, because nothing here can fail.
 *
 * Both encodings the backend could plausibly ship are accepted — the array
 * form, which mirrors the `sync` response's existing
 * `sections: [{ section, tabTitle, status }]`, and a record keyed by section.
 * That is tolerance of an encoding, not a guess at a meaning: the field was
 * specified, its SHAPE was not, and accepting both costs six lines where
 * picking one costs a silently dead deep link.
 */
function readSectionTabs(data: unknown): Partial<Record<Section, string>> {
  if (typeof data !== 'object' || data === null) return {};
  const sections = (data as { sections?: unknown }).sections;
  const tabs: Partial<Record<Section, string>> = {};

  if (Array.isArray(sections)) {
    for (const entry of sections) {
      if (typeof entry !== 'object' || entry === null) continue;
      const record = entry as { section?: unknown; sheetId?: unknown };
      const section = asSection(record.section);
      const gid = normaliseGid(record.sheetId);
      if (section !== null && gid !== null) tabs[section] = gid;
    }
    return tabs;
  }

  if (typeof sections === 'object' && sections !== null) {
    for (const [key, value] of Object.entries(sections as Record<string, unknown>)) {
      const section = asSection(key);
      if (section === null) continue;
      const gid = normaliseGid(
        typeof value === 'object' && value !== null
          ? (value as { sheetId?: unknown }).sheetId
          : value,
      );
      if (gid !== null) tabs[section] = gid;
    }
  }

  return tabs;
}

/**
 * See `SheetState.lastSyncedKnown` for why absence and `null` are separate.
 *
 * ⚠️ **THE SERVER REPORTS THIS PER SECTION, NOT AT THE TOP LEVEL, AND READING
 * ONLY THE TOP LEVEL IS WHAT SHIPPED FIRST.** Measured against the deployed API
 * on 2026-09-02: `/accounts-dashboard/sheet` answers `configured`,
 * `spreadsheetUrl`, `spreadsheetPreviewUrl`, `observingSince` and `sections`,
 * and every `lastSyncedAt` lives on a `sections` entry. So the top-level read
 * found nothing and the panel said *"not reported by this server"* about a
 * server that was reporting it on every one of three tabs.
 *
 * **It failed in the honest direction, which is the only reason it was a
 * cosmetic bug rather than a lie** — the branch for "cannot tell" exists
 * precisely so a shape nobody promised is never rendered as freshness.
 *
 * Per section is also the RIGHT granularity rather than a workaround: the panel
 * frames one tab at a time, the three tabs are written by three independent
 * passes, and a partial sync genuinely leaves them at different times. A single
 * figure would have to pick one and would be wrong about the other two.
 */
function readLastSyncedFor(
  data: unknown,
  section: Section,
): { known: boolean; at: string | null } {
  if (typeof data !== 'object' || data === null) return { known: false, at: null };
  const sections = (data as { sections?: unknown }).sections;
  let scoped: unknown;
  let found = false;
  if (Array.isArray(sections)) {
    for (const entry of sections) {
      if (typeof entry !== 'object' || entry === null) continue;
      const record = entry as { section?: unknown; lastSyncedAt?: unknown };
      if (asSection(record.section) !== section) continue;
      // `in` and not truthiness: `null` is a real answer here (never synced).
      if ('lastSyncedAt' in record) { scoped = record.lastSyncedAt; found = true; }
      break;
    }
  } else if (typeof sections === 'object' && sections !== null) {
    const entry = (sections as Record<string, unknown>)[section];
    if (typeof entry === 'object' && entry !== null && 'lastSyncedAt' in entry) {
      scoped = (entry as { lastSyncedAt?: unknown }).lastSyncedAt;
      found = true;
    }
  }
  if (found) return coerceInstant(scoped);
  // Fall back to a top-level key, which an older or a future server may carry.
  if (!('lastSyncedAt' in data)) return { known: false, at: null };
  return coerceInstant((data as { lastSyncedAt?: unknown }).lastSyncedAt);
}

function coerceInstant(value: unknown): { known: boolean; at: string | null } {
  if (typeof value === 'string' && value.trim() !== '') return { known: true, at: value };
  if (value === null) return { known: true, at: null };
  // Anything else is a shape nobody promised. Reporting it as "never synced"
  // would be a claim; `known: false` is the honest "we cannot tell".
  return { known: false, at: null };
}

/*
 * ⚠️ **THE `reloadToken` PARAMETER WENT WITH THE `Sync now` BUTTON (2026-09-02).**
 * Its only purpose was to re-read the link after a manual run so the freshness
 * line was the SERVER's answer rather than this app's memory of having pressed
 * something. With no button there is nothing to re-read after, and a parameter
 * whose only argument is a constant is a seam that reads as wired and is not.
 * The read still re-runs on a section change, which is the only other thing
 * that changes the answer.
 */
function useAccountsSheetLink(section: Section): SheetState {
  const { user } = useAuth();
  const signedIn = user !== null;
  const [state, setState] = useState<SheetState>({ ...EMPTY_SHEET_STATE, loading: true });

  useEffect(() => {
    if (!signedIn) return;
    const controller = new AbortController();
    api
      .accountsDashboardSheet(controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return;
        const raw: unknown = response.data;
        const lastSynced = readLastSyncedFor(raw, section);
        setState({
          loading: false,
          link: response.data?.spreadsheetUrl ?? null,
          /*
           * ⚠️ Normalised to `string | null` HERE and nowhere else.
           * `spreadsheetPreviewUrl` is optional on the wire because a server
           * predating it omits the key, and an iframe handed `undefined` does
           * not throw or warn — it resolves to a relative URL and quietly frames
           * this app inside itself.
           */
          previewLink: response.data?.spreadsheetPreviewUrl ?? null,
          tabs: readSectionTabs(raw),
          lastSyncedKnown: lastSynced.known,
          lastSyncedAt: lastSynced.at,
        });
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setState(EMPTY_SHEET_STATE);
      });
    return () => controller.abort();
  }, [signedIn, section]);

  return signedIn ? state : EMPTY_SHEET_STATE;
}

/**
 * The address of ONE SECTION's tab.
 *
 * ⚠️ **NO GID MEANS NO FRAGMENT, AND THAT IS THE WHOLE DECISION.** A URL built
 * from an undefined gid — `#gid=undefined`, or the bare document — opens the
 * spreadsheet's FIRST tab, renders perfectly, and puts Quotes under a
 * "Future Billable" heading with nothing failing. That is the bug this change
 * exists to fix, in a new costume. So the fragment is appended only when the
 * gid is genuinely known, and every CALLER treats "no href" as "there is no
 * per-section link", never as "link to the document and hope".
 */
function sheetHrefFor(
  base: string | null,
  gid: string | undefined,
  /**
   * ⚠️ **`fvid` RIDES IN THE SAME FRAGMENT, AND IT IS NEVER APPENDED ALONE.** A
   * filter view belongs to ONE TAB, so `#fvid=` with no `gid` would name a view
   * on whichever tab Google opened first — the same defect the gid gate exists
   * to close, wearing the new feature's costume. It is a parameter of this
   * function rather than a second helper for that reason: there is one place a
   * Google fragment is built here and it stays one.
   *
   * ⚠️ **`null` AND `undefined` BOTH MEAN "NO FILTER VIEW"** — the wire sends
   * `null` for a filter set that emptied, and a caller that has never applied one
   * sends nothing at all. Those are the same thing to a frame.
   */
  filterViewId?: number | null,
): string | null {
  if (base === null || gid === undefined) return null;
  if (filterViewId === null || filterViewId === undefined) return `${base}#gid=${gid}`;
  return `${base}#gid=${gid}&fvid=${filterViewId}`;
}

/* -------------------------------------------------------------------------
 * Icons — Lucide paths, drawn by hand rather than pulling in a dependency.
 * --------------------------------------------------------------------- */

function SearchIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

function FilterIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <path d="M4 6h16" />
      <path d="M7 12h10" />
      <path d="M10 18h4" />
    </svg>
  );
}

/**
 * ⚠️ **BYTE-FOR-BYTE THE `PlusIcon` `@/components/catalog/icons` EXPORTS, AND
 * SINCE 2026-09-13 THAT AGREEMENT IS LOAD-BEARING RATHER THAN A COINCIDENCE.**
 * `ConvertPlannedAction` mounts `RecordCreateMenu`, which draws the shared one,
 * and falls back to a DISABLED trigger drawn with THIS one when a row cannot be
 * raised. The two have to be pixel identical: a refusal that is a different
 * shape from the live control reads as a second, broken control rather than as
 * the same one standing down.
 *
 * ⛔ **SO DO NOT RE-CUT THIS ONE ALONE.** It was not imported instead because
 * this page draws its whole icon set locally — see the block above — and one
 * imported icon among six local ones is the inconsistency somebody "tidies"
 * back. Move both, or neither.
 */
function PlusIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </svg>
  );
}

/** A pie chart — the same glyph the Accounts row carries in the sidebar. */
function AccountsIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-5 w-5"
    >
      <path d="M12 2.5V12h9.5a9.5 9.5 0 0 0-9.5-9.5Z" />
      <path d="M20.9 15.6A9.5 9.5 0 1 1 8.4 3.1" />
    </svg>
  );
}

function ChevronIcon({ direction }: { direction: 'left' | 'right' }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-3.5 w-3.5"
    >
      <path d={direction === 'left' ? 'm15 18-6-6 6-6' : 'm9 18 6-6-6-6'} />
    </svg>
  );
}

/* -------------------------------------------------------------------------
 * Shared filter machinery
 * --------------------------------------------------------------------- */

/**
 * Options for the company filter: every company, read once per mount.
 *
 * *SUPERSEDED 2026-10-28: "searched server-side", one page per typed term.* The
 * filter takes several companies now and its control searches locally.
 */
function useCompanyOptions() {
  const [options, setOptions] = useState<ComboboxOption[]>([]);
  const [failed, setFailed] = useState(false);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  /*
   * ⚠️ **THE WHOLE DIRECTORY, PAGE BY PAGE (2026-10-28).** The company filters
   * are multi-selects now, and `MultiCombobox` filters locally with no
   * `onSearch`, so a list cut at the first page would make every company past
   * it unreachable. Capped at `COMPANY_OPTION_MAX_PAGES` pages.
   */
  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    void (async () => {
      try {
        const collected: ComboboxOption[] = [];
        let page = 1;
        let totalPages = 1;
        let seenTotal = 0;
        do {
          const result = await api.listCompanies(
            { page, perPage: OPTION_PER_PAGE, sort: 'name', order: 'asc' },
            signal,
          );
          if (signal.aborted) return;
          for (const company of result.data) {
            collected.push({ value: String(company.id), label: company.name });
          }
          seenTotal = result.meta.total;
          totalPages = result.meta.totalPages;
          page += 1;
        } while (page <= totalPages && page <= COMPANY_OPTION_MAX_PAGES);
        setOptions(collected);
        setTotal(seenTotal);
        setFailed(false);
      } catch {
        if (signal.aborted) return;
        setOptions([]);
        setFailed(true);
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, []);

  return { options, loading, failed, total };
}

/** 20 × 100 companies; the directory is ~415 rows. */
const COMPANY_OPTION_MAX_PAGES = 20;

/**
 * Keeps a selected filter renderable when its id is not in the loaded options.
 *
 * Three ways that happens: the id arrived on the URL before the options did, the
 * list is one page of a longer one, or the value is a master row since
 * deactivated — a retired row still filters correctly server-side, but the
 * option hooks list active rows only. With even a row-derived label missing we
 * show the bare id rather than an empty trigger, which would read as "no filter"
 * while the list stayed filtered.
 */
function withSelected(
  options: ComboboxOption[],
  value: string,
  labels: Map<string, string>,
  noun: string,
): ComboboxOption[] {
  // `value` may be a comma-separated list: every selected member is kept.
  const present = new Set(options.map((option) => option.value));
  const missing = idListValues(value).filter((member) => !present.has(member));
  if (missing.length === 0) return options;
  return [
    ...missing.map((member) => {
      const known = labels.get(member);
      return known
        ? { value: member, label: known }
        : { value: member, label: `${noun} #${member}`, hint: 'Not in the list below' };
    }),
    ...options,
  ];
}

/**
 * One picker filter.
 *
 * No `hint` prop, deliberately: helper text is prohibited across this app, and a
 * prop nobody may pass is one somebody eventually does. `error` stays — a failed
 * option fetch is a refusal the reader has to see, not advice.
 */
function FilterField({
  label,
  value,
  options,
  onChange,
  allLabel,
  loading,
  failed,
  onSearch,
  searchPlaceholder,
  multi = false,
}: {
  label: string;
  value: string;
  options: ComboboxOption[];
  onChange: (value: string) => void;
  allLabel: string;
  loading: boolean;
  failed: boolean;
  onSearch?: (term: string) => void;
  searchPlaceholder?: string;
  /**
   * Several values, as one comma-separated `value` (owner 2026-10-28). Id
   * filters only: currency and the flags stay single. `onSearch` is not used
   * here, so a multi field's `options` must be the whole list.
   */
  multi?: boolean;
}) {
  return (
    <Field
      label={label}
      {...(failed ? { error: `Couldn’t load ${label.toLowerCase()} options.` } : {})}
    >
      {({ id, 'aria-describedby': describedBy }) =>
        multi ? (
          <MultiCombobox
            id={id}
            values={idListValues(value)}
            onChange={(values) => onChange(values.join(','))}
            options={options}
            placeholder={allLabel}
            loading={loading}
            searchPlaceholder={searchPlaceholder}
            aria-describedby={describedBy}
          />
        ) : (
        <Combobox
          id={id}
          value={value}
          onChange={onChange}
          options={options}
          emptyOptionLabel={allLabel}
          placeholder={allLabel}
          loading={loading}
          onSearch={onSearch}
          searchPlaceholder={searchPlaceholder}
          aria-describedby={describedBy}
        />
        )
      }
    </Field>
  );
}

/** A plain text filter — the date bounds and the two amount bounds. */
function TextFilterField({
  label,
  value,
  onChange,
  type = 'text',
  inputMode,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: 'text' | 'date';
  inputMode?: 'text' | 'decimal';
  placeholder?: string;
}) {
  return (
    <Field label={label}>
      {({ id, 'aria-describedby': describedBy }) => (
        <Input
          id={id}
          type={type}
          inputMode={inputMode}
          placeholder={placeholder}
          value={value}
          aria-describedby={describedBy}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </Field>
  );
}

/* -------------------------------------------------------------------------
 * Cells
 * --------------------------------------------------------------------- */

/** Nothing recorded, rendered as a mark rather than as blank space. */
function NotRecorded() {
  return <span className="text-xs text-fg-subtle">—</span>;
}

/**
 * A `{id, name}` reference, linked where there is a page to link to.
 *
 * ⚠️ **ONE LINE, ELLIPSIS, AND A `title` CARRYING THE WHOLE VALUE.** A name is a
 * label rather than prose — two lines of it buys almost nothing and makes every
 * row in the table taller — but a clipped name with no way to read it is the
 * complaint this page was reported for. The `title` is the way back to the full
 * string, and it costs nothing.
 */
function RefCell({ to, name }: { to: string | null; name: string | null | undefined }) {
  const label = name?.trim();
  if (!label) return <NotRecorded />;
  if (to === null)
    return (
      <span title={label} className="block truncate text-xs text-fg-secondary">
        {label}
      </span>
    );
  return (
    <TextLink to={to} title={label} className="block truncate text-xs">
      {label}
    </TextLink>
  );
}

/**
 * The projects a document's LINE ITEMS name — its own scope, as a SET.
 *
 * 🔴 **THIS REPLACED A CELL READING THE HEADER COLUMN, WHICH WAS BLANK ON EVERY
 * ROW IN PRODUCTION.** *SUPERSEDED, on quotes and invoices alike:
 * `<RefCell to={row.project ? …} name={row.project?.name} />`.* Migrations
 * 067–069 moved the project onto the LINE and the header pickers were removed on
 * the owner's instruction, so nothing has written `d_quotes.project_id` or
 * `d_invoices.project_id` since — **measured 2026-09-15: NULL on all 279 quotes
 * and all 809 invoices**, against 119 quotes and 356 invoices whose lines name
 * one. The old cell was not wrong about the column it read; it read a column
 * nothing fills.
 *
 * ⚠️ **NOT A LINK, AND THAT IS THE SET'S DOING.** `RefCell` resolves ONE record
 * to one route; a document naming three projects has no single page to send
 * somebody to, and linking the first would be a confident wrong answer about
 * which of them the row is "for". The names are the value here; the record pages
 * are a click away through the document itself.
 *
 * ⚠️ **`companyProjectsPills` / `companyProjectsTitle`, NOT a second copy.** The
 * `#id` fallback for a blank name is the UP dashboard's exactly —
 * `company-projects.ts` says in terms that a document-specific twin must not be
 * added. 🔴 **EVERY project renders as a wrapping pill since 2026-09-25 (owner's
 * decision)** — *SUPERSEDED: `companyProjectsLabel`'s two-name cap and `+N`
 * marker, truncated by CSS* — so the row grows instead of the tail being hidden.
 * The `title` is kept for the joined list.
 *
 * ⚠️ **EMPTY RENDERS `NotRecorded`, not the helper's own dash.** Both draw the
 * same glyph; only this one carries the muted tone every other empty cell on
 * these three tables uses. It is the MAJORITY answer — 160 of 279 quotes name no
 * project on any line — so it must read as the ordinary absence it is.
 */
function DocumentProjectsCell({ projects }: { projects: readonly CompanyProject[] }) {
  if (projects.length === 0) return <NotRecorded />;
  const title = companyProjectsTitle(projects);
  return (
    /* Spread conditionally: `companyProjectsTitle` answers `undefined` on an
       empty set so React omits the attribute, and an empty `title` renders a
       blank tooltip box that reads as a broken one. Unreachable past the guard
       above, and written this way so it stays correct if the guard moves. */
    <ValuePills
      items={companyProjectsPills(projects)}
      {...(title ? { title } : {})}
    />
  );
}

/**
 * The document's line projects as the ONE scalar `ColumnSpec.get` may answer.
 *
 * ⚠️ **FOR THE EMPTINESS TEST ALONE.** `get` decides whether a row counts as
 * empty for `Is empty` / `Is not empty`, and — where no `options` are supplied —
 * is also accumulated into the funnel's list. A joined string is the right answer
 * to the first question and useless as the second, which is why every caller of
 * this pairs it with supplied `options` and a `matches`. See
 * {@link lineProjectOptions}.
 */
function lineProjectsValue(projects: readonly CompanyProject[]): string | null {
  return projects.length === 0 ? null : projects.map(projectDisplayName).join(', ');
}

/**
 * The funnel's options for a line-projects column: the DISTINCT projects the
 * loaded rows name, one per option.
 *
 * ⛔ **SUPPLIED, NEVER ACCUMULATED, AND THE DIFFERENCE IS A BROKEN CONTROL.** The
 * hook's fallback collects distinct `get` values, which here are JOINED strings —
 * so a document naming two projects would offer `Bocconi, LBS` as a single
 * tickable option matching only the documents naming exactly that pair, and the
 * two projects would have no option of their own. Same ruling as the UP
 * dashboard's `lineProjects` funnel.
 *
 * ⚠️ **NO `count`.** `ColumnFilterOption.count` means rows on the loaded page
 * holding this value, and a set-valued row holds several — so the counts would
 * add to more than the page and read as a miscount rather than as an overlap.
 * The primitive omits the number where it would mislead, and this is that case.
 *
 * ⚠️ **Keyed and sorted by the rendered NAME, which is what the reader ticks**,
 * and deliberately not by id: two ticked options must not collapse into one row
 * of the popover, and `projectDisplayName` is what puts a blank-named project on
 * screen as `#id`.
 */
function lineProjectOptions(
  rows: readonly { lineProjects: readonly CompanyProject[] }[],
): ColumnFilterOption[] {
  const names = new Set<string>();
  for (const row of rows) for (const project of row.lineProjects) names.add(projectDisplayName(project));
  return [...names]
    .sort((a, b) => a.localeCompare(b))
    .map((name) => ({ value: name, label: name }));
}

/**
 * Does this document name any of the ticked projects?
 *
 * **OR across the ticked values**, which is the only reading under which ticking
 * a second box can never REMOVE a row the first returned — the same rule
 * `?lineProjectId=` follows on the wire.
 */
function lineProjectsMatch(
  projects: readonly CompanyProject[],
  selected: readonly string[],
): boolean {
  return projects.some((project) => selected.includes(projectDisplayName(project)));
}

/**
 * A plain string cell — Services, Comment, a lead's name.
 *
 * 🔴 **CLAMPED TO TWO LINES, WITH A `title` CARRYING THE FULL TEXT.** Owner
 * report, 2026-09-02: Services wrapping to four lines while its neighbours were
 * squeezed, so rows were tall and ragged and the table read as broken.
 *
 * ⚠️ *SUPERSEDED: "A plain string cell that WRAPS rather than truncating."* That
 * was the right instinct and the wrong mechanism: unbounded wrapping in an
 * auto-layout table lets one long comment set the height of a whole row, and —
 * because a wrapping column asks for more width than a clipped one — it also
 * takes that width from the columns beside it.
 *
 * **Two lines rather than one**, deliberately: these hold sentences, and a
 * one-line ellipsis on a sentence shows about four words. Two lines at these
 * widths shows most short comments in full, and the `title` is the route to the
 * rest — so nothing is unreachable, which is the actual requirement.
 *
 * ⚠️ `[overflow-wrap:anywhere]` STAYS, and it is load-bearing beyond looks: it
 * drops the cell's min-content width to one character, which is what lets the
 * header-declared column widths above actually hold. Remove it and a single long
 * unbroken token (a URL in a comment) widens the column past its declared width
 * and squeezes every other one.
 */
function TextCell({ value }: { value: string | null }) {
  if (!value || value.trim() === '') return <NotRecorded />;
  return (
    <span
      title={value}
      className="line-clamp-2 text-xs text-fg-secondary [overflow-wrap:anywhere]"
    >
      {value}
    </span>
  );
}

/**
 * Overdue balances keyed by company id, or `null` where the read did not answer.
 *
 * ⚠️ **`null` AND AN EMPTY MAP ARE DIFFERENT FACTS** — empty means nobody is
 * overdue, `null` means we could not find out. For a chip the two are correctly
 * identical (both say nothing), which is why `overdueFor` collapses them; a
 * page-level SENTENCE about overdue accounts would have to branch on the map
 * itself, or "no overdue invoices" gets printed over an outage.
 */
type OverdueLookup = Map<number, OverdueSummary> | null;

/**
 * The overdue summary for a row's company, if there is one.
 *
 * ⚠️ **KEYED ON THE COMPANY ID AND NEVER ON THE NAME.** Production holds
 * `Think Test Company` twice, as two distinct Xero contacts — the same reason
 * `companyGroupOf` bands on the id — so a name match would attribute one
 * customer's debt to another's row. A row with no company gets no chip rather
 * than a guess.
 */
function overdueFor(
  overdue: OverdueLookup,
  company: { id: number } | null | undefined,
): OverdueSummary | undefined {
  if (overdue === null || !company) return undefined;
  return overdue.get(company.id);
}

/**
 * A band's company in the shape `overdueFor` takes.
 *
 * A band keyed on a free-text party name, or the no-company band, has no id and
 * gets no bracket — the same rule a row follows. There is no id in existence for
 * those, so this is an absence rather than a lookup that failed.
 */
function groupCompany<T>(group: RowGroup<T>): { id: number } | null {
  return group.companyId === null ? null : { id: group.companyId };
}

/**
 * A company reference, with its overdue bracket under it ONLY where the row is
 * the one thing naming that company.
 *
 * 🔴 **THE BRACKET IS A COMPANY-LEVEL FIGURE, SO ON A BANDED TABLE IT BELONGS
 * ON THE BAND AND NOT ON EVERY ROW — owner's report, 2026-09-02.** *SUPERSEDED:
 * this cell carried the chip unconditionally.* Repeated down four invoices it
 * reads as **that invoice's** debt, and the four brackets are identical, so the
 * table appears to say the company owes 45,500 four times over. It was at its
 * worst on the rows the owner screenshotted: **two of them were `Paid`, wearing
 * an "overdue" bracket** — a company-level truth rendered as a row-level lie,
 * with nothing failing.
 *
 * The Google Sheets mirror already had this right; its band carries the suffix
 * for exactly this reason, and the web table did not match it. One fact, one
 * place: the row where the company is identified.
 *
 * ⚠️ **WHEN THE TABLE IS NOT BANDED THE CHIP STAYS ON THE ROW**, because then
 * the row IS where the company is identified and there is no band to carry it.
 * Banding is conditional on the sort, so removing it here outright would have
 * made the whole feature vanish for anybody sorted by anything else — a
 * disappearance nobody would report as a bug.
 *
 * 🔴 **THE CHIP SITS UNDER THE NAME, NOT BESIDE IT, AND THAT IS A LAYOUT
 * DECISION RATHER THAN A DEVIATION FROM THE BRACKET.** The company column is
 * 224px in the quotes and invoices tables and 148px in Future Billables; a name
 * and `(GBP 12,400.00 overdue, 45 days)` sharing that inline would truncate BOTH
 * to about four characters each, so the row would carry two ellipses and no
 * facts. Stacked, the name keeps its full width and the bracket keeps its own —
 * and it still reads as belonging to the name above it. **Widening the column
 * instead would move `minWidth` on both tables**, which is a change to every row
 * on the page to accommodate the few that are overdue.
 *
 * ⚠️ **`min-w-0` ON THE WRAPPER IS REQUIRED AND CANNOT BE WRITTEN FROM INSIDE
 * THE CHIP.** Without it the box takes its min-content width from the badge's
 * `whitespace-nowrap` content and widens the column instead of letting the chip
 * truncate — `OverdueChip`'s own header flags this as the caller's line.
 *
 * ⚠️ **The margin is on the CHIP, never on a wrapper around it.** The chip
 * renders `null` for a company with nothing overdue — which is the ordinary case
 * — so a spacer element would add height to every row on the page to make room
 * for something that is not there.
 */
function CompanyCell({
  to,
  name,
  overdue,
}: {
  to: string | null;
  /**
   * What the row calls its customer, already chosen by the caller: on a quote
   * or invoice row that is `documentCustomerName(company)` (the Billing name,
   * because the document is synced with Xero); on a planned Future Billable it
   * is the row's `partyName` (owner 2026-10-25, the demarcation). One line, no
   * second name under it.
   */
  name: string | null | undefined;
  overdue: OverdueSummary | undefined;
}) {
  return (
    <div className="min-w-0">
      <RefCell to={to} name={name} />
      <OverdueChip summary={overdue} className="mt-1" />
    </div>
  );
}

/** A `YYYY-MM-DD`, never constructed into a `Date` — that shifts it a day west of Greenwich. */
function DateCell({ value }: { value: string | null }) {
  if (!value) return <NotRecorded />;
  return <span className="whitespace-nowrap text-xs tabular">{formatDateOnly(value)}</span>;
}

/* -------------------------------------------------------------------------
 * Grouping by company — the sheet's own layout
 *
 * Owner's instruction, 2026-09-02: *"the table view that you have created for
 * all the three things that should also be the same sheet view company row then
 * under all the related quotes and invoices as per the view"*. The Google Sheets
 * mirror already draws a full-width company banner and the documents beneath it;
 * these tables did not, and he wants them to match.
 * --------------------------------------------------------------------- */

interface RowGroup<T> {
  /**
   * `c:<id>` or `n:<name>` — see `companyGroupOf` — followed by `@<run index>`
   * from `groupRows`, so two runs of one company never share a React key. Never
   * the label alone. Used as a React key and nothing else.
   */
  key: string;
  label: string;
  /**
   * The band's company, where it has one — `null` for a free-text party name
   * and for the no-company band. What the overdue lookup keys on.
   */
  companyId: number | null;
  rows: T[];
}

/**
 * The band a row belongs to.
 *
 * 🔴 **KEYED ON THE COMPANY **ID**, NEVER ON THE DISPLAY NAME, wherever there is
 * an id to key on.** Two companies can carry the same name — production holds
 * `Think Test Company` twice, as two distinct Xero contacts — so a name-keyed
 * band merges two customers' documents under one heading and the reader has no
 * way to tell. Nothing fails; the total under the banner is simply wrong.
 *
 * ⚠️ **THE ONE PLACE A NAME IS THE KEY IS A ROW THAT HAS NO COMPANY AT ALL**, and
 * that is the Future Billable section only: a planned row may be typed against a
 * free-text name instead of a CRM account (`FutureDocumentDialog` labels that box
 * **Company name**), so `company` is null while `partyName` still says who. There
 * is no id in existence for those, and folding every one of them into a single
 * "no company" heap would file unrelated customers under one banner — which is
 * the very failure the id rule exists to prevent, arrived at from the other end.
 * So the typed name is used, and the id-collision hazard above is REAL but
 * confined to rows nobody has linked to an account. Linking one fixes it.
 *
 * 🔴 **`naming` PICKS THE BAND'S LABEL, AND ONLY THE LABEL** (owner 2026-10-25,
 * the demarcation). `'document'` = the quote and invoice sections, whose rows are
 * Xero-synced documents, so the band names the customer as the documents do:
 * `documentCustomerName` (the Billing name). `'company'` = the planned Future
 * Billables, which are not in Xero, so the Company name. The KEY is the id
 * either way; the label never decides grouping.
 */
function companyGroupOf(
  company: { id: number; name: string; billingName?: string | null } | null | undefined,
  naming: 'document' | 'company',
  fallbackName?: string | null,
): { key: string; label: string; companyId: number | null } {
  if (company) {
    const shown = naming === 'document' ? documentCustomerName(company) : company.name;
    return {
      key: `c:${company.id}`,
      label: shown.trim() || `Company #${company.id}`,
      /*
       * ⚠️ **CARRIED FORWARD RATHER THAN PARSED BACK OUT OF `key`.** The band
       * needs the id to look up its overdue balance, and `c:${id}` is right
       * there — but recovering it with a `slice(2)` would be matching on the
       * shape of a string this function invented, which is the same mistake as
       * lifting an invoice id out of a server's prose. It is also wrong for the
       * `n:` and `none` keys, where there is no id to recover and the slice
       * would produce a plausible one.
       */
      companyId: company.id,
    };
  }
  const typed = fallbackName?.trim();
  if (typed) return { key: `n:${typed.toLowerCase()}`, label: typed, companyId: null };
  /*
   * ⚠️ Deliberately NOT a blank banner. An empty full-width rule reads as a
   * rendering fault, and this is an ordinary answer: production holds documents
   * with no company on them at all. It is worded as a statement about the
   * RECORD rather than a confident "no company" — these references resolve
   * server-side and a null is not proof one was never set.
   */
  return { key: 'none', label: 'No company recorded', companyId: null };
}

/**
 * Groups CONSECUTIVE runs of rows, never by collecting them into a map.
 *
 * ⚠️ The distinction is the whole correctness of this: the rows arrive in the
 * order the SERVER sorted them, and a map-based grouping would silently REORDER
 * anything that did not arrive grouped — so the page would show an arrangement
 * the server never sent, and no filter, sort or pager could explain it. Runs
 * show exactly what came back. If a company appears in two bands, that is the
 * response, and it is visible rather than papered over.
 *
 * Same construction as `groupByProject` on `ClientDeliverablesPage`; read that
 * one's header too before changing either.
 */
function groupRows<T>(
  rows: T[],
  identify: (row: T) => { key: string; label: string; companyId: number | null },
): RowGroup<T>[] {
  const groups: RowGroup<T>[] = [];
  /*
   * ⚠️ **The run is compared on the company's IDENTITY, but each group's `key`
   * carries its RUN INDEX as well.** A company can legitimately appear in two
   * non-adjacent runs (see above), and keying both `<TBody>`s on the bare
   * identity handed React two siblings with one key. The index changes nothing
   * about grouping or order — it only makes the React key unique per run.
   */
  let lastIdentity: string | null = null;
  for (const row of rows) {
    const { key, label, companyId } = identify(row);
    const last = groups.at(-1);
    if (last && lastIdentity === key) last.rows.push(row);
    else groups.push({ key: `${key}@${groups.length}`, label, companyId, rows: [row] });
    lastIdentity = key;
  }
  return groups;
}

/**
 * The full-width company banner, as a real row header rather than a styled cell.
 *
 * ⚠️ **THE COUNT SAYS "on this page", AND THAT WORDING IS LOAD-BEARING.** A
 * company's documents can straddle a page boundary — the grouping runs over the
 * rows this page received, so the band simply restarts at the top of the next
 * page, which is the right behaviour and needs no special case. What would be
 * wrong is the count: *"4 rows"* under a banner holding the tail of a company
 * that had six on the previous page is a claim about the customer, and it is
 * false. *"4 on this page"* is true on every band, straddled or not.
 */
function CompanyBandRow({
  label,
  count,
  colSpan,
  overdue,
}: {
  label: string;
  count: number;
  colSpan: number;
  overdue: OverdueSummary | undefined;
}) {
  return (
    <TR>
      <TH scope="row" colSpan={colSpan} className="bg-surface-sunken font-medium text-fg-secondary">
        <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="min-w-0 truncate">{label}</span>
          <span className="shrink-0 text-2xs font-normal text-fg-muted tabular">
            {count} on this page
          </span>
          <OverdueChip summary={overdue} className="shrink-0 font-normal" />
        </span>
      </TH>
    </TR>
  );
}

/** The card-list equivalent of the banner, for below `lg`. */
function CompanyBandHeading({
  label,
  count,
  overdue,
}: {
  label: string;
  count: number;
  overdue: OverdueSummary | undefined;
}) {
  return (
    <h2 className="m-0 flex flex-wrap items-baseline gap-x-2 gap-y-1 border-b border-border bg-surface-sunken px-4 py-2">
      <span className="min-w-0 truncate text-xs font-medium text-fg-secondary">{label}</span>
      <span className="shrink-0 text-2xs text-fg-muted tabular">{count} on this page</span>
      <OverdueChip summary={overdue} className="shrink-0" />
    </h2>
  );
}

/**
 * The one spelling of the company sort key, for `/quotes` and `/invoices`.
 *
 * ✅ **SHIPPED 2026-09-02, AND THIS REPLACES THE `COMPANY_SORT_AVAILABLE` FLAG
 * THAT STOOD HERE.** *SUPERSEDED: "🔴 QUOTES AND INVOICES CANNOT BE BANDED
 * TODAY, AND THIS CONSTANT IS WHY — IT IS A MISSING SERVER SORT KEY, NOT AN
 * UNFINISHED UI … `/quotes` and `/invoices` have no company member at all …
 * The fix is one member on each enum server-side."* **Both enums gained
 * `company` server-side, ordering by the company's NAME, with company-less
 * documents contiguous at one end and a stable tiebreak within a company.** The
 * flag was `boolean`-annotated so the waiting wiring would not narrow to dead
 * code and get tidied away; that wiring is now live, so the annotation has
 * nothing left to protect and the flag is gone rather than pinned at `true` — a
 * constant named `…_AVAILABLE` that can only ever be `true` is a claim nothing
 * checks.
 *
 * 🔴 **BANDING IS GATED ON THE ACTIVE SORT, NOT ON THE FEATURE EXISTING, AND
 * THAT DISTINCTION IS THE WHOLE REASON THIS CONSTANT SURVIVES AS A KEY.** A band
 * is only honest when the rows ARRIVE ordered by company: grouping consecutive
 * runs over a list the server sorted by `issueDate` produces a banner above
 * almost every single row, which reads as *"every customer has one document"* —
 * a confident wrong answer, and worse than no banding at all. So each section
 * compares the sort it actually SENT against this constant, and a reader who
 * switches the sort to Issue Date watches the bands disappear rather than
 * fragment. Same rule `ClientDeliverablesPage` states for its project headers,
 * where `GROUPED_SORT` gates them to the one order they describe.
 *
 * ⚠️ **COMPARE THE SENT SORT, NEVER `table.sort?.key`.** The header's key is a
 * bare string that has not been through `asQuoteSort` / `asInvoiceSort` yet, and
 * those narrow an unrecognised member away to the section default — so a header
 * whose `sortKey` the API does not accept would band a list ordered by something
 * else entirely.
 *
 * ⚠️ **ONE LITERAL, USED EVERYWHERE, BECAUSE EVERY NEAR MISS IS A 422 THAT READS
 * AS AN EMPTY TABLE.** Both query schemas are `.strict()`, so `sort` is refused
 * by name rather than falling back — and a 422 on a list page renders as *"you
 * have no quotes"*, not as a bad sort. `companyName`, `companyId`,
 * `company_name` and `party` are all refused; `party` is the dangerous one,
 * being a REAL member of `InvoiceTrackerSort` that the Future Billable section
 * in this same file legitimately sends. Two endpoints, two spellings, one idea,
 * and neither travels.
 *
 * ⚠️ **DO NOT BAND BY SORTING THE LOADED PAGE IN THE BROWSER** if this key is
 * ever withdrawn. That re-orders 50 rows against the ordering the pager
 * describes, so page 2 restarts the alphabet — a reader seeing A–Z twice
 * concludes the list is broken. It is also the one thing `useTableFilters`
 * refuses to do, in its own words: *"reordering one page while implying the
 * whole table is ordered is worse than no sort at all"*.
 */
const COMPANY_SORT_KEY = 'company' satisfies QuoteListSort & InvoiceListSort;

/* -------------------------------------------------------------------------
 * Section chrome
 *
 * The parts every section renders identically. Factored out so the three cannot
 * drift into three slightly different finance screens — which is exactly what
 * the owner's "make it look like the Client Deliverables tracker" was asking to
 * avoid.
 * --------------------------------------------------------------------- */

function ListSkeleton() {
  return (
    <ul className="m-0 list-none p-0">
      {Array.from({ length: SKELETON_ROWS }, (_, i) => (
        <li key={i} className="border-b border-border px-4 py-3 last:border-b-0">
          <Skeleton className="h-3.5 w-44" />
          <div className="mt-2 flex items-center gap-2">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-24" />
          </div>
          <div className="mt-2.5 grid gap-1.5">
            <Skeleton className="h-2.5 w-40" />
            <Skeleton className="h-2.5 w-32" />
          </div>
        </li>
      ))}
    </ul>
  );
}

function TableSkeleton({ columnCount, showActions }: { columnCount: number; showActions: boolean }) {
  return (
    <TBody>
      {Array.from({ length: SKELETON_ROWS }, (_, rowIndex) => (
        <TR key={rowIndex}>
          {Array.from({ length: columnCount }, (_, cellIndex) => (
            <TD key={cellIndex}>
              <Skeleton className="h-3 w-24" />
            </TD>
          ))}
          {showActions && (
            <TD numeric pinned>
              <Skeleton className="ml-auto h-6 w-24" />
            </TD>
          )}
        </TR>
      ))}
    </TBody>
  );
}

/**
 * The width every column asks for, keyed by `ColumnSpec.key`.
 *
 * 🔴 **WIDTHS ARE DECLARED ON THE HEADER ROW AND NOWHERE ELSE.** Owner report,
 * 2026-09-02: comments cut mid-word, Services wrapping to four lines beside
 * squeezed neighbours, and a horizontal scrollbar under columns that were still
 * too narrow. All of it is one cause — `Table` carries no `table-fixed`, so with
 * no width asked for anywhere the browser sized every column off its content and
 * gave the long free-text columns whatever was left.
 *
 * ⚠️ **A `<th>` WIDTH IS A REQUEST, NOT A RULE, AND THAT IS WHY IT WORKS HERE.**
 * Auto table layout honours a declared width unless a cell's MIN-CONTENT demands
 * more. Every text cell on this page is `[overflow-wrap:anywhere]`, whose
 * min-content is one character, so nothing can force a column past what it asks
 * for — which is exactly the property `table-fixed` would buy, without
 * `table-fixed`'s cost of silently clipping anything that cannot wrap (the money
 * and date cells are `whitespace-nowrap`).
 *
 * ⚠️ **THE NUMBERS ARE FLOORS SET BY THE HEADER LABEL, not by the data.** A
 * header is `text-2xs` uppercase with `tracking-[0.06em]`, plus a funnel button
 * (~20px), its gap (4px) and `TH`'s `px-3` (24px) — so "Planned from" needs
 * ~140px before any value is considered, and a column narrower than its own
 * label is a heading truncated to "Planned fro…". Check the LABEL first when
 * adding a column, then widen for content.
 *
 * ⚠️ **`min-w` ON THE TABLE MUST BE THE SUM OF THESE.** Below the sum the
 * browser starts shrinking columns proportionally and the truncation comes
 * straight back; above it the surplus is spread and nothing is harmed. The sums
 * are written out at each `minWidth` call site because Tailwind scans SOURCE for
 * literal class names — a computed `min-w-[${n}px]` produces no CSS at all and
 * fails silently, which is the worst available outcome here.
 */
type ColumnWidths = Readonly<Record<string, string>>;

/**
 * The actions lane's layout key.
 *
 * ⛔ **IT IS A LAYOUT KEY SO THE MANAGER CAN LIST IT, AND IT IS LOCKED SO THE
 * MANAGER CANNOT ACT ON IT.** The Future billables table and (since 2026-10-25,
 * for "Preview PDF") the quotes table render one; invoices does not. It is
 * not a `ColumnSpec`: it carries no filterable value, and giving it a funnel
 * would offer to filter on a button — so every lookup here goes through the
 * key, never through the columns array.
 */
const ACTIONS_COLUMN = 'actions';

/**
 * The px a `w-[NNNpx]` literal asks for.
 *
 * ⚠️ **PARSED FROM THE CLASS RATHER THAN WRITTEN BESIDE IT, AND THAT IS THE
 * POINT.** The class is what the browser lays out with; the number is only ever
 * the sum the table's floor is built from. A second, numeric copy of the three
 * width maps would agree on the day it was written and drift on the first
 * re-cut — and this file's own `min-w-` history is exactly that failure, three
 * hand-totalled sums that had to be re-derived by hand every time a column
 * moved. One map, one source of truth, arithmetic derived from it.
 *
 * An unparseable or absent entry contributes `0` rather than `NaN`, which would
 * blank the whole inline style and silently drop the floor to nothing.
 */
function widthPx(widths: ColumnWidths, key: string): number {
  const match = /\[(\d+)px\]/.exec(widths[key] ?? '');
  return match === null ? 0 : Number(match[1]);
}

/**
 * The table's floor, in px, for the columns currently VISIBLE.
 *
 * 🔴 **AN INLINE STYLE, NEVER A CLASS.** Tailwind v4 scans SOURCE TEXT, so
 * `min-w-[${n}px]` emits no rule whatsoever — the class lands in the DOM and
 * does nothing, which looks identical to a table that simply chose not to have a
 * floor. The three `min-w-[…]` literals this replaces were correct precisely
 * because they were hand-written constants; a computed one cannot be.
 *
 * ⚠️ **HIDING A COLUMN HAS TO SHRINK THIS.** A floor frozen at the
 * all-columns sum is a table that still scrolls sideways with empty space in
 * it — the reader hides six of sixteen columns to stop the horizontal scroll
 * and the scroll stays, which reads as the control not working.
 *
 * ⚠️ **AND ON THE FUTURE BILLABLES TABLE IT IS LOAD-BEARING RATHER THAN
 * COSMETIC.** That table is sized to FIT (see `FUTURE_WIDTHS`) so its pinned
 * actions lane never floats over the Comment column. Hiding a column there now
 * shrinks the floor further below the content area, which keeps that property
 * rather than breaking it.
 */
function tableMinWidth(widths: ColumnWidths, visibleKeys: readonly string[]): number {
  return visibleKeys.reduce((total, key) => total + widthPx(widths, key), 0);
}

/**
 * The keys to RENDER, with the actions lane forced last.
 *
 * ⚠️ **`useColumnLayout` REFUSES TO MOVE A LOCKED KEY; IT DOES NOT REFUSE TO
 * MOVE ANOTHER KEY PAST ONE.** So a reader dragging Comment to the bottom of the
 * manager would otherwise put a `pinned` lane — `sticky right-0` — in front of a
 * live column, which is the exact overlap `FUTURE_WIDTHS` documents paying to
 * avoid. Clamping here makes the bad order inexpressible at the render layer
 * rather than relying on the manager to prevent it.
 *
 * ⚠️ **THE SAME LIST MUST GO TO THE HEAD, THE BODY, THE SKELETON AND THE
 * MEASURER.** `useFrozenOffsets` indexes the header row's DOM children BY
 * POSITION, so a measurer handed a different order from the one rendered reads
 * one column's width as another's — a stale offset does not error, it overlaps
 * two frozen columns by the difference.
 */
function renderKeys(visibleKeys: readonly string[], hasActions: boolean): readonly string[] {
  if (!hasActions) return visibleKeys;
  const rest = visibleKeys.filter((key) => key !== ACTIONS_COLUMN);
  return rest.length === visibleKeys.length ? rest : [...rest, ACTIONS_COLUMN];
}

interface FrozenProps {
  frozen?: FrozenSide;
  frozenOffset?: number;
}

/**
 * What a CELL needs to sit on a frozen edge.
 *
 * ⚠️ **THE ACTIONS LANE IS REPORTED AS UNFROZEN HERE AND COUNTED AS RIGHT-FROZEN
 * BY THE MEASURER.** It keeps `pinned`, which is `right-0` and takes no offset.
 * Losing the lane's width from the offsets would put a second right-frozen
 * column underneath it; putting `pinned` and `frozen="right"` on one cell gives
 * it two `box-shadow` literals that cannot merge, so which edge is drawn would
 * be decided by whatever order Tailwind happened to emit them in.
 *
 * ⚠️ *SUPERSEDED: a local `measureSideOf` wrapper that answered `'right'` for
 * the lane, handed to `useFrozenOffsets` in place of the controller's own
 * `frozenSideOf`.* **The hook takes `pinnedKeys` itself now** and does exactly
 * that, so the two views are one argument apart at the call site rather than a
 * function nobody would think to look for.
 *
 * The early return stays: the controller refuses to freeze a pinned key, so
 * this guards only against a preferences document written by something else.
 */
function frozenPropsFor(
  key: string,
  frozenSideOf: (key: string) => FrozenSide | undefined,
  offsets: Record<string, number>,
): FrozenProps {
  if (key === ACTIONS_COLUMN) return {};
  const side = frozenSideOf(key);
  if (side === undefined) return {};
  return { frozen: side, frozenOffset: offsets[key] ?? 0 };
}

/**
 * Every header is driven off the same key list the row cells and the skeleton
 * are ordered by — so a column hidden, moved or frozen cannot leave the header
 * row describing a different table from the body.
 *
 * ⚠️ *SUPERSEDED: this mapped `columns` directly, and the actions header was
 * appended behind a `showActions` flag.* The order is the reader's now, so the
 * declared array is a LOOKUP rather than the sequence.
 */
function SectionTableHead<T>({
  columns,
  headerFor,
  widths,
  visibleKeys,
  frozenSideOf,
  frozenOffsets,
  headRowRef,
  actionsWidth,
}: {
  columns: ColumnSpec<T>[];
  headerFor: (key: string) => FilterableTHProps;
  widths: ColumnWidths;
  visibleKeys: readonly string[];
  frozenSideOf: (key: string) => FrozenSide | undefined;
  frozenOffsets: Record<string, number>;
  /**
   * The header row, handed to `useFrozenOffsets`, which measures the RENDERED
   * width of each cell. There is no `table-fixed` and no `<colgroup>` on any of
   * these three tables, so a `w-*` on a `<th>` is a request the content may
   * outvote — the only honest source for a frozen column's offset is the DOM.
   *
   * ⚠️ *SUPERSEDED: the row was a raw `<tr>` carrying a copy of `TR`'s class
   * string, "because `TR` cannot take a `ref`".* **It takes one as of
   * 2026-09-12**, so the copy is gone — a second copy of a shared primitive's
   * styling is exactly what nothing would have kept in step.
   */
  headRowRef: React.RefObject<HTMLTableRowElement | null>;
  /**
   * ⚠️ The actions lane is a real column and needs a real width like any other.
   * Left to the content it collapses to the widest button, and the two controls
   * that can appear here ("Raise quote" alone, or "Raise quote" beside "Link to
   * document") are very different sizes — so the lane would change width when a
   * recovery banner appears, moving every column left of it.
   */
  actionsWidth?: string;
}) {
  const byKey = new Map(columns.map((column) => [column.key, column] as const));

  return (
    <THead>
      <TR ref={headRowRef}>
        {visibleKeys.map((key) => {
          if (key === ACTIONS_COLUMN) {
            return (
              <TH key={key} numeric pinned className={actionsWidth}>
                <span className="sr-only">Actions</span>
              </TH>
            );
          }
          // A key the table no longer declares renders nothing rather than an
          // empty cell — `applyOrder` already drops those, so this is the
          // belt-and-braces half.
          if (!byKey.has(key)) return null;
          const width = widths[key];
          /*
           * `headerFor` never returns a `className` (measured in
           * `use-table-filters.ts` — it returns `label`, `numeric` and
           * `filter`), so this assigns rather than merges. If it ever starts
           * returning one, merge with `cx` instead of overwriting it.
           */
          return (
            <FilterableTH
              key={key}
              {...headerFor(key)}
              {...(width === undefined ? {} : { className: width })}
              {...frozenPropsFor(key, frozenSideOf, frozenOffsets)}
            />
          );
        })}
      </TR>
    </THead>
  );
}

/**
 * One body row, ordered by the same key list as the header.
 *
 * ⚠️ **THE CELL FOR EACH KEY IS A LOOKUP BUILT PER ROW, AND EVERY `max-w` AND
 * `numeric` TRAVELS WITH IT.** The three hardcoded cell lists this replaces
 * could only be kept in step with their headers by hand; the file's own width
 * note already recorded what that costs — a cell missing or out of order is how
 * the layout jumps a column over as the rows arrive, with nothing failing.
 */
interface SectionCell {
  content: React.ReactNode;
  className?: string;
  numeric?: boolean;
}

function SectionCells({
  cells,
  visibleKeys,
  frozenSideOf,
  frozenOffsets,
}: {
  cells: Readonly<Record<string, SectionCell>>;
  visibleKeys: readonly string[];
  frozenSideOf: (key: string) => FrozenSide | undefined;
  frozenOffsets: Record<string, number>;
}) {
  return (
    <>
      {visibleKeys.map((key) => {
        const cell = cells[key];
        if (cell === undefined) return null;
        /*
         * ⛔ **`pinned` IS APPLIED HERE, BY KEY, AND NOT LEFT TO THE CELL
         * TABLE.** It is the lane's defining property — `sticky right-0` — and
         * `SectionTableHead` decides the header's the same way, so the header
         * and the body cannot end up disagreeing about which column sticks.
         * `frozenPropsFor` deliberately returns nothing for this key: `pinned`
         * and `frozen` are two `box-shadow` literals that cannot merge, so a
         * cell carrying both would draw whichever edge Tailwind happened to
         * emit last.
         */
        return (
          <TD
            key={key}
            {...(cell.numeric ? { numeric: true } : {})}
            {...(key === ACTIONS_COLUMN ? { pinned: true } : {})}
            {...(cell.className === undefined ? {} : { className: cell.className })}
            {...frozenPropsFor(key, frozenSideOf, frozenOffsets)}
          >
            {cell.content}
          </TD>
        );
      })}
    </>
  );
}

function EmptyStateSlot({
  noun,
  search,
  filtered,
  columnsHidAll,
  onClearColumns,
  onClear,
  hasError,
  emptyDescription,
}: {
  noun: string;
  search: string;
  filtered: boolean;
  columnsHidAll: boolean;
  onClearColumns: () => void;
  onClear: () => void;
  hasError: boolean;
  emptyDescription: string;
}) {
  if (hasError) {
    return (
      <EmptyState
        icon={<AccountsIcon />}
        title="Nothing to show"
        description="See the message above."
      />
    );
  }

  /*
   * The server did return rows — the column filters hid all of them. Saying "no
   * rows match" here would send the reader to change the search or the filters
   * above, neither of which is the cause. The headers go with the table, so the
   * way out has to be offered here.
   */
  if (columnsHidAll) {
    return (
      <EmptyState
        icon={<AccountsIcon />}
        title="Every row on this page is filtered out"
        description="Column filters apply to this page only. Clear them to see it again."
        action={
          <Button variant="secondary" size="sm" onClick={onClearColumns}>
            Clear column filters
          </Button>
        }
      />
    );
  }

  const narrowed = Boolean(search) || filtered;

  return (
    <EmptyState
      icon={<AccountsIcon />}
      title={narrowed ? 'No rows match' : `No ${noun} yet`}
      description={
        /*
         * ⚠️ NEITHER SENTENCE MAY IMPLY DATA IS ON ITS WAY. An empty section is
         * an ordinary answer, not a load that has not finished.
         */
        narrowed ? 'Nothing matches these filters. Clear them to see the whole list.' : emptyDescription
      }
      action={
        narrowed ? (
          <Button variant="secondary" size="sm" onClick={onClear}>
            Clear search and filters
          </Button>
        ) : null
      }
    />
  );
}

/**
 * The panel every section is rendered inside — header, filter bank, the two
 * filter-count lines, the error band, the mobile FilterSheet, the desktop table
 * and the pager.
 *
 * A presentational wrapper only: it owns no state and makes no request. Each
 * section still owns its own filters, its own query and its own columns, which
 * is what keeps the three from having to agree about anything but their chrome.
 */
function SectionPanel({
  title,
  countLabel,
  summaryBar,
  filters,
  moreFilters,
  moreFiltersId,
  showMoreFilters,
  onToggleMoreFilters,
  activeFilterCount,
  onClearFilters,
  columnActiveCount,
  columnKeys,
  headerFor,
  clearColumns,
  hiddenByColumns,
  rowCount,
  error,
  errorTitle,
  loading,
  hasResult,
  initialLoad,
  isEmpty,
  emptyState,
  minWidth,
  columnManager,
  columnCount,
  showActions,
  tableHead,
  tableBody,
  cards,
  page,
  totalPages,
  onPage,
  firstRow,
  lastRow,
  total,
}: {
  title: string;
  countLabel: string;
  /**
   * The stat-card filter bar, or `null` when no card is narrowing this section.
   *
   * ⚠️ **A NODE, not the card key** — this wrapper owns no state and makes no
   * request, and it must not learn what a stat card is to keep that true. The
   * page builds the bar once and every section hands it the same node.
   */
  summaryBar: React.ReactNode;
  filters: React.ReactNode;
  /*
   * ⚠️ **OPTIONAL SINCE 2026-09-05.** The Future billables section's only
   * `moreFilters` content was an explanatory paragraph, which went with the rest
   * of this app's helper text — so that section now has nothing behind the
   * disclosure. The button is therefore withheld when this is absent: a
   * "More filters" control that opens an empty box is an affordance whose only
   * outcome is nothing happening, which this app removes everywhere else.
   */
  moreFilters?: React.ReactNode;
  moreFiltersId: string;
  showMoreFilters: boolean;
  onToggleMoreFilters: () => void;
  activeFilterCount: number;
  onClearFilters: () => void;
  columnActiveCount: number;
  columnKeys: readonly string[];
  headerFor: (key: string) => FilterableTHProps;
  clearColumns: () => void;
  hiddenByColumns: number;
  rowCount: number;
  error: string | null;
  errorTitle: string;
  loading: boolean;
  hasResult: boolean;
  initialLoad: boolean;
  isEmpty: boolean;
  emptyState: React.ReactNode;
  /**
   * The table's floor in PX, summed from the VISIBLE columns' widths.
   *
   * 🔴 **A NUMBER, NOT A CLASS, AND IT CANNOT BE A CLASS.** *SUPERSEDED: three
   * hand-totalled `min-w-` literals at 2472, 2388 and 1128px.* Those were correct
   * only because they were constants; the floor has to shrink as a reader hides
   * columns, and Tailwind v4 scans SOURCE TEXT, so a computed `min-w-[${n}px]`
   * emits no rule at all — the class lands in the DOM and does nothing, which is
   * indistinguishable from a table that simply has no floor. See `tableMinWidth`.
   *
   * ⚠️ **THE THREE OLD VALUES ARE SPELLED OUT AS NUMBERS ABOVE, NOT AS CLASSES,
   * AND THAT IS DELIBERATE.** Tailwind scans this file, so writing a dead
   * `min-w-[…]` literal in a comment EMITS IT into the production stylesheet —
   * three rules nothing references, and a false positive for anyone later
   * grepping the bundle to prove a class is gone.
   */
  minWidth: number;
  /** The column manager, in the panel header beside the title. */
  columnManager: React.ReactNode;
  columnCount: number;
  showActions: boolean;
  tableHead: React.ReactNode;
  tableBody: React.ReactNode;
  cards: React.ReactNode;
  page: number;
  totalPages: number;
  onPage: (next: number) => void;
  firstRow: number;
  lastRow: number;
  total: number;
}) {
  return (
    <Panel>
      {/*
        * ⚠️ **THE MANAGER SITS IN THE PANEL HEADER, NOT IN THE FILTER BANK.**
        * The bank is a four-column grid of filters and the two count lines under
        * it are about ROWS; column visibility is the reader's own chrome and
        * narrows nothing. Putting it beside the title also keeps it above the
        * `lg:hidden` breakpoint boundary — see the `columnManager` slot at each
        * call site for why it is withheld below `lg`.
        */}
      <PanelHeader title={title} description={countLabel} actions={columnManager} />

      {/*
       * ⚠️ **BETWEEN THE HEADING AND THE FILTER BANK, and that placement is the
       * point.** Owner's choice of *inside the panel, above the filter bank*: the
       * title says which records these are and the bar says which of them, so the
       * two read as one statement — and it stays on screen with the table rather
       * than a card row away, which is what the reader is looking at when they
       * wonder why the count is 6 and the card said 41.
       *
       * ⚠️ **ABOVE the bank rather than beside its own `N filters applied` line**,
       * which sits BELOW the bank and is about how many keys are set. Two
       * different questions: which population, and how narrowed. Putting the bar
       * down there would bury the prominent half under a disclosure a reader may
       * never open.
       */}
      {summaryBar}

      <div className="border-b border-border px-4 py-3">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {filters}
          {moreFilters !== undefined && (
            <div className="flex items-end">
              <Button
                variant="secondary"
                leadingIcon={<FilterIcon />}
                aria-expanded={showMoreFilters}
                aria-controls={moreFiltersId}
                onClick={onToggleMoreFilters}
                className="w-full sm:w-auto"
              >
                {showMoreFilters ? 'Fewer filters' : 'More filters'}
              </Button>
            </div>
          )}
        </div>

        {showMoreFilters && moreFilters !== undefined && (
          <div
            id={moreFiltersId}
            className="mt-3 grid gap-3 border-t border-border pt-3 sm:grid-cols-2 lg:grid-cols-4"
          >
            {moreFilters}
          </div>
        )}

        {activeFilterCount > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3">
            <p className="m-0 text-xs text-fg-muted">
              {activeFilterCount} {activeFilterCount === 1 ? 'filter' : 'filters'} applied.
            </p>
            <Button variant="ghost" size="sm" onClick={onClearFilters}>
              Clear filters
            </Button>
          </div>
        )}

        {/*
          * Explained here, not only at the headers, so a filter set at one
          * breakpoint doesn't silently keep hiding rows at another. The clear
          * button is wide-screen only — below `lg` the FilterSheet has its own,
          * and two buttons with the same label a screen-length apart would read
          * as two different scopes.
          */}
        {columnActiveCount > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3">
            <p className="m-0 text-xs text-fg-muted">
              {columnActiveCount} column {columnActiveCount === 1 ? 'filter' : 'filters'} applied to
              the {rowCount} {rowCount === 1 ? 'row' : 'rows'} on this page
              {hiddenByColumns > 0 ? `, hiding ${hiddenByColumns}` : ''}.
            </p>
            <span className="hidden lg:contents">
              <Button variant="ghost" size="sm" onClick={clearColumns}>
                Clear column filters
              </Button>
            </span>
          </div>
        )}
      </div>

      <p role="status" aria-live="polite" className="sr-only">
        {countLabel}
      </p>

      {error && (
        <div className="border-b border-border px-4 py-3">
          <Notice tone="danger" role="alert" title={errorTitle}>
            <span className="[overflow-wrap:anywhere]">{error}</span>
          </Notice>
        </div>
      )}

      <div
        aria-busy={loading || undefined}
        className={cx(
          'transition-opacity duration-150 ease-out motion-reduce:transition-none',
          loading && hasResult && 'opacity-60',
        )}
      >
        {/* Below lg a fifteen-column table cannot fit 320px, so rows become a stacked list. */}
        <div className="lg:hidden">
          {/*
            * `FilterSheet` is the header row's mobile equivalent — cards have no
            * header row for the column funnels to hang off below `lg`. It shares
            * the same `headerFor`/`clearAll`, so a filter set here is what the
            * table shows once the viewport widens (one state store).
            */}
          <div className="border-b border-border px-4 py-2">
            <FilterSheet
              keys={columnKeys}
              headerFor={headerFor}
              activeCount={columnActiveCount}
              clearAll={clearColumns}
            />
          </div>
          {initialLoad ? <ListSkeleton /> : isEmpty ? emptyState : cards}
        </div>

        <div className="hidden lg:block">
          {initialLoad ? (
            <TableWrap frozenHeader>
              {/*
                * The floor is a FLOOR, not a sum: these tables have no
                * `table-fixed`, so a `<th>` width is a request and the content
                * decides.
                *
                * ⚠️ *SUPERSEDED: "THE FLOOR IS ON A WRAPPER DIV, NOT ON
                * `Table`… `Table` accepts `className` and `children` only."*
                * **`Table` takes a `style` now**, so the spacer `<div>` that
                * carried this number is gone from both branches. It still has
                * to be an inline style — see `minWidth`.
                */}
              <Table style={{ minWidth }}>
                {tableHead}
                <TableSkeleton columnCount={columnCount} showActions={showActions} />
              </Table>
            </TableWrap>
          ) : isEmpty ? (
            emptyState
          ) : (
            <TableWrap frozenHeader>
              <Table style={{ minWidth }}>
                {tableHead}
                {tableBody}
              </Table>
            </TableWrap>
          )}
        </div>
      </div>

      {hasResult && (
        <PanelFooter>
          <p className="m-0 text-center text-xs text-fg-muted tabular sm:text-left">
            {total === 0 ? 'No rows to show' : `Showing ${firstRow}–${lastRow} of ${total}`}
            {hiddenByColumns > 0 ? ` · ${hiddenByColumns} hidden by column filters` : ''}
          </p>
          <div className="flex items-center gap-2">
            <span className="hidden text-xs text-fg-subtle tabular sm:inline">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="secondary"
              size="sm"
              className="flex-1 sm:flex-none"
              leadingIcon={<ChevronIcon direction="left" />}
              disabled={page <= 1}
              onClick={() => onPage(page - 1)}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              size="sm"
              className="flex-1 sm:flex-none"
              trailingIcon={<ChevronIcon direction="right" />}
              disabled={page >= totalPages}
              onClick={() => onPage(page + 1)}
            >
              Next
            </Button>
          </div>
        </PanelFooter>
      )}
    </Panel>
  );
}

/* =========================================================================
 * QUOTES
 * ===================================================================== */

/*
 * 🔴 **`lineProjectId`, NOT `projectId` — REPOINTED 2026-09-15, AND THE OLD KEY
 * COULD NOT MATCH ANYTHING.** `?projectId=` narrows on the document's HEADER
 * project, which migrations 067-069 moved onto the line and the header pickers
 * stopped writing when they were removed: **measured that day, NULL on all 279
 * quotes and all 809 invoices**, so every value of that filter returned an empty
 * table. Not a narrow list — an empty one, which reads as "this account has no
 * quotes on that project" rather than as the filter asking the wrong question.
 *
 * `?lineProjectId=` is the real filter and the backend's own schema says so in
 * terms. It is what `/quotes` and `/invoices` already render a picker for; this
 * page simply had not been moved across.
 *
 * ⚠️ **A SHARED LINK OR SAVED FILTER HOLDING `?projectId=` STOPS NARROWING.**
 * Accepted, and it costs nothing that worked: such a link empties the table
 * today. Nothing here parses the old key any more, so it is now inert rather
 * than silently filtering to nothing.
 *
 * ✅ **Every id filter here is a multi-select since 2026-10-28 (owner).** The URL
 * holds a CSV (`readIdList`), one id goes out as a number and several as the CSV
 * (`idListParam`); several account leads go as `accountLeadIds`. Saved filters
 * and the summary bar are key-based, so a CSV value passes through both.
 * *SUPERSEDED: "this control sends ONE id … do not widen the picker to multi
 * without checking the saved-filter and badge paths."*
 */
const QUOTE_ID_FILTER_KEYS = [
  'companyId',
  'contactId',
  'stageId',
  'lineProjectId',
  'accountLeadId',
] as const;
const QUOTE_TEXT_FILTER_KEYS = [
  'currency',
  'issueDateAfter',
  'issueDateBefore',
  'validTillAfter',
  'validTillBefore',
  'amountMin',
  'amountMax',
] as const;
/**
 * The flag filters — no value of their own, just present or absent.
 *
 * ⚠️ **A SEPARATE GROUP RATHER THAN A MEMBER OF `QUOTE_TEXT_FILTER_KEYS`**, even
 * though the union would be identical: the text group is what `readQuoteFilters`
 * runs a date or amount pattern over, and a flag validated by one of those
 * readers would silently blank itself. The grouping is what says which reader a
 * key belongs to.
 */
const QUOTE_FLAG_FILTER_KEYS = ['draft', 'awaitingSignature', 'acceptedUnbilled'] as const;
const QUOTE_FILTER_KEYS = [
  ...QUOTE_ID_FILTER_KEYS,
  ...QUOTE_TEXT_FILTER_KEYS,
  ...QUOTE_FLAG_FILTER_KEYS,
] as const;

type QuoteIdFilterKey = (typeof QUOTE_ID_FILTER_KEYS)[number];
type QuoteFilterKey = (typeof QUOTE_FILTER_KEYS)[number];
type QuoteFilters = Record<QuoteFilterKey, string>;

function readQuoteFilters(params: URLSearchParams): QuoteFilters {
  const id = (key: QuoteIdFilterKey) => readIdList(params.get(key));
  /* Exactly `FLAG_ON`; anything else is no filter at all. See that constant. */
  const flag = (key: QuoteFilterKey) => (params.get(key) === FLAG_ON ? FLAG_ON : '');
  const date = (key: QuoteFilterKey) => {
    const raw = params.get(key) ?? '';
    return DATE_PATTERN.test(raw) && isRealDate(raw) ? raw : '';
  };
  const amount = (key: QuoteFilterKey) => {
    const raw = params.get(key) ?? '';
    return AMOUNT_PATTERN.test(raw) ? raw : '';
  };
  const currencyRaw = params.get('currency') ?? '';
  return {
    companyId: id('companyId'),
    contactId: id('contactId'),
    stageId: id('stageId'),
    lineProjectId: id('lineProjectId'),
    accountLeadId: id('accountLeadId'),
    currency: CURRENCY_PATTERN.test(currencyRaw) ? currencyRaw : '',
    issueDateAfter: date('issueDateAfter'),
    issueDateBefore: date('issueDateBefore'),
    validTillAfter: date('validTillAfter'),
    validTillBefore: date('validTillBefore'),
    amountMin: amount('amountMin'),
    amountMax: amount('amountMax'),
    draft: flag('draft'),
    awaitingSignature: flag('awaitingSignature'),
    acceptedUnbilled: flag('acceptedUnbilled'),
  };
}

/**
 * Written out key by key so a request can never carry a stray param, and so an
 * empty value is omitted entirely rather than emitted as a bare `key=` — every
 * list schema on this API is `.strict()`, where an empty value is a 422 rather
 * than "no filter".
 *
 * ⚠️ **`amountMin` / `amountMax` stay STRINGS.** Money is decimal text on this
 * wire; `Number('1234.10')` is exact today and the arithmetic around it is not,
 * and a rounded bound silently excludes the row sitting on it.
 *
 * 🔴 **`issueDateAfter` / `issueDateBefore` / `amountMin` / `amountMax` ARE THE NEW
 * RANGE KEYS AND THEIR SPELLING IS NOT THIS FILE'S TO SETTLE.**
 * `QuoteListParams` is another agent's file. The names below follow the closest
 * sibling in this repo — `InvoiceTrackerListParams`, which spells its bounds
 * `billDateAfter` / `billDateBefore` / `amountMin` / `amountMax`.
 *
 * 🔴 **`tsc` DOES NOT CHECK THESE KEY NAMES — MEASURED 2026-09-13, AND THIS
 * COMMENT SAID THE OPPOSITE.** *SUPERSEDED: "the return type is the API's own
 * params interface, so a name that does not match is a COMPILE ERROR here rather
 * than a 422 in production. If `tsc` names one of these four, the fix is to
 * rename it…"* Measured by putting a deliberate typo into the twin function
 * (`invoiceFilterParams`) and running `tsc --noEmit -p tsconfig.app.json`: **it
 * compiled clean.** Excess-property freshness does NOT survive a conditional
 * spread, and `x satisfies QuoteListParams` does not close it either — isolated
 * both ways. The only error that ever fires here is TS2559 *"has no properties in
 * common"*, which **cannot fire once any one key in the literal matches**, and
 * this literal spreads a dozen good ones. So the guard was never real; it looked
 * real because it has never been tested and because the spellings happen to be
 * right.
 *
 * ⚠️ **What DOES check a key is assignment to a typed object** (`params.foo = v`
 * on a `const params: QuoteListParams = {}`). Not adopted here: it would be a
 * second construct for the six functions on this page that all build params this
 * way, and that is a page-wide call rather than a side effect of adding a filter.
 * **Until it is taken, verify a new bound's spelling against the backend's own
 * query schema, not against a green build.** Both list schemas are `.strict()`,
 * so a wrong name is at least a LOUD 422 naming the key rather than a silent
 * unfiltered list — that is what bounds the cost, not the compiler.
 *
 * Do not silence a rename with a cast: a cast turns a loud rename into a silent
 * unfiltered list.
 */
function quoteFilterParams(filters: QuoteFilters): QuoteListParams {
  return {
    /* Id filters: one id as a number, several as CSV (`idListParam`). Several
       account leads ride `accountLeadIds`, the list key the API already has. */
    ...(filters.companyId ? { companyId: idListParam(filters.companyId) } : {}),
    ...(filters.contactId ? { contactId: idListParam(filters.contactId) } : {}),
    ...(filters.stageId ? { stageId: idListParam(filters.stageId) } : {}),
    ...(filters.lineProjectId ? { lineProjectId: idListParam(filters.lineProjectId) } : {}),
    ...accountLeadParam(filters.accountLeadId),
    ...(filters.currency ? { currency: filters.currency } : {}),
    ...(filters.issueDateAfter ? { issueDateAfter: filters.issueDateAfter } : {}),
    ...(filters.issueDateBefore ? { issueDateBefore: filters.issueDateBefore } : {}),
    ...(filters.validTillAfter ? { validTillAfter: filters.validTillAfter } : {}),
    ...(filters.validTillBefore ? { validTillBefore: filters.validTillBefore } : {}),
    ...(filters.amountMin ? { amountMin: filters.amountMin } : {}),
    ...(filters.amountMax ? { amountMax: filters.amountMax } : {}),
    /*
     * ⚠️ The literal `true` or the key is absent — never `awaitingSignature:
     * Boolean(...)`. `QuoteListParams` types it as the literal for exactly this
     * reason, so `tsc` refuses the widened version rather than letting a
     * `?awaitingSignature=false` reach a `.strict()` schema.
     *
     * ⚠️ **THE THREE ARE COMPOSED, NOT EXCLUSIVE, AND NOTHING HERE ENFORCES
     * OTHERWISE.** `draft` and `awaitingSignature` are complements on the
     * stage's `isSent` flag, so sending both can only ever answer nothing —
     * which is the honest result of a request that asks for it, not a state to
     * refuse client-side. The stat cards never produce it (each clears every
     * flag before setting its own); a person setting both by hand in the panel
     * gets an empty list, and the "3 filters applied" line above the table is
     * what explains it.
     */
    ...(filters.draft ? { draft: true as const } : {}),
    ...(filters.awaitingSignature ? { awaitingSignature: true as const } : {}),
    ...(filters.acceptedUnbilled ? { acceptedUnbilled: true as const } : {}),
  };
}

/**
 * The same filter set, as the SHEET's filter-view body spells it.
 *
 * 🔴 **KEY BY KEY, AND NEVER A SPREAD OF `filters`.**
 * `AccountsSheetFilterCriteria` is one interface serving BOTH sections while the
 * server's schema is `.strict()` and per section — so `stageId` on an invoices
 * body is a **422 naming it**, and `tsc` cannot see that because the type
 * declares every member of both. Writing the list out is the only thing standing
 * between a shared type and a refusal nobody can read.
 *
 * ⚠️ **AN EMPTY VALUE OMITS THE KEY ENTIRELY**, exactly as `quoteFilterParams`
 * does and for a harder reason: an absent member means *no criterion on that
 * column*, so a `''` or a `0` reaching the sheet would filter a column down to
 * rows matching nothing and return an empty tab that looks like an empty
 * business.
 *
 * ⚠️ **THE AMOUNT BOUNDS STAY STRINGS AND THE IDS BECOME NUMBERS** — the wire
 * says so at each member. Money is decimal text everywhere on this API; an id is
 * a number, and `ID_PATTERN` has already proved this one parses.
 *
 * ⚠️ **AN ID CRITERION MATCHES THE NAME THE SHEET DISPLAYS**, resolved
 * server-side — the tabs carry no id column. **Two records sharing a name both
 * match, and nothing in the sheet can tell them apart.** That is a real
 * divergence from the table, which matches on the id itself.
 */
function quoteSheetCriteria(filters: QuoteFilters): AccountsSheetFilterCriteria {
  return {
    /* Several ids go as CSV (`idListParam`); a body that cannot take a list
       answers a loud 422 on Apply rather than a silently wider sheet. */
    ...(filters.companyId ? { companyId: idListParam(filters.companyId) } : {}),
    ...(filters.contactId ? { contactId: idListParam(filters.contactId) } : {}),
    ...(filters.stageId ? { stageId: idListParam(filters.stageId) } : {}),
    ...(filters.lineProjectId ? { lineProjectId: idListParam(filters.lineProjectId) } : {}),
    ...(filters.accountLeadId ? { accountLeadId: idListParam(filters.accountLeadId) } : {}),
    ...(filters.currency ? { currency: filters.currency } : {}),
    ...(filters.issueDateAfter ? { issueDateAfter: filters.issueDateAfter } : {}),
    ...(filters.issueDateBefore ? { issueDateBefore: filters.issueDateBefore } : {}),
    ...(filters.validTillAfter ? { validTillAfter: filters.validTillAfter } : {}),
    ...(filters.validTillBefore ? { validTillBefore: filters.validTillBefore } : {}),
    ...(filters.amountMin ? { amountMin: filters.amountMin } : {}),
    ...(filters.amountMax ? { amountMax: filters.amountMax } : {}),
    /*
     * ⚠️ **THE THREE FLAGS BIND TO HIDDEN `TRUE`/`FALSE` COLUMNS THE MIRROR
     * WRITES FOR THIS AND NOTHING ELSE** — Sheets criteria are per column and
     * these are derived values with no visible column. If one ever stops being
     * written into the tab, its criterion here **matches nothing rather than
     * failing**: an empty sheet, not an error.
     */
    ...(filters.draft ? { draft: true } : {}),
    ...(filters.awaitingSignature ? { awaitingSignature: true } : {}),
    ...(filters.acceptedUnbilled ? { acceptedUnbilled: true } : {}),
  };
}

/**
 * The API's own sort enum. An unrecognised member is a 422, not a fallback, so
 * the column filters' bare string is NARROWED here rather than cast.
 */
const QUOTE_SORT_KEYS: readonly QuoteListSort[] = [
  'subject',
  COMPANY_SORT_KEY,
  'issueDate',
  'createdAt',
  'updatedAt',
  'id',
  /*
   * 2026-10-09: every member a `QUOTE_COLUMNS` header now sends. ⚠️ Until this
   * list grew, `quoteNumber`, `validTill` and the Grand total header carried a
   * `sortKey` missing from it, so clicking them silently sent `issueDate`.
   * Every column `sortKey` below MUST appear here, or `asQuoteSort` swaps it.
   */
  'quoteNumber',
  'stage',
  'contact',
  'lineProjects',
  'accountLead',
  'validTill',
  'currency',
  'subTotal',
  'discountTotal',
  'taxTotal',
  'amount',
  'billingMode',
  'xeroQuoteNumber',
];

/**
 * What the list is ordered by until somebody clicks a header.
 *
 * 🔴 **THIS CHANGED ON 2026-09-02, FROM `issueDate` DESC TO `company` ASC, AND
 * IT IS A VISIBLE BEHAVIOUR CHANGE RATHER THAN A TIDY-UP.** The owner asked to
 * open this page on quotes and invoices GROUPED BY COMPANY, and the grouping is
 * only honest while the server is ordering by company — so the default sort and
 * the default banding are one decision, not two. The register no longer opens
 * newest-first; it opens alphabetically by customer, which is how the Google
 * Sheet mirror and the Future Billable section already lead.
 *
 * ⚠️ **ASC, not the `desc` every other default here uses.** A name column read
 * descending opens on Z, which is nobody's idea of an alphabetical list — and
 * the Future Billable section already sends `party` ASC for exactly this reason.
 * `desc` is still one click away on the header.
 *
 * ⚠️ **A DEFAULT, NEVER AN OVERRIDE.** It applies only while `table.sort` is
 * null — that is, while nobody has chosen. The moment a header is clicked the
 * choice wins, in both key and direction, and the bands go with it.
 */
const DEFAULT_QUOTE_SORT: QuoteListSort = COMPANY_SORT_KEY;
const DEFAULT_QUOTE_ORDER: ListOrder = 'asc';

/*
 * ⚠️ The fallback here is for an UNRECOGNISED key, which is a different question
 * from "nobody has chosen yet" and deliberately keeps its own answer.
 * ⚠️ *SUPERSEDED 2026-10-09: "It is reachable: three columns carry a `sortKey`
 * the API's enum does not hold".* Every column's `sortKey` is now in
 * `QUOTE_SORT_KEYS`, so this only fires for a key nothing here sends. Sending
 * one to the section default instead would band the table under a header that
 * is not Company.
 */
function asQuoteSort(key: string | undefined): QuoteListSort {
  return QUOTE_SORT_KEYS.includes(key as QuoteListSort) ? (key as QuoteListSort) : 'issueDate';
}

/*
 * Every column is `scope: 'page'`, deliberately: the twelve server filters are
 * already driven by the URL-backed bank above the table, and declaring any of
 * them again on a header would give one query param two uncoordinated controls.
 *
 * `sortKey` appears only on the columns the API can order by. An unrecognised
 * `sort` is a 422, so a header that highlighted while nothing moved would be
 * worse than no control.
 */
const QUOTE_COLUMNS: ColumnSpec<Quote>[] = [
  {
    key: 'quoteNumber',
    label: 'Quote #',
    get: (row) => row.quoteNumber,
    // NULL on every quote today, so this orders nothing until numbers exist.
    sortKey: 'quoteNumber' satisfies QuoteListSort,
  },
  { key: 'subject', label: 'Subject', get: (row) => row.subject, sortKey: 'subject' satisfies QuoteListSort },
  { key: 'stage', label: 'Stage', get: (row) => row.stage?.name ?? null, sortKey: 'stage' satisfies QuoteListSort },
  {
    key: 'company',
    label: 'Company',
    get: (row) => (row.company ? documentCustomerName(row.company) : null),
    // Sorting on this is what puts the company bands on screen — see
    // `COMPANY_SORT_KEY`. Clicking any other sortable header takes them away.
    sortKey: COMPANY_SORT_KEY,
  },
  /*
   * ⚠️ **12 of 279 quotes carry one, measured 2026-09-15, and that is the DATA.**
   * A Xero "Contact" is the ORGANISATION — it becomes the EMERGE company — so a
   * Xero-imported quote has no person to name here, and 277 of these came from
   * Xero. ⛔ **Do NOT fall back to the company's primary contact**: it would fill
   * nearly every cell with somebody who was never on that quote, under a heading
   * saying they were. Owner's decision, 2026-09-15: leave it blank.
   */
  { key: 'contact', label: 'Contact', get: (row) => row.contact?.name ?? null, sortKey: 'contact' satisfies QuoteListSort },
  {
    key: 'project',
    /*
     * ⚠️ **`PROJECTS_ON_DOCUMENT_LABEL`, not a second string literal, and not the
     * bare "Project" this column carried.** Singular was a lie of number the
     * moment the cell became a set — and the word "on this document" is
     * load-bearing beside Company, Contact and Account lead, which are all about
     * the ACCOUNT: a bare "Projects" in that company reads as the account's
     * pursuit list, which is the exact misreading the constant exists to stop.
     */
    label: PROJECTS_ON_DOCUMENT_LABEL,
    /*
     * ⚠️ **`get` IS THE EMPTINESS TEST AND NOTHING ELSE HERE** — the funnel's
     * list comes from `options` and the comparison from `matches`, because the
     * cell is a SET. See `lineProjectOptions` for what accumulating this instead
     * would offer.
     *
     * ⛔ **NO `?? []`.** `lineProjects` is always present and `[]` when no line
     * names a project; writing a coalesce would make a server that stopped
     * sending the key indistinguishable from the 160 quotes that genuinely name
     * none — silently, on the one column whose whole job is to say what a
     * document covers.
     */
    get: (row) => lineProjectsValue(row.lineProjects),
    matches: (row, selected) => lineProjectsMatch(row.lineProjects, selected),
    /*
     * ⚠️ *SUPERSEDED 2026-10-09: "⛔ NO `sortKey` … `/quotes` publishes no such
     * sort member".* It does now — `lineProjects`, backend `32057ed` — and it
     * orders by the COUNT of distinct line projects, not by any name, because a
     * set has no single value. Owner's instruction: every funnelled column sorts.
     */
    sortKey: 'lineProjects' satisfies QuoteListSort,
  },
  {
    key: 'accountLead',
    label: 'Account lead',
    get: (row) => row.accountLead?.name ?? null,
    sortKey: 'accountLead' satisfies QuoteListSort,
  },
  {
    key: 'issueDate',
    label: 'Issue date',
    // Stays the raw string: it sorts lexicographically and `formatDateOnly`
    // renders it without ever constructing a `Date`.
    get: (row) => row.issueDate,
    format: formatDateOnly,
    sortKey: 'issueDate' satisfies QuoteListSort,
  },
  {
    key: 'validTill',
    label: 'Valid till',
    get: (row) => row.validTill,
    format: formatDateOnly,
    sortKey: 'validTill' satisfies QuoteListSort,
  },
  { key: 'currency', label: 'Currency', get: (row) => row.currency, sortKey: 'currency' satisfies QuoteListSort },
  // The raw decimal STRINGS, never `Number(...)`. The funnel groups exact
  // values, and a float round-trip is how two identical figures stop being the
  // same option.
  {
    key: 'subTotal',
    label: 'Sub total',
    get: (row) => row.subTotal,
    numeric: true,
    sortKey: 'subTotal' satisfies QuoteListSort,
  },
  {
    key: 'discountTotal',
    label: 'Discount',
    get: (row) => row.discountTotal,
    numeric: true,
    sortKey: 'discountTotal' satisfies QuoteListSort,
  },
  {
    key: 'taxTotal',
    label: 'Tax',
    get: (row) => row.taxTotal,
    numeric: true,
    sortKey: 'taxTotal' satisfies QuoteListSort,
  },
  {
    key: 'grandTotal',
    label: 'Grand total',
    get: (row) => row.grandTotal,
    numeric: true,
    /*
     * ⛔ `amount`, NOT `grandTotal` — there is no `grandTotal` sort member (a
     * 422), and `amount` orders `q.grand_total` on `/quotes`. This header sent
     * `grandTotal` until 2026-10-09 and `asQuoteSort` silently swapped it for
     * `issueDate`.
     */
    sortKey: 'amount' satisfies QuoteListSort,
  },
  {
    key: 'billingMode',
    label: 'Billing',
    get: (row) => row.billingMode,
    sortKey: 'billingMode' satisfies QuoteListSort,
  },
  // A REFERENCE, not a status. See the header of this file.
  {
    key: 'xeroQuoteNumber',
    label: 'Xero ref',
    get: (row) => row.xeroQuoteNumber,
    sortKey: 'xeroQuoteNumber' satisfies QuoteListSort,
  },
];

const QUOTE_COLUMN_KEYS: readonly string[] = QUOTE_COLUMNS.map((column) => column.key);

/**
 * See `ColumnWidths`. **The sum is 2512px and `minWidth` below must say so.**
 *
 * 128+288+144+224+176+232+168+128+128+116+128+120+112+152+116+152 = 2512
 *
 * ⚠️ *SUPERSEDED 2026-09-15: 2472, with `project` at 192.* The projects column
 * went to 232 because its heading is now `Projects on this document` — the
 * shared constant, not a literal this page chose — and `FilterableTH` TRUNCATES
 * a heading by default. At 192 it read `Projects on this…`, which is a heading
 * that has to be hovered to be understood. **A width is the cheaper half of that
 * pair to move**; the alternative was `wrap` on one header of sixteen, which
 * makes that header alone two lines tall and takes the whole row with it.
 *
 * ⚠️ Sixteen columns cannot fit any desktop this app runs on — the content area
 * is `max-w-7xl` minus a 256px sidebar, so about 1136px at 1440. This table
 * scrolls horizontally and is MEANT to; what it must not do is squeeze Subject
 * and Company to make room for four money columns, which is what happened with
 * no widths declared. There is no actions column here, so scrolling costs
 * nothing but a drag.
 */
const QUOTE_WIDTHS: ColumnWidths = {
  quoteNumber: 'w-[128px]',
  subject: 'w-[288px]',
  stage: 'w-[144px]',
  company: 'w-[224px]',
  contact: 'w-[176px]',
  project: 'w-[232px]',
  accountLead: 'w-[168px]',
  issueDate: 'w-[128px]',
  validTill: 'w-[128px]',
  currency: 'w-[116px]',
  subTotal: 'w-[128px]',
  discountTotal: 'w-[120px]',
  taxTotal: 'w-[112px]',
  grandTotal: 'w-[152px]',
  billingMode: 'w-[116px]',
  xeroQuoteNumber: 'w-[152px]',
};

/**
 * ⚠️ **ITS OWN SCOPE, AND THE THREE MAY NEVER BE SHARED.** See the note on the
 * `use-column-layout` import: one scope across three different column sets means
 * each save drops the keys the other two declare.
 */
const QUOTE_LAYOUT_SCOPE = 'accounts.quotes';

/**
 * ⚠️ **NO LOCKED COLUMN HERE, AND THAT IS MEASURED RATHER THAN ASSUMED.** The
 * quotes table renders no actions lane (`showActions={false}`) and every cell in
 * it is a value or a link to the record's own page — nothing on the row is the
 * only home for a control. `useColumnLayout` still refuses to hide the LAST
 * visible column, so the table can never be emptied.
 *
 * ⛔ **AND NO `pinnedKeys` EITHER, WHICH IS A SEPARATE FACT AND NOT AN
 * OMISSION.** This table pins nothing — there is no lane to pin — so every
 * column is the reader's to freeze on either edge, and there is no lock to
 * explain, so no `lockReasonOf`. Only the Future billables table below needs
 * any of the three.
 */
/*
 * ⚠️ *SUPERSEDED 2026-10-25 (the two paragraphs above): "no actions lane … no
 * locked column … no `pinnedKeys`".* The owner put **Preview PDF** on every
 * quote row, so this table now renders the same locked, pinned actions lane the
 * Future billables table does, for the same reason that one is locked: on
 * desktop the lane is the only home for the control.
 */
const QUOTE_LOCKED_KEYS: readonly string[] = [ACTIONS_COLUMN];

/** Separate from `QUOTE_LOCKED_KEYS` for the reason `FUTURE_PINNED_KEYS` gives. */
const QUOTE_PINNED_KEYS: readonly string[] = [ACTIONS_COLUMN];

/** The lane is a layout key so the manager lists it (disabled). */
const QUOTE_LAYOUT_KEYS: readonly string[] = [...QUOTE_COLUMN_KEYS, ACTIONS_COLUMN];

/** One icon button. See `SectionTableHead`'s `actionsWidth`. */
const QUOTE_ACTIONS_WIDTH = 'w-[64px]';

/** The floor's widths, lane included — see `FUTURE_FLOOR_WIDTHS`. */
const QUOTE_FLOOR_WIDTHS: ColumnWidths = {
  ...QUOTE_WIDTHS,
  [ACTIONS_COLUMN]: QUOTE_ACTIONS_WIDTH,
};

const QUOTE_LABELS = new Map<string, string>([
  ...QUOTE_COLUMNS.map((column) => [column.key, column.label] as const),
  // Not a `ColumnSpec`, so it has no label to read — see `ACTIONS_COLUMN`.
  [ACTIONS_COLUMN, 'Actions'],
]);

function quoteColumnLabel(key: string): string {
  return QUOTE_LABELS.get(key) ?? key;
}

/** A why-this-is-disabled sentence — see `futureLockReason`. */
const QUOTE_LOCK_REASONS: Record<string, string> = {
  [ACTIONS_COLUMN]: 'This column holds the only desktop route to previewing a quote PDF, so it cannot be hidden',
};

function quoteLockReason(key: string): string | undefined {
  return QUOTE_LOCK_REASONS[key];
}

/** The viewer's target for a listed quote. */
function quotePreviewTarget(row: Quote): QuotePdfPreviewTarget {
  return {
    id: row.id,
    quoteNumber: row.quoteNumber?.trim() || null,
    subject: row.subject,
    // A quote is synced with Xero, so its preview names the Billing name (owner 2026-10-25).
    companyName: row.company ? documentCustomerName(row.company) : null,
  };
}

/*
 * ⚠️ **EVERY `max-w` BELOW MIRRORS THAT COLUMN'S ENTRY IN `QUOTE_WIDTHS`, AND
 * IT IS NOT REDUNDANT WITH IT.** The header's `w-[…]` is what the column ASKS
 * for; this is what stops a cell demanding more. `RefCell` and the two link
 * cells use `truncate`, which is `white-space: nowrap` — so their min-content
 * width is the WHOLE string, and without a cap on the cell a long company name
 * would widen its column past the declared width and squeeze every other one.
 * The two have to move together; a `max-w` wider than the `w-[…]` quietly
 * re-opens that.
 */
function quoteCells(
  row: Quote,
  overdue: OverdueLookup,
  onPreview: (target: QuotePdfPreviewTarget) => void,
): Readonly<Record<string, SectionCell>> {
  return {
    quoteNumber: {
      className: 'max-w-[128px]',
      content: (
        <TextLink
          to={`/quotes/${row.id}`}
          title={row.quoteNumber?.trim() || `#${row.id}`}
          className="block truncate text-xs tabular"
        >
          {row.quoteNumber?.trim() || `#${row.id}`}
        </TextLink>
      ),
    },
    subject: { className: 'max-w-[288px]', content: <TextCell value={row.subject} /> },
    stage: { className: 'max-w-[144px]', content: <StageBadge stage={row.stage} /> },
    company: {
      className: 'max-w-[224px] min-w-0',
      content: (
        <CompanyCell
          to={row.company ? `/companies/${row.company.id}` : null}
          name={row.company ? documentCustomerName(row.company) : undefined}
          overdue={overdueFor(overdue, row.company)}
        />
      ),
    },
    contact: {
      className: 'max-w-[176px]',
      content: (
        <RefCell to={row.contact ? `/contacts/${row.contact.id}` : null} name={row.contact?.name} />
      ),
    },
    project: {
      className: 'max-w-[232px]',
      content: <DocumentProjectsCell projects={row.lineProjects} />,
    },
    accountLead: {
      className: 'max-w-[168px]',
      content: <TextCell value={row.accountLead?.name ?? null} />,
    },
    issueDate: { content: <DateCell value={row.issueDate} /> },
    /*
     * ⚠️ **13 of 279 quotes carry one, measured 2026-09-15.** The plumbing is
     * correct end to end and the column is nearly empty because the Xero importer
     * does not read Xero's `ExpiryDate` — a backfill of the 266 is the fix, and it
     * is the importer's, not this cell's. Do not invent a date here.
     */
    validTill: { content: <DateCell value={row.validTill} /> },
    currency: {
      className: 'whitespace-nowrap text-xs tabular',
      content: row.currency ?? <NotRecorded />,
    },
    /*
     * Right-aligned and tabular, ALWAYS with the currency code beside the grand
     * total. There is no exchange rate anywhere on this page, so a figure that
     * does not say which currency it is in is a figure somebody will add to
     * another one.
     *
     * ⚠️ **AND HIDING THE CURRENCY COLUMN DOES NOT TAKE THAT AWAY**, which is
     * why the code is on the figure rather than left to its own column.
     */
    subTotal: { numeric: true, content: <InvoiceMoney value={row.subTotal} /> },
    discountTotal: { numeric: true, content: <InvoiceMoney value={row.discountTotal} /> },
    taxTotal: { numeric: true, content: <InvoiceMoney value={row.taxTotal} /> },
    grandTotal: {
      numeric: true,
      content: <InvoiceMoney value={row.grandTotal} currency={row.currency} />,
    },
    billingMode: {
      content: (
        <Badge tone="neutral" variant="outline">
          {row.billingMode === 'staged' ? 'Staged' : 'Full'}
        </Badge>
      ),
    },
    xeroQuoteNumber: {
      className: 'max-w-[152px]',
      /* A dash here means "no number recorded", never "not in Xero". */
      content: (
        <span
          title={row.xeroQuoteNumber?.trim() || undefined}
          className="block truncate text-xs text-fg-secondary tabular"
        >
          {row.xeroQuoteNumber?.trim() || '—'}
        </span>
      ),
    },
    /*
     * Every stage (owner, 2026-10-25). `pinned` is applied by `SectionCells`.
     * The button renders nothing without `quotes.read`.
     */
    [ACTIONS_COLUMN]: {
      numeric: true,
      className: 'max-w-[64px]',
      content: (
        <div className="flex items-center justify-end">
          <PreviewQuotePdfButton target={quotePreviewTarget(row)} onPreview={onPreview} variant="row" />
        </div>
      ),
    },
  };
}

function QuoteRowCells({
  row,
  overdue,
  visibleKeys,
  frozenSideOf,
  frozenOffsets,
  onPreview,
}: {
  row: Quote;
  overdue: OverdueLookup;
  visibleKeys: readonly string[];
  frozenSideOf: (key: string) => FrozenSide | undefined;
  frozenOffsets: Record<string, number>;
  onPreview: (target: QuotePdfPreviewTarget) => void;
}) {
  return (
    <TR interactive>
      <SectionCells
        cells={quoteCells(row, overdue, onPreview)}
        visibleKeys={visibleKeys}
        frozenSideOf={frozenSideOf}
        frozenOffsets={frozenOffsets}
      />
    </TR>
  );
}

function QuoteRowCard({
  row,
  overdue,
  onPreview,
}: {
  row: Quote;
  overdue: OverdueLookup;
  onPreview: (target: QuotePdfPreviewTarget) => void;
}) {
  return (
    <li className="border-b border-border px-4 py-3 last:border-b-0">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Link
            to={`/quotes/${row.id}`}
            className="block truncate text-sm font-medium text-fg underline-offset-2 transition-colors duration-150 ease-out hover:underline motion-reduce:transition-none"
          >
            {row.subject}
          </Link>
          <div className="mt-0.5 flex flex-wrap items-baseline gap-x-2 text-xs text-fg-muted">
            <span className="tabular">{row.quoteNumber?.trim() || `#${row.id}`}</span>
            {row.issueDate && <span className="tabular">{formatDateOnly(row.issueDate)}</span>}
          </div>
        </div>
        <span className="shrink-0 text-sm">
          <InvoiceMoney value={row.grandTotal} currency={row.currency} />
        </span>
      </div>

      <div className="mt-2 flex items-center justify-between gap-3">
        <StageBadge stage={row.stage} />
        <PreviewQuotePdfButton target={quotePreviewTarget(row)} onPreview={onPreview} variant="card" />
      </div>

      <dl className="mt-2.5 grid gap-1.5 text-xs">
        {/*
         * ⚠️ **`min-w-0` ON THIS ROW, AND ONLY THIS ROW NEEDS IT.** The `<dl>`
         * is a GRID, so each row is a grid item defaulting to
         * `min-width: auto` — it sizes to its content's minimum and never goes
         * under it. Every other row's content is a `RefCell` or a `TextCell`,
         * both of which clip (`truncate` / `line-clamp-2`), so their minimum is
         * effectively nothing; the overdue Badge is `whitespace-nowrap` and does
         * not, so without this the row grows to fit the whole bracket and takes
         * the card past the viewport instead of letting the chip truncate.
         * **Measured before the fix: the row's right edge sat at 432 on a 375
         * viewport** — clipped by an ancestor rather than scrolling the page, so
         * an overflow probe reported nothing and only a width measurement found
         * it. This is the line `OverdueChip`'s header says the caller must
         * write.
         */}
        <div className="flex min-w-0 items-baseline gap-2">
          <dt className="w-28 shrink-0 text-fg-subtle">Company</dt>
          <dd className="m-0 min-w-0 flex-1">
            {/*
             * ⚠️ **THE CHIP RENDERS AT EVERY WIDTH.** This card list is the
             * whole view below `lg`, and this repo has already had a working
             * feature reported as DELETED because it was only drawn on desktop.
             */}
            <CompanyCell
              to={row.company ? `/companies/${row.company.id}` : null}
              name={row.company ? documentCustomerName(row.company) : undefined}
              overdue={overdueFor(overdue, row.company)}
            />
          </dd>
        </div>
        {/*
         * ⚠️ **ADDED 2026-09-15, WITH THE COLUMN IT MIRRORS.** This card is the
         * whole view below `lg`, and the omission was costless only while the
         * column read the dead header project and was blank on every row. It
         * names a real set on 43% of quotes now, so leaving it out would be the
         * desktop-only feature this file has already had reported as DELETED.
         * ⚠️ **The heading is the same shared constant as the column** — two
         * spellings of one fact, one breakpoint apart, is worse than none.
         */}
        <div className="flex items-baseline gap-2">
          <dt className="w-28 shrink-0 text-fg-subtle">{PROJECTS_ON_DOCUMENT_LABEL}</dt>
          <dd className="m-0 min-w-0 flex-1">
            <DocumentProjectsCell projects={row.lineProjects} />
          </dd>
        </div>
        <div className="flex items-baseline gap-2">
          <dt className="w-28 shrink-0 text-fg-subtle">Account lead</dt>
          <dd className="m-0 min-w-0 flex-1">
            <TextCell value={row.accountLead?.name ?? null} />
          </dd>
        </div>
        <div className="flex items-baseline gap-2">
          <dt className="w-28 shrink-0 text-fg-subtle">Valid till</dt>
          <dd className="m-0 min-w-0 flex-1">
            <DateCell value={row.validTill} />
          </dd>
        </div>
        <div className="flex items-baseline gap-2">
          <dt className="w-28 shrink-0 text-fg-subtle">Xero ref</dt>
          <dd className="m-0 min-w-0 flex-1">
            <TextCell value={row.xeroQuoteNumber} />
          </dd>
        </div>
      </dl>
    </li>
  );
}

function QuotesSection({
  overdue,
  summaryBar,
  onPreview,
  reloadToken,
}: {
  overdue: OverdueLookup;
  /** The page-built stat-card filter bar, passed straight through. */
  summaryBar: React.ReactNode;
  /** The page's one PDF viewer — see the `QuotePdfPreview` import. */
  onPreview: (target: QuotePdfPreviewTarget) => void;
  /**
   * Bumped by the page when the viewer changed a quote (Mark Sent, Send to
   * Xero), so this list re-reads its stage.
   */
  reloadToken: number;
}) {
  const [searchParams, setSearchParams] = useSearchParams();

  const applySavedFilters = useSavedFilterApply<QuoteFilterKey>();
  useSavedFilters({
    scope: 'accounts-dashboard-quotes',
    keys: QUOTE_FILTER_KEYS,
    store: preferencesStore,
    apply: applySavedFilters,
  });

  const [result, setResult] = useState<Paginated<Quote> | null>(null);
  const [search, setSearch] = useRememberedState('quotes-search', '');
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showMoreFilters, setShowMoreFilters] = useRememberedState('quotes-more-filters', false);
  const [editing, setEditing] = useState<Quote | null>(null);

  /*
   * `useSearchParams()` takes NO argument: its first parameter is a default
   * init, not options, so `useSearchParams({ replace: true })` would write a
   * literal `replace=true` into the query string and then into these filters.
   */
  const searchParamsKey = searchParams.toString();
  const filters = useMemo(
    () => readQuoteFilters(new URLSearchParams(searchParamsKey)),
    [searchParamsKey],
  );
  const activeFilterCount = QUOTE_FILTER_KEYS.filter((key) => filters[key] !== '').length;

  // Keyed on `result` so the identity is stable between renders: `?? []` is a
  // fresh array every time, which would make every memo below it do nothing.
  const rows = useMemo(() => result?.data ?? [], [result]);
  const total = result?.meta.total ?? 0;
  // `meta.totalPages` is 0 (not 1) for an empty set, which would disable both
  // pager buttons and render "Page 1 of 0".
  const totalPages = Math.max(result?.meta.totalPages ?? 1, 1);
  const firstRow = total === 0 ? 0 : (page - 1) * PER_PAGE + 1;
  const lastRow = total === 0 ? 0 : firstRow + rows.length - 1;
  const initialLoad = loading && !result;

  /*
   * ⚠️ **THE COLUMN LIST IS DERIVED PER RENDER FOR ONE REASON: the projects
   * funnel's options.** Every other column's option list is accumulated from the
   * loaded rows by the hook, which is right for a scalar cell and wrong for a
   * set — see `lineProjectOptions`. `options` can only be supplied where the rows
   * are in hand, so the constant is spread and exactly one entry is replaced.
   *
   * ⚠️ **`QUOTE_COLUMN_KEYS` and `QUOTE_LABELS` still come off the CONSTANT**,
   * deliberately: neither a key nor a label depends on the data, and deriving
   * them here would rebuild the column layout's identity list on every render.
   */
  const columns = useMemo<ColumnSpec<Quote>[]>(
    () =>
      QUOTE_COLUMNS.map((column) =>
        column.key === 'project' ? { ...column, options: lineProjectOptions(rows) } : column,
      ),
    [rows],
  );

  const table = useTableFilters(rows, columns);

  /*
   * ⚠️ **THE LAYOUT IS THE READER'S CHROME AND THE FILTERS ARE THE ROWS — two
   * separate stores, deliberately.** `useTableFilters` narrows what is shown and
   * lives in component state; this decides which columns exist at all and lives
   * on the account. Neither is in the URL: a shared link must show the recipient
   * the sender's ROWS, never impose the sender's hidden columns.
   */
  const layout = useColumnLayout(
    QUOTE_LAYOUT_SCOPE,
    QUOTE_LAYOUT_KEYS,
    QUOTE_LOCKED_KEYS,
    QUOTE_PINNED_KEYS,
  );
  const headRowRef = useRef<HTMLTableRowElement | null>(null);
  /*
   * ⚠️ *SUPERSEDED 2026-10-25: "No actions lane on this table, so the rendered
   * order is the layout's own."* The Preview PDF lane is clamped last exactly as
   * Future billables' is — see `renderKeys`.
   */
  const renderedKeys = renderKeys(layout.visibleKeys, true);
  const frozenOffsets = useFrozenOffsets(
    headRowRef,
    renderedKeys,
    layout.frozenSideOf,
    QUOTE_PINNED_KEYS,
  );
  const minWidth = tableMinWidth(QUOTE_FLOOR_WIDTHS, renderedKeys);
  /* `SectionPanel` adds the skeleton's lane cell itself — see Future billables. */
  const skeletonColumnCount = renderedKeys.length - 1;

  /*
   * `table.sort` is null until a header is clicked, and it is NOT URL-backed —
   * `useTableFilters` holds it in component state, and neither the query string
   * (`QUOTE_FILTER_KEYS`) nor the saved filter set carries `sort` or `order`. So
   * the two branches here are "nobody has chosen" and "somebody has", and the
   * chosen one always wins.
   *
   * Both `sort` and `order` are sent on every request rather than left to the
   * server's own default — the default is not published, and a first render that
   * assumes one order while the response arrives in another is a table that
   * appears to re-sort itself on load.
   */
  const sort = table.sort ? asQuoteSort(table.sort.key) : DEFAULT_QUOTE_SORT;
  const order: ListOrder = table.sort?.direction ?? DEFAULT_QUOTE_ORDER;
  const visibleRows = table.visibleRows;
  const hiddenByColumns = rows.length - visibleRows.length;

  /*
   * 🔴 **BAND ONLY WHILE THE SERVER IS ACTUALLY ORDERING BY COMPANY.** Read from
   * `sort` — the value about to be SENT — and never from `table.sort?.key`,
   * which is a bare header string that has not been narrowed yet. Switching the
   * sort to Issue Date takes the bands away in the same render that reorders the
   * rows; banding a date-ordered list would put a banner above nearly every row
   * and claim every customer has one document. See `COMPANY_SORT_KEY`.
   */
  const banded = sort === COMPANY_SORT_KEY;
  const groups = useMemo(
    () => (banded ? groupRows(visibleRows, (row) => companyGroupOf(row.company, 'document')) : []),
    [banded, visibleRows],
  );

  /*
   * A new sort re-orders the whole table, so page 4 of the old order is
   * meaningless in the new one. Adjusted during render rather than in an effect:
   * `sort` lives inside `useTableFilters`, so there is no onSort event to hang
   * it off, and an effect would commit one render (and fire one request)
   * against the stale page first.
   */
  const sortToken = `${sort}:${order}`;
  const [lastSortToken, setLastSortToken] = useState(sortToken);
  if (lastSortToken !== sortToken) {
    setLastSortToken(sortToken);
    setPage(1);
  }

  const load = useCallback(
    (signal?: AbortSignal) => {
      setLoading(true);
      return api
        .listQuotes(
          {
            page,
            perPage: PER_PAGE,
            sort,
            order,
            ...(search ? { search } : {}),
            ...quoteFilterParams(filters),
          },
          signal,
        )
        .then((data) => {
          setResult(data);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setError(caught instanceof Error ? caught.message : 'Failed to load quotes');
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [page, search, sort, order, filters],
  );

  // Debounced at 250ms so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => void load(controller.signal), 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
    // `reloadToken`: the PDF viewer changed a quote's stage — re-read this page.
  }, [load, reloadToken]);

  const companies = useCompanyOptions();
  const projects = useProjectOptions();
  const stages = useQuoteStageOptions();
  const currencies = useCurrencyOptions();
  const staff = useUserOptions(true, true);

  /*
   * id → name harvested from the rows currently on screen. Cheap, and it is the
   * only label source for a value the option lists cannot offer: a master row
   * deactivated since the quote was raised still filters correctly, but the
   * option hooks list active rows only.
   */
  const rowLabels = useMemo(() => {
    const maps: Record<QuoteIdFilterKey, Map<string, string>> = {
      companyId: new Map(),
      contactId: new Map(),
      stageId: new Map(),
      lineProjectId: new Map(),
      accountLeadId: new Map(),
    };
    for (const row of result?.data ?? []) {
      if (row.company) maps.companyId.set(String(row.company.id), row.company.name);
      if (row.contact) maps.contactId.set(String(row.contact.id), row.contact.name);
      if (row.stage) maps.stageId.set(String(row.stage.id), row.stage.name);
      /* Every project on the row, not one: the filter names a LINE project, so a
         document naming three can be the label source for any of the three. */
      for (const project of row.lineProjects) {
        maps.lineProjectId.set(String(project.id), project.name);
      }
      if (row.accountLead) {
        maps.accountLeadId.set(String(row.accountLead.id), row.accountLead.name);
      }
    }
    return maps;
  }, [result]);

  // Masters render in the order the API sent them (sortOrder ASC, name ASC).
  // That ordering is curated — re-sorting alphabetically would discard it.
  const stageOptions = useMemo<ComboboxOption[]>(
    () =>
      stages.stages.map((stage) => ({
        value: String(stage.id),
        // Retired stages are offered too — this is a FILTER over quotes that
        // already hold one, not a picker for a new value, so hiding a retired
        // stage would make its quotes unfindable.
        label: stage.isActive ? stage.name : `${stage.name} (retired)`,
      })),
    [stages.stages],
  );
  const projectOptions = useMemo<ComboboxOption[]>(
    () => projects.projects.map((project) => ({ value: String(project.id), label: project.name })),
    [projects.projects],
  );
  const currencyPickerOptions = useMemo<ComboboxOption[]>(
    () => currencyOptions(currencies.currencies, filters.currency || null),
    [currencies.currencies, filters.currency],
  );
  /*
   * ⚠️ `byGroup` degrades to the UN-NARROWED directory when the group master
   * cannot be read, which is the right direction here: over-offering costs a
   * filter that matches nothing, where under-offering silently hides the person
   * somebody is looking for.
   */
  const leadOptions = useMemo<ComboboxOption[]>(
    () =>
      staff.byGroup.account_lead.users.map((user) => ({
        value: String(user.id),
        label: user.name.trim() || user.email,
      })),
    [staff.byGroup.account_lead.users],
  );

  const setFilter = useCallback(
    (key: QuoteFilterKey, value: string) => {
      const next = new URLSearchParams(searchParamsKey);
      if (value) next.set(key, value);
      else next.delete(key);
      // Changing a filter is not a navigation, and a history entry per keystroke
      // would make Back mean "undo one filter".
      setSearchParams(next, { replace: true });
      setPage(1);
    },
    [searchParamsKey, setSearchParams],
  );

  const clearFilters = useCallback(() => {
    const next = new URLSearchParams(searchParamsKey);
    for (const key of QUOTE_FILTER_KEYS) next.delete(key);
    setSearchParams(next, { replace: true });
    setPage(1);
  }, [searchParamsKey, setSearchParams]);

  function clearEverything() {
    setSearch('');
    table.clearAll();
    clearFilters();
  }

  const filtered = activeFilterCount > 0;
  const countLabel = initialLoad
    ? 'Loading…'
    : `${total} ${total === 1 ? 'quote' : 'quotes'}${search ? ` matching “${search}”` : ''}${
        filtered ? ` (${activeFilterCount} ${activeFilterCount === 1 ? 'filter' : 'filters'})` : ''
      }`;

  return (
    <>
      <SectionPanel
        title="All quotes"
        countLabel={countLabel}
        summaryBar={summaryBar}
        moreFiltersId="accounts-quotes-more-filters"
        showMoreFilters={showMoreFilters}
        onToggleMoreFilters={() => setShowMoreFilters((open) => !open)}
        activeFilterCount={activeFilterCount}
        onClearFilters={clearFilters}
        columnActiveCount={table.activeCount}
        columnKeys={QUOTE_COLUMN_KEYS}
        headerFor={table.headerFor}
        clearColumns={table.clearAll}
        hiddenByColumns={hiddenByColumns}
        rowCount={rows.length}
        error={error}
        errorTitle="Couldn’t load quotes."
        loading={loading}
        hasResult={result !== null}
        initialLoad={initialLoad}
        isEmpty={visibleRows.length === 0}
        // Summed from `QUOTE_WIDTHS` over the VISIBLE columns, so hiding one
        // shrinks the table's floor. See `tableMinWidth`.
        minWidth={minWidth}
        columnManager={
          /*
           * ⚠️ **`hidden lg:flex`, BECAUSE THE CONTROL ONLY GOVERNS THE TABLE.**
           * Below `lg` this section is the `lg:hidden` card list, whose fields
           * are chosen by `QuoteRowCard` and are not columns — so a Columns
           * button there would change nothing on screen, which is the
           * affordance-with-one-outcome this app removes everywhere else.
           */
          <ColumnManager
            layout={layout}
            labelOf={quoteColumnLabel}
            lockedKeys={QUOTE_LOCKED_KEYS}
            lockReasonOf={quoteLockReason}
            pinnedKeys={QUOTE_PINNED_KEYS}
            className="hidden lg:flex"
          />
        }
        // LIVE, not `QUOTE_COLUMNS.length` — the skeleton must have exactly as
        // many cells as the header, or the layout jumps a column over as the
        // real rows arrive. The lane's cell is added by `SectionPanel`.
        columnCount={skeletonColumnCount}
        showActions
        page={page}
        totalPages={totalPages}
        onPage={setPage}
        firstRow={firstRow}
        lastRow={lastRow}
        total={total}
        emptyState={
          <EmptyStateSlot
            noun="quotes"
            search={search}
            filtered={filtered}
            columnsHidAll={rows.length > 0 && visibleRows.length === 0}
            onClearColumns={table.clearAll}
            onClear={clearEverything}
            hasError={Boolean(error)}
            emptyDescription="A row appears here for every quote raised in EMERGE."
          />
        }
        tableHead={
          <SectionTableHead
            columns={columns}
            headerFor={table.headerFor}
            widths={QUOTE_WIDTHS}
            visibleKeys={renderedKeys}
            frozenSideOf={layout.frozenSideOf}
            frozenOffsets={frozenOffsets}
            headRowRef={headRowRef}
            actionsWidth={QUOTE_ACTIONS_WIDTH}
          />
        }
        tableBody={
          banded ? (
            <>
              {groups.map((group) => (
                <TBody key={group.key}>
                  <CompanyBandRow
                    label={group.label}
                    count={group.rows.length}
                    /* LIVE: the banner must span the columns actually on screen,
                       or the rule stops short of the right edge — which throws
                       nothing, renders, and just looks like a broken table. */
                    colSpan={renderedKeys.length}
                    overdue={overdueFor(overdue, groupCompany(group))}
                  />
                  {/*
                    ⚠️ `null`, not `overdue` — the band above carries the bracket
                    for this whole company, and repeating it on every row would
                    read as each document's own debt. See `CompanyCell`.
                  */}
                  {group.rows.map((row) => (
                    <QuoteRowCells
                      key={row.id}
                      row={row}
                      overdue={null}
                      visibleKeys={renderedKeys}
                      frozenSideOf={layout.frozenSideOf}
                      frozenOffsets={frozenOffsets}
                      onPreview={onPreview}
                    />
                  ))}
                </TBody>
              ))}
            </>
          ) : (
            <TBody>
              {visibleRows.map((row) => (
                <QuoteRowCells
                  key={row.id}
                  row={row}
                  overdue={overdue}
                  visibleKeys={renderedKeys}
                  frozenSideOf={layout.frozenSideOf}
                  frozenOffsets={frozenOffsets}
                  onPreview={onPreview}
                />
              ))}
            </TBody>
          )
        }
        cards={
          banded ? (
            <>
              {groups.map((group) => (
                <section key={group.key}>
                  <CompanyBandHeading
                    label={group.label}
                    count={group.rows.length}
                    overdue={overdueFor(overdue, groupCompany(group))}
                  />
                  <ul className="m-0 list-none p-0">
                    {group.rows.map((row) => (
                      <QuoteRowCard key={row.id} row={row} overdue={null} onPreview={onPreview} />
                    ))}
                  </ul>
                </section>
              ))}
            </>
          ) : (
            <ul className="m-0 list-none p-0">
              {visibleRows.map((row) => (
                <QuoteRowCard key={row.id} row={row} overdue={overdue} onPreview={onPreview} />
              ))}
            </ul>
          )
        }
        filters={
          <>
            <Field label="Search">
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  type="search"
                  placeholder="Search…"
                  leadingIcon={<SearchIcon />}
                  aria-describedby={describedBy}
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                />
              )}
            </Field>

            <FilterField
              label="Company"
              value={filters.companyId}
              onChange={(value) => setFilter('companyId', value)}
              options={withSelected(
                companies.options,
                filters.companyId,
                rowLabels.companyId,
                'Company',
              )}
              allLabel="All companies"
              loading={companies.loading}
              failed={companies.failed}
              multi
              searchPlaceholder="Search companies…"
            />

            <FilterField
              label="Stage"
              multi
              value={filters.stageId}
              onChange={(value) => setFilter('stageId', value)}
              options={withSelected(stageOptions, filters.stageId, rowLabels.stageId, 'Stage')}
              allLabel="Any stage"
              loading={stages.loading}
              failed={stages.failed}
            />
          </>
        }
        moreFilters={
          <>
            {/* Narrows to documents whose LINE ITEMS name this project. See
                `QUOTE_ID_FILTER_KEYS` for why it is not `projectId`, which
                matched nothing on any document in production. */}
            <FilterField
              label="Project"
              multi
              value={filters.lineProjectId}
              onChange={(value) => setFilter('lineProjectId', value)}
              options={withSelected(
                projectOptions,
                filters.lineProjectId,
                rowLabels.lineProjectId,
                'Project',
              )}
              allLabel="All projects"
              loading={projects.loading}
              failed={projects.failed}
              searchPlaceholder="Search projects…"
            />

            <FilterField
              label="Account lead"
              multi
              value={filters.accountLeadId}
              onChange={(value) => setFilter('accountLeadId', value)}
              options={withSelected(
                leadOptions,
                filters.accountLeadId,
                rowLabels.accountLeadId,
                'User',
              )}
              allLabel="Any account lead"
              loading={staff.loading}
              failed={staff.failed}
              searchPlaceholder="Search people…"
            />

            <FilterField
              label="Currency"
              value={filters.currency}
              onChange={(value) => setFilter('currency', value)}
              options={currencyPickerOptions}
              allLabel="Any currency"
              loading={currencies.loading}
              failed={currencies.failed}
              searchPlaceholder="Search currencies…"
            />

            {/*
              * The date and amount bounds write to the URL on every keystroke,
              * and `readQuoteFilters` drops anything that does not parse — so a
              * half-typed date narrows nothing rather than 422ing the list.
              */}
            <TextFilterField
              label="Issued from"
              type="date"
              value={filters.issueDateAfter}
              onChange={(value) => setFilter('issueDateAfter', value)}
            />
            <TextFilterField
              label="Issued to"
              type="date"
              value={filters.issueDateBefore}
              onChange={(value) => setFilter('issueDateBefore', value)}
            />
            {/*
             * ⚠️ **THE STAT CARDS SET THESE THREE KEYS, SO THE KEYS NEED
             * CONTROLS.** A filter that is counted in "(1 filter)" and appears
             * nowhere in the panel is a narrowed list with no visible cause —
             * the reader can only undo it by finding Clear filters or by
             * guessing that the card toggles. Same rule as the companies board
             * banner: never leave somebody hunting for a control that is not on
             * screen. **A card added to the Quotes row means a control added
             * here, in the same change.**
             *
             * All three are read off the stage's own `isSent` / `isAccepted` /
             * `isDeclined` flags SERVER-SIDE, never off a stage name — so none
             * of them is a narrowing of the Stage picker above, and each
             * composes with it.
             *
             * ⚠️ **"Draft" and "Awaiting signature" are COMPLEMENTS on `isSent`,
             * not opposites of each other**, and "Accepted, not invoiced" is a
             * third population rather than the far end of a scale — a sent quote
             * that was declined is in none of them. The labels say what each
             * one IS rather than implying a pipeline, because a set of three
             * tickboxes reads as exhaustive and these are not.
             */}
            <FilterField
              label="Draft"
              value={filters.draft}
              onChange={(value) => setFilter('draft', value)}
              options={FLAG_OPTIONS}
              allLabel="Any"
              loading={false}
              failed={false}
            />

            <FilterField
              label="Awaiting signature"
              value={filters.awaitingSignature}
              onChange={(value) => setFilter('awaitingSignature', value)}
              options={FLAG_OPTIONS}
              allLabel="Any"
              loading={false}
              failed={false}
            />

            {/*
             * ⚠️ **"not invoiced" MEANS NOTHING RAISED AT ALL** — a staged quote
             * billed line by line leaves this population on its first invoice,
             * with plenty still unbilled. The label does not say "outstanding to
             * invoice", which would claim that wider set.
             */}
            <FilterField
              label="Accepted, not invoiced"
              value={filters.acceptedUnbilled}
              onChange={(value) => setFilter('acceptedUnbilled', value)}
              options={FLAG_OPTIONS}
              allLabel="Any"
              loading={false}
              failed={false}
            />

            <TextFilterField
              label="Valid till after"
              type="date"
              value={filters.validTillAfter}
              onChange={(value) => setFilter('validTillAfter', value)}
            />
            <TextFilterField
              label="Valid till before"
              type="date"
              value={filters.validTillBefore}
              onChange={(value) => setFilter('validTillBefore', value)}
            />
            <div className="grid grid-cols-2 gap-3">
              <TextFilterField
                label="Total from"
                inputMode="decimal"
                value={filters.amountMin}
                onChange={(value) => setFilter('amountMin', value)}
              />
              <TextFilterField
                label="Total to"
                inputMode="decimal"
                value={filters.amountMax}
                onChange={(value) => setFilter('amountMax', value)}
              />
            </div>
          </>
        }
      />

      {/*
        * The SAME modal `/quotes`, `/quotes/:id` and `/deals/:id` mount — not a
        * copy. `onSaved` re-reads rather than splicing: this list is sorted,
        * paginated and narrowed by up to twelve filters, so a row inserted at
        * the top would sit outside the server's own ordering and a row that does
        * not match the active filters would appear on a page it has no business
        * being on.
        */}
      {/*
       * 🔴 **THE CREATE MODAL WAS MOUNTED HERE AND IS GONE, 2026-09-02.** It was
       * opened by the header's "New quote" button and by nothing else, so
       * removing that button left it in the tree with no way to reach it — a
       * modal permanently `open={false}`, which no build, lint or render can
       * report. Raising a quote lives on `/quotes`, which mounts the same
       * `QuoteFormModal`.
       *
       * The EDIT mount below is untouched: it is opened from a row on this page.
       */}
      <QuoteFormModal
        open={editing !== null}
        onClose={() => setEditing(null)}
        {...(editing ? { quote: editing } : {})}
        // The list carries no line count, so the totals inputs must fail safe:
        // `'unknown'` is treated exactly as `true` and sends the reader to the
        // detail page rather than offering four inputs the next line write would
        // silently overwrite.
        hasLineItems="unknown"
        onSaved={() => {
          setEditing(null);
          void load();
        }}
      />
    </>
  );
}

/* =========================================================================
 * INVOICES
 * ===================================================================== */

/* `lineProjectId` for the reason stated at `QUOTE_ID_FILTER_KEYS` — read it. */
const INVOICE_ID_FILTER_KEYS = [
  'companyId',
  'contactId',
  'statusId',
  'lineProjectId',
  'accountLeadId',
  'quoteId',
] as const;
/**
 * ⚠️ **`billDate*` AND `dueDate*` ARE TWO DIFFERENT COLUMNS, NOT TWO SPELLINGS OF
 * ONE.** Bill date is when the invoice was RAISED; due date is when it must be
 * PAID. Substituting one for the other is a clean `200` over the wrong
 * population, and the near-miss is the hazard — see `invoiceFilterParams`, and
 * `InvoiceListParams` in `@/lib/types` for the wire rules.
 *
 * ⚠️ **NEITHER PAIR PARTITIONS THE LIST.** Both bounds are INCLUSIVE, and an
 * invoice with a NULL `dueDate` matches NEITHER of them — exactly as a NULL
 * `billDate` matches neither of its own. So "every invoice due after X" is not
 * what a lower bound answers, and no control here may be worded as though it
 * were. There is deliberately no `OR IS NULL` on either side of this.
 */
const INVOICE_TEXT_FILTER_KEYS = [
  'currency',
  'billDateAfter',
  'billDateBefore',
  'dueDateAfter',
  'dueDateBefore',
  'amountMin',
  'amountMax',
] as const;
/**
 * ⚠️ **THESE ARE NESTED POPULATIONS, NOT FOUR SIBLINGS** — an overdue invoice is
 * by definition unsettled, and one 90+ days overdue is by definition overdue —
 * so they are offered as separate pickers rather than as one bank of tickboxes,
 * and the stat cards never set two: each card clears every flag before setting
 * its own. `dueIn30Days` is the odd one, and it is DISJOINT from the two overdue
 * keys rather than nested in them: an invoice cannot be both past due and due in
 * the next 30 days. See `QUOTE_FLAG_FILTER_KEYS` for why this is its own group.
 */
const INVOICE_FLAG_FILTER_KEYS = [
  'unsettled',
  'overdue',
  'overdue90Plus',
  'dueIn30Days',
] as const;
const INVOICE_FILTER_KEYS = [
  ...INVOICE_ID_FILTER_KEYS,
  ...INVOICE_TEXT_FILTER_KEYS,
  ...INVOICE_FLAG_FILTER_KEYS,
] as const;

type InvoiceIdFilterKey = (typeof INVOICE_ID_FILTER_KEYS)[number];
type InvoiceFilterKey = (typeof INVOICE_FILTER_KEYS)[number];
type InvoiceFilters = Record<InvoiceFilterKey, string>;

function readInvoiceFilters(params: URLSearchParams): InvoiceFilters {
  const id = (key: InvoiceIdFilterKey) => {
    // `quoteId` names ONE quote (a link from that quote), so it stays single.
    if (key === 'quoteId') {
      const raw = params.get(key) ?? '';
      return ID_PATTERN.test(raw) ? raw : '';
    }
    return readIdList(params.get(key));
  };
  /* Exactly `FLAG_ON`; anything else is no filter at all. See that constant. */
  const flag = (key: InvoiceFilterKey) => (params.get(key) === FLAG_ON ? FLAG_ON : '');
  const date = (key: InvoiceFilterKey) => {
    const raw = params.get(key) ?? '';
    return DATE_PATTERN.test(raw) && isRealDate(raw) ? raw : '';
  };
  const amount = (key: InvoiceFilterKey) => {
    const raw = params.get(key) ?? '';
    return AMOUNT_PATTERN.test(raw) ? raw : '';
  };
  const currencyRaw = params.get('currency') ?? '';
  return {
    companyId: id('companyId'),
    contactId: id('contactId'),
    statusId: id('statusId'),
    lineProjectId: id('lineProjectId'),
    accountLeadId: id('accountLeadId'),
    quoteId: id('quoteId'),
    currency: CURRENCY_PATTERN.test(currencyRaw) ? currencyRaw : '',
    billDateAfter: date('billDateAfter'),
    billDateBefore: date('billDateBefore'),
    /*
     * Through the same `date` reader as the bill bounds, so `2026-02-30` — which
     * the pattern accepts and `isRealDate` does not — is dropped here rather than
     * collected as a 422 from the list request. No `new Date(...)` anywhere on
     * this path: a `YYYY-MM-DD` parsed as a Date is midnight UTC and renders as
     * the previous day west of Greenwich.
     */
    dueDateAfter: date('dueDateAfter'),
    dueDateBefore: date('dueDateBefore'),
    amountMin: amount('amountMin'),
    amountMax: amount('amountMax'),
    unsettled: flag('unsettled'),
    overdue: flag('overdue'),
    overdue90Plus: flag('overdue90Plus'),
    dueIn30Days: flag('dueIn30Days'),
  };
}

/**
 * See `quoteFilterParams` for why the amount bounds stay strings.
 *
 * ⚠️ *SUPERSEDED, and it was this function the measurement was taken on: "…and
 * why the four range keys are a compile error rather than a runtime 422 if the
 * contract spells them differently."* **They are not a compile error.** A
 * misspelled bound here compiles clean; read the note at `quoteFilterParams` for
 * the mechanism and for what to check instead.
 *
 * ✅ The six bound keys below were verified against the SERVER, 2026-09-13, not
 * against a green build: `invoices.schema.ts` declares `billDateBefore`,
 * `billDateAfter`, `dueDateBefore`, `dueDateAfter`, `amountMin` and `amountMax`;
 * `invoices.service.ts` forwards each into the repository rather than parsing and
 * dropping it; and `invoices.repository.ts` turns the due pair into
 * `i.due_date >= :dueDateAfter` / `i.due_date <= :dueDateBefore` — which is also
 * where both bounds being INCLUSIVE, and being on `due_date` rather than
 * `bill_date`, stop being a claim and become a SQL clause.
 */
function invoiceFilterParams(filters: InvoiceFilters): InvoiceListParams {
  return {
    /* Id lists: see `quoteFilterParams`. */
    ...(filters.companyId ? { companyId: idListParam(filters.companyId) } : {}),
    ...(filters.contactId ? { contactId: idListParam(filters.contactId) } : {}),
    ...(filters.statusId ? { statusId: idListParam(filters.statusId) } : {}),
    ...(filters.lineProjectId ? { lineProjectId: idListParam(filters.lineProjectId) } : {}),
    ...accountLeadParam(filters.accountLeadId),
    ...(filters.quoteId ? { quoteId: Number(filters.quoteId) } : {}),
    ...(filters.currency ? { currency: filters.currency } : {}),
    ...(filters.billDateAfter ? { billDateAfter: filters.billDateAfter } : {}),
    ...(filters.billDateBefore ? { billDateBefore: filters.billDateBefore } : {}),
    /*
     * ⚠️ **THE DUE-DATE PAIR, AND IT IS NOT THE BILL-DATE PAIR ABOVE.** Four keys
     * over two columns, spelled one word apart, and every one of the four is a
     * clean `200` on this endpoint — so a substitution in either direction cannot
     * fail loudly. `d_invoices` carries both columns; `bill_date` is when the
     * invoice was raised and `due_date` is when it must be paid.
     *
     * ⛔ **THE INVOICES KANBAN TAKES THIS PAIR AND NOT THE BILL ONE**, its schema
     * is `.strict()` and per board, and a board 422 takes out the whole board
     * rather than one filter. Nothing on this page builds board params — there is
     * no kanban here — but that is why these keys must never be copied into one.
     */
    ...(filters.dueDateAfter ? { dueDateAfter: filters.dueDateAfter } : {}),
    ...(filters.dueDateBefore ? { dueDateBefore: filters.dueDateBefore } : {}),
    ...(filters.amountMin ? { amountMin: filters.amountMin } : {}),
    ...(filters.amountMax ? { amountMax: filters.amountMax } : {}),
    /* The literal `true` or nothing — see `quoteFilterParams`. */
    ...(filters.unsettled ? { unsettled: true as const } : {}),
    ...(filters.overdue ? { overdue: true as const } : {}),
    /*
     * ⚠️ **THE 90-DAY AND 30-DAY WINDOWS ARE THE SERVER'S AND ARE NOT RE-DERIVED
     * HERE.** No date arithmetic happens on this page for either: which clock,
     * and which boundary day, are the API's answers, and a browser-side
     * `dueDate < today - 90` would be a second definition disagreeing with the
     * card's own figure on one day in ninety — silently, and only for readers in
     * some time zones. Same rule as `quote-utils.ts` computing expiry from local
     * calendar parts *because there is no server field for it*: where there is
     * one, it wins.
     */
    ...(filters.overdue90Plus ? { overdue90Plus: true as const } : {}),
    ...(filters.dueIn30Days ? { dueIn30Days: true as const } : {}),
  };
}

/**
 * See `quoteSheetCriteria` for every rule this follows — the omit-on-empty one,
 * the strings-stay-strings one, and the reason a name match is not an id match.
 *
 * ⚠️ **THE TWO LISTS ARE NOT INTERCHANGEABLE AND THE SHARED TYPE HIDES IT.**
 * `billDateAfter` on a quotes body and `stageId` on this one are both **422s
 * naming the key**, and both compile. The only guard is that each function names
 * its own section's keys and nothing else.
 */
function invoiceSheetCriteria(filters: InvoiceFilters): AccountsSheetFilterCriteria {
  return {
    /* Id lists: see `quoteSheetCriteria`. */
    ...(filters.companyId ? { companyId: idListParam(filters.companyId) } : {}),
    ...(filters.contactId ? { contactId: idListParam(filters.contactId) } : {}),
    ...(filters.statusId ? { statusId: idListParam(filters.statusId) } : {}),
    ...(filters.lineProjectId ? { lineProjectId: idListParam(filters.lineProjectId) } : {}),
    ...(filters.accountLeadId ? { accountLeadId: idListParam(filters.accountLeadId) } : {}),
    ...(filters.quoteId ? { quoteId: Number(filters.quoteId) } : {}),
    ...(filters.currency ? { currency: filters.currency } : {}),
    ...(filters.billDateAfter ? { billDateAfter: filters.billDateAfter } : {}),
    ...(filters.billDateBefore ? { billDateBefore: filters.billDateBefore } : {}),
    /*
     * ⛔ **`dueDateAfter` / `dueDateBefore` ARE DELIBERATELY ABSENT, AND THIS IS
     * THE ONE LIST FILTER THE SHEET CANNOT EXPRESS.** The filter-view body is
     * `.strict()` and its invoices arm declares the BILL bounds and not these —
     * measured in the backend's `accounts-dashboard-filter-view.schema.ts`, which
     * says so at the schema: the four booleans below (`unsettled`, `overdue`,
     * `overdue90Plus`, `dueIn30Days`) are how this contract answers a due-date
     * question, and an unasked-for pair would be a key no client sends.
     *
     * So sending either here is a **422 on the whole apply**, not a stripped key:
     * the reader would lose every other criterion too. Same ruling, same reason,
     * as `?search=` having no box on this bank — see `QuoteSheetFilters` — and
     * `InvoiceSheetFilters` therefore renders no due-date control either. **The
     * absence is the contract; do not "finish" it by adding one.**
     */
    ...(filters.amountMin ? { amountMin: filters.amountMin } : {}),
    ...(filters.amountMax ? { amountMax: filters.amountMax } : {}),
    /* Hidden derived columns, exactly as on quotes — see `quoteSheetCriteria`. */
    ...(filters.unsettled ? { unsettled: true } : {}),
    ...(filters.overdue ? { overdue: true } : {}),
    ...(filters.overdue90Plus ? { overdue90Plus: true } : {}),
    ...(filters.dueIn30Days ? { dueIn30Days: true } : {}),
  };
}

const INVOICE_SORT_KEYS: readonly InvoiceListSort[] = [
  'subject',
  COMPANY_SORT_KEY,
  'billDate',
  'createdAt',
  'updatedAt',
  'id',
  /*
   * 2026-10-09: every member an `INVOICE_COLUMNS` header now sends. ⚠️ Until
   * this list grew, `dueDate` and the Grand total header carried a `sortKey`
   * missing from it, so clicking them silently sent `billDate`.
   */
  'status',
  'contact',
  'quote',
  'lineProjects',
  'accountLead',
  'dueDate',
  'currency',
  'subTotal',
  'discountTotal',
  'taxTotal',
  'amount',
  'xeroInvoiceNumber',
];

/*
 * Company ASC, matching the quotes section — see `DEFAULT_QUOTE_SORT` for why
 * this is no longer the business date descending, and say so out loud when
 * handing the change over. The two sections default alike deliberately: one
 * page opening on two different orderings is a difference somebody has to
 * explain every time they read it.
 */
const DEFAULT_INVOICE_SORT: InvoiceListSort = COMPANY_SORT_KEY;
const DEFAULT_INVOICE_ORDER: ListOrder = 'asc';

/* The unrecognised-key fallback, not the default. See `asQuoteSort`. */
function asInvoiceSort(key: string | undefined): InvoiceListSort {
  return INVOICE_SORT_KEYS.includes(key as InvoiceListSort)
    ? (key as InvoiceListSort)
    : 'billDate';
}

/*
 * ⚠️ There is no CRM invoice number to lead with. `d_invoices` carries only
 * `xeroInvoiceNumber` — the FINANCE SYSTEM's reference — where a quote has its
 * own `quoteNumber`. So the identifying column here is the subject, and the
 * Xero reference sits at the far end with the other reference data.
 */
const INVOICE_COLUMNS: ColumnSpec<Invoice>[] = [
  { key: 'subject', label: 'Invoice', get: (row) => row.subject, sortKey: 'subject' satisfies InvoiceListSort },
  { key: 'status', label: 'Status', get: (row) => row.status?.name ?? null, sortKey: 'status' satisfies InvoiceListSort },
  {
    key: 'company',
    label: 'Company',
    get: (row) => (row.company ? documentCustomerName(row.company) : null),
    // Gates the company bands, exactly as on quotes — see `COMPANY_SORT_KEY`.
    sortKey: COMPANY_SORT_KEY,
  },
  { key: 'contact', label: 'Contact', get: (row) => row.contact?.name ?? null, sortKey: 'contact' satisfies InvoiceListSort },
  {
    key: 'quote',
    label: 'From quote',
    // The presence of the OBJECT is what says there is a quote — `quoteNumber`
    // is itself nullable, so it is never the test.
    get: (row) => (row.quote ? (row.quote.quoteNumber ?? `#${row.quote.id}`) : null),
    sortKey: 'quote' satisfies InvoiceListSort,
  },
  {
    key: 'project',
    /* The quotes column's twin, and every rule on it applies here unchanged —
       read that one. Measured 2026-09-15: `d_invoices.project_id` is NULL on all
       809 invoices, where 356 of them name a project on their lines. */
    label: PROJECTS_ON_DOCUMENT_LABEL,
    get: (row) => lineProjectsValue(row.lineProjects),
    matches: (row, selected) => lineProjectsMatch(row.lineProjects, selected),
    // Orders by the COUNT of distinct line projects — see the quotes twin.
    sortKey: 'lineProjects' satisfies InvoiceListSort,
  },
  {
    key: 'accountLead',
    label: 'Account lead',
    get: (row) => row.accountLead?.name ?? null,
    sortKey: 'accountLead' satisfies InvoiceListSort,
  },
  {
    key: 'billDate',
    label: 'Bill date',
    get: (row) => row.billDate,
    format: formatDateOnly,
    sortKey: 'billDate' satisfies InvoiceListSort,
  },
  {
    key: 'dueDate',
    label: 'Due date',
    get: (row) => row.dueDate,
    format: formatDateOnly,
    sortKey: 'dueDate' satisfies InvoiceListSort,
  },
  { key: 'currency', label: 'Currency', get: (row) => row.currency, sortKey: 'currency' satisfies InvoiceListSort },
  {
    key: 'subTotal',
    label: 'Sub total',
    get: (row) => row.subTotal,
    numeric: true,
    sortKey: 'subTotal' satisfies InvoiceListSort,
  },
  {
    key: 'discountTotal',
    label: 'Discount',
    get: (row) => row.discountTotal,
    numeric: true,
    sortKey: 'discountTotal' satisfies InvoiceListSort,
  },
  {
    key: 'taxTotal',
    label: 'Tax',
    get: (row) => row.taxTotal,
    numeric: true,
    sortKey: 'taxTotal' satisfies InvoiceListSort,
  },
  {
    key: 'grandTotal',
    label: 'Grand total',
    get: (row) => row.grandTotal,
    numeric: true,
    // ⛔ `amount`, not `grandTotal` (a 422) — see the quotes twin.
    sortKey: 'amount' satisfies InvoiceListSort,
  },
  {
    key: 'xeroInvoiceNumber',
    label: 'Xero ref',
    get: (row) => row.xeroInvoiceNumber,
    sortKey: 'xeroInvoiceNumber' satisfies InvoiceListSort,
  },
];

const INVOICE_COLUMN_KEYS: readonly string[] = INVOICE_COLUMNS.map((column) => column.key);

/**
 * See `ColumnWidths`. **The sum is 2428px and `minWidth` below must say so.**
 *
 * 288+160+224+176+152+232+168+128+120+116+128+120+112+152+152 = 2428
 *
 * ⚠️ *SUPERSEDED 2026-09-15: 2388, with `project` at 192.* Same move as the
 * quotes table above and for the same reason — read that one.
 */
const INVOICE_WIDTHS: ColumnWidths = {
  subject: 'w-[288px]',
  status: 'w-[160px]',
  company: 'w-[224px]',
  contact: 'w-[176px]',
  quote: 'w-[152px]',
  project: 'w-[232px]',
  accountLead: 'w-[168px]',
  billDate: 'w-[128px]',
  dueDate: 'w-[120px]',
  currency: 'w-[116px]',
  subTotal: 'w-[128px]',
  discountTotal: 'w-[120px]',
  taxTotal: 'w-[112px]',
  grandTotal: 'w-[152px]',
  xeroInvoiceNumber: 'w-[152px]',
};

/** Its own scope — see `QUOTE_LAYOUT_SCOPE`. */
const INVOICE_LAYOUT_SCOPE = 'accounts.invoices';

/**
 * No actions lane on this table either, so no lock, no pin and nothing to
 * explain — see `QUOTE_LOCKED_KEYS`.
 */
const INVOICE_LOCKED_KEYS: readonly string[] = [];

const INVOICE_LABELS = new Map(
  INVOICE_COLUMNS.map((column) => [column.key, column.label] as const),
);

function invoiceColumnLabel(key: string): string {
  return INVOICE_LABELS.get(key) ?? key;
}

/* Every `max-w` mirrors `INVOICE_WIDTHS` — see the note above `QuoteRowCells`. */
function invoiceCells(
  row: Invoice,
  overdue: OverdueLookup,
): Readonly<Record<string, SectionCell>> {
  return {
    subject: {
      className: 'max-w-[288px]',
      content: (
        <TextLink to={`/invoices/${row.id}`} title={row.subject} className="block truncate text-sm">
          {row.subject}
        </TextLink>
      ),
    },
    status: { className: 'max-w-[160px]', content: <StatusBadge status={row.status} /> },
    company: {
      className: 'max-w-[224px] min-w-0',
      content: (
        <CompanyCell
          to={row.company ? `/companies/${row.company.id}` : null}
          name={row.company ? documentCustomerName(row.company) : undefined}
          overdue={overdueFor(overdue, row.company)}
        />
      ),
    },
    contact: {
      className: 'max-w-[176px]',
      content: (
        <RefCell to={row.contact ? `/contacts/${row.contact.id}` : null} name={row.contact?.name} />
      ),
    },
    quote: {
      className: 'max-w-[152px]',
      content: (
        <RefCell
          to={row.quote ? `/quotes/${row.quote.id}` : null}
          name={row.quote ? (row.quote.quoteNumber ?? `#${row.quote.id}`) : null}
        />
      ),
    },
    project: {
      className: 'max-w-[232px]',
      content: <DocumentProjectsCell projects={row.lineProjects} />,
    },
    accountLead: {
      className: 'max-w-[168px]',
      content: <TextCell value={row.accountLead?.name ?? null} />,
    },
    billDate: { content: <DateCell value={row.billDate} /> },
    dueDate: { content: <DateCell value={row.dueDate} /> },
    currency: {
      className: 'whitespace-nowrap text-xs tabular',
      content: row.currency ?? <NotRecorded />,
    },
    subTotal: { numeric: true, content: <InvoiceMoney value={row.subTotal} /> },
    discountTotal: { numeric: true, content: <InvoiceMoney value={row.discountTotal} /> },
    taxTotal: { numeric: true, content: <InvoiceMoney value={row.taxTotal} /> },
    grandTotal: {
      numeric: true,
      content: <InvoiceMoney value={row.grandTotal} currency={row.currency} />,
    },
    xeroInvoiceNumber: {
      className: 'max-w-[152px]',
      /* A dash here means "no number recorded", never "not in Xero". */
      content: (
        <span
          title={row.xeroInvoiceNumber?.trim() || undefined}
          className="block truncate text-xs text-fg-secondary tabular"
        >
          {row.xeroInvoiceNumber?.trim() || '—'}
        </span>
      ),
    },
  };
}

function InvoiceRowCells({
  row,
  overdue,
  visibleKeys,
  frozenSideOf,
  frozenOffsets,
}: {
  row: Invoice;
  overdue: OverdueLookup;
  visibleKeys: readonly string[];
  frozenSideOf: (key: string) => FrozenSide | undefined;
  frozenOffsets: Record<string, number>;
}) {
  return (
    <TR interactive>
      <SectionCells
        cells={invoiceCells(row, overdue)}
        visibleKeys={visibleKeys}
        frozenSideOf={frozenSideOf}
        frozenOffsets={frozenOffsets}
      />
    </TR>
  );
}

function InvoiceRowCard({ row, overdue }: { row: Invoice; overdue: OverdueLookup }) {
  return (
    <li className="border-b border-border px-4 py-3 last:border-b-0">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Link
            to={`/invoices/${row.id}`}
            className="block truncate text-sm font-medium text-fg underline-offset-2 transition-colors duration-150 ease-out hover:underline motion-reduce:transition-none"
          >
            {row.subject}
          </Link>
          <div className="mt-0.5 flex flex-wrap items-baseline gap-x-2 text-xs text-fg-muted">
            <span className="tabular">{row.xeroInvoiceNumber?.trim() || `#${row.id}`}</span>
            {row.billDate && <span className="tabular">{formatDateOnly(row.billDate)}</span>}
          </div>
        </div>
        <span className="shrink-0 text-sm">
          <InvoiceMoney value={row.grandTotal} currency={row.currency} />
        </span>
      </div>

      <div className="mt-2">
        <StatusBadge status={row.status} />
      </div>

      <dl className="mt-2.5 grid gap-1.5 text-xs">
        {/* `min-w-0` on the grid row — see `QuoteRowCard` for why only this one. */}
        <div className="flex min-w-0 items-baseline gap-2">
          <dt className="w-28 shrink-0 text-fg-subtle">Company</dt>
          <dd className="m-0 min-w-0 flex-1">
            {/* Rendered at every width — see `QuoteRowCard`. */}
            <CompanyCell
              to={row.company ? `/companies/${row.company.id}` : null}
              name={row.company ? documentCustomerName(row.company) : undefined}
              overdue={overdueFor(overdue, row.company)}
            />
          </dd>
        </div>
        {/* The quotes card's twin, added the same day — see the note there. */}
        <div className="flex items-baseline gap-2">
          <dt className="w-28 shrink-0 text-fg-subtle">{PROJECTS_ON_DOCUMENT_LABEL}</dt>
          <dd className="m-0 min-w-0 flex-1">
            <DocumentProjectsCell projects={row.lineProjects} />
          </dd>
        </div>
        <div className="flex items-baseline gap-2">
          <dt className="w-28 shrink-0 text-fg-subtle">Account lead</dt>
          <dd className="m-0 min-w-0 flex-1">
            <TextCell value={row.accountLead?.name ?? null} />
          </dd>
        </div>
        <div className="flex items-baseline gap-2">
          <dt className="w-28 shrink-0 text-fg-subtle">Due date</dt>
          <dd className="m-0 min-w-0 flex-1">
            <DateCell value={row.dueDate} />
          </dd>
        </div>
      </dl>
    </li>
  );
}

function InvoicesSection({
  overdue,
  summaryBar,
}: {
  overdue: OverdueLookup;
  /** The page-built stat-card filter bar, passed straight through. */
  summaryBar: React.ReactNode;
}) {
  const [searchParams, setSearchParams] = useSearchParams();

  const applySavedFilters = useSavedFilterApply<InvoiceFilterKey>();
  useSavedFilters({
    scope: 'accounts-dashboard-invoices',
    keys: INVOICE_FILTER_KEYS,
    store: preferencesStore,
    apply: applySavedFilters,
  });

  const [result, setResult] = useState<Paginated<Invoice> | null>(null);
  const [search, setSearch] = useRememberedState('invoices-search', '');
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showMoreFilters, setShowMoreFilters] = useRememberedState('invoices-more-filters', false);

  const searchParamsKey = searchParams.toString();
  const filters = useMemo(
    () => readInvoiceFilters(new URLSearchParams(searchParamsKey)),
    [searchParamsKey],
  );
  const activeFilterCount = INVOICE_FILTER_KEYS.filter((key) => filters[key] !== '').length;

  const rows = useMemo(() => result?.data ?? [], [result]);
  const total = result?.meta.total ?? 0;
  const totalPages = Math.max(result?.meta.totalPages ?? 1, 1);
  const firstRow = total === 0 ? 0 : (page - 1) * PER_PAGE + 1;
  const lastRow = total === 0 ? 0 : firstRow + rows.length - 1;
  const initialLoad = loading && !result;

  /* The quotes section's twin — see the note there for why this is derived. */
  const columns = useMemo<ColumnSpec<Invoice>[]>(
    () =>
      INVOICE_COLUMNS.map((column) =>
        column.key === 'project' ? { ...column, options: lineProjectOptions(rows) } : column,
      ),
    [rows],
  );

  const table = useTableFilters(rows, columns);

  /* Its own layout, on its own scope — see the quotes section for why. */
  const layout = useColumnLayout(INVOICE_LAYOUT_SCOPE, INVOICE_COLUMN_KEYS, INVOICE_LOCKED_KEYS);
  const headRowRef = useRef<HTMLTableRowElement | null>(null);
  const renderedKeys = layout.visibleKeys;
  const frozenOffsets = useFrozenOffsets(headRowRef, renderedKeys, layout.frozenSideOf);
  const minWidth = tableMinWidth(INVOICE_WIDTHS, renderedKeys);
  /* Two branches — "nobody has chosen" and "somebody has". See the quotes twin. */
  const sort = table.sort ? asInvoiceSort(table.sort.key) : DEFAULT_INVOICE_SORT;
  const order: ListOrder = table.sort?.direction ?? DEFAULT_INVOICE_ORDER;
  const visibleRows = table.visibleRows;
  const hiddenByColumns = rows.length - visibleRows.length;

  /*
   * Gated exactly as the quotes section is: on the sort ABOUT TO BE SENT, not on
   * the feature existing and not on the raw header key. See `COMPANY_SORT_KEY`.
   */
  const banded = sort === COMPANY_SORT_KEY;
  const groups = useMemo(
    () => (banded ? groupRows(visibleRows, (row) => companyGroupOf(row.company, 'document')) : []),
    [banded, visibleRows],
  );

  const sortToken = `${sort}:${order}`;
  const [lastSortToken, setLastSortToken] = useState(sortToken);
  if (lastSortToken !== sortToken) {
    setLastSortToken(sortToken);
    setPage(1);
  }

  const load = useCallback(
    (signal?: AbortSignal) => {
      setLoading(true);
      return api
        .listInvoices(
          {
            page,
            perPage: PER_PAGE,
            sort,
            order,
            ...(search ? { search } : {}),
            ...invoiceFilterParams(filters),
          },
          signal,
        )
        .then((data) => {
          setResult(data);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setError(caught instanceof Error ? caught.message : 'Failed to load invoices');
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [page, search, sort, order, filters],
  );

  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => void load(controller.signal), 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [load]);

  const companies = useCompanyOptions();
  const projects = useProjectOptions();
  const statuses = useInvoiceStatusOptions();
  const currencies = useCurrencyOptions();
  const staff = useUserOptions(true, true);

  const rowLabels = useMemo(() => {
    const maps: Record<InvoiceIdFilterKey, Map<string, string>> = {
      companyId: new Map(),
      contactId: new Map(),
      statusId: new Map(),
      lineProjectId: new Map(),
      accountLeadId: new Map(),
      quoteId: new Map(),
    };
    for (const row of result?.data ?? []) {
      if (row.company) maps.companyId.set(String(row.company.id), row.company.name);
      if (row.contact) maps.contactId.set(String(row.contact.id), row.contact.name);
      if (row.status) maps.statusId.set(String(row.status.id), row.status.name);
      /* Every project on the row — see the quotes map for why. */
      for (const project of row.lineProjects) {
        maps.lineProjectId.set(String(project.id), project.name);
      }
      if (row.accountLead) {
        maps.accountLeadId.set(String(row.accountLead.id), row.accountLead.name);
      }
      if (row.quote) {
        maps.quoteId.set(String(row.quote.id), row.quote.quoteNumber ?? `#${row.quote.id}`);
      }
    }
    return maps;
  }, [result]);

  const statusOptions = useMemo<ComboboxOption[]>(
    () =>
      statuses.statuses.map((status) => ({
        value: String(status.id),
        label: status.isActive ? status.name : `${status.name} (retired)`,
      })),
    [statuses.statuses],
  );
  const projectOptions = useMemo<ComboboxOption[]>(
    () => projects.projects.map((project) => ({ value: String(project.id), label: project.name })),
    [projects.projects],
  );
  const currencyPickerOptions = useMemo<ComboboxOption[]>(
    () => currencyOptions(currencies.currencies, filters.currency || null),
    [currencies.currencies, filters.currency],
  );
  const leadOptions = useMemo<ComboboxOption[]>(
    () =>
      staff.byGroup.account_lead.users.map((user) => ({
        value: String(user.id),
        label: user.name.trim() || user.email,
      })),
    [staff.byGroup.account_lead.users],
  );

  const setFilter = useCallback(
    (key: InvoiceFilterKey, value: string) => {
      const next = new URLSearchParams(searchParamsKey);
      if (value) next.set(key, value);
      else next.delete(key);
      setSearchParams(next, { replace: true });
      setPage(1);
    },
    [searchParamsKey, setSearchParams],
  );

  const clearFilters = useCallback(() => {
    const next = new URLSearchParams(searchParamsKey);
    for (const key of INVOICE_FILTER_KEYS) next.delete(key);
    setSearchParams(next, { replace: true });
    setPage(1);
  }, [searchParamsKey, setSearchParams]);

  function clearEverything() {
    setSearch('');
    table.clearAll();
    clearFilters();
  }

  const filtered = activeFilterCount > 0;
  const countLabel = initialLoad
    ? 'Loading…'
    : `${total} ${total === 1 ? 'invoice' : 'invoices'}${search ? ` matching “${search}”` : ''}${
        filtered ? ` (${activeFilterCount} ${activeFilterCount === 1 ? 'filter' : 'filters'})` : ''
      }`;

  return (
    <>
      <SectionPanel
        title="All invoices"
        countLabel={countLabel}
        summaryBar={summaryBar}
        moreFiltersId="accounts-invoices-more-filters"
        showMoreFilters={showMoreFilters}
        onToggleMoreFilters={() => setShowMoreFilters((open) => !open)}
        activeFilterCount={activeFilterCount}
        onClearFilters={clearFilters}
        columnActiveCount={table.activeCount}
        columnKeys={INVOICE_COLUMN_KEYS}
        headerFor={table.headerFor}
        clearColumns={table.clearAll}
        hiddenByColumns={hiddenByColumns}
        rowCount={rows.length}
        error={error}
        errorTitle="Couldn’t load invoices."
        loading={loading}
        hasResult={result !== null}
        initialLoad={initialLoad}
        isEmpty={visibleRows.length === 0}
        // Summed from `INVOICE_WIDTHS` over the VISIBLE columns — see
        // `tableMinWidth`.
        minWidth={minWidth}
        columnManager={
          /* `hidden lg:flex` — see the quotes section. */
          <ColumnManager
            layout={layout}
            labelOf={invoiceColumnLabel}
            lockedKeys={INVOICE_LOCKED_KEYS}
            className="hidden lg:flex"
          />
        }
        // LIVE, not `INVOICE_COLUMNS.length`.
        columnCount={renderedKeys.length}
        showActions={false}
        page={page}
        totalPages={totalPages}
        onPage={setPage}
        firstRow={firstRow}
        lastRow={lastRow}
        total={total}
        emptyState={
          <EmptyStateSlot
            noun="invoices"
            search={search}
            filtered={filtered}
            columnsHidAll={rows.length > 0 && visibleRows.length === 0}
            onClearColumns={table.clearAll}
            onClear={clearEverything}
            hasError={Boolean(error)}
            emptyDescription="A row appears here for every invoice raised in EMERGE."
          />
        }
        tableHead={
          <SectionTableHead
            columns={columns}
            headerFor={table.headerFor}
            widths={INVOICE_WIDTHS}
            visibleKeys={renderedKeys}
            frozenSideOf={layout.frozenSideOf}
            frozenOffsets={frozenOffsets}
            headRowRef={headRowRef}
          />
        }
        tableBody={
          banded ? (
            <>
              {groups.map((group) => (
                <TBody key={group.key}>
                  <CompanyBandRow
                    label={group.label}
                    count={group.rows.length}
                    /* LIVE — see the quotes section. */
                    colSpan={renderedKeys.length}
                    overdue={overdueFor(overdue, groupCompany(group))}
                  />
                  {/*
                    ⚠️ `null`, not `overdue`. This is the section the owner
                    reported: four invoices under one company, each wearing the
                    same company-level bracket, two of them `Paid`.
                  */}
                  {group.rows.map((row) => (
                    <InvoiceRowCells
                      key={row.id}
                      row={row}
                      overdue={null}
                      visibleKeys={renderedKeys}
                      frozenSideOf={layout.frozenSideOf}
                      frozenOffsets={frozenOffsets}
                    />
                  ))}
                </TBody>
              ))}
            </>
          ) : (
            <TBody>
              {visibleRows.map((row) => (
                <InvoiceRowCells
                  key={row.id}
                  row={row}
                  overdue={overdue}
                  visibleKeys={renderedKeys}
                  frozenSideOf={layout.frozenSideOf}
                  frozenOffsets={frozenOffsets}
                />
              ))}
            </TBody>
          )
        }
        cards={
          banded ? (
            <>
              {groups.map((group) => (
                <section key={group.key}>
                  <CompanyBandHeading
                    label={group.label}
                    count={group.rows.length}
                    overdue={overdueFor(overdue, groupCompany(group))}
                  />
                  <ul className="m-0 list-none p-0">
                    {group.rows.map((row) => (
                      <InvoiceRowCard key={row.id} row={row} overdue={null} />
                    ))}
                  </ul>
                </section>
              ))}
            </>
          ) : (
            <ul className="m-0 list-none p-0">
              {visibleRows.map((row) => (
                <InvoiceRowCard key={row.id} row={row} overdue={overdue} />
              ))}
            </ul>
          )
        }
        filters={
          <>
            <Field label="Search">
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  type="search"
                  placeholder="Search…"
                  leadingIcon={<SearchIcon />}
                  aria-describedby={describedBy}
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                />
              )}
            </Field>

            <FilterField
              label="Company"
              value={filters.companyId}
              onChange={(value) => setFilter('companyId', value)}
              options={withSelected(
                companies.options,
                filters.companyId,
                rowLabels.companyId,
                'Company',
              )}
              allLabel="All companies"
              loading={companies.loading}
              failed={companies.failed}
              multi
              searchPlaceholder="Search companies…"
            />

            <FilterField
              label="Status"
              multi
              value={filters.statusId}
              onChange={(value) => setFilter('statusId', value)}
              options={withSelected(statusOptions, filters.statusId, rowLabels.statusId, 'Status')}
              allLabel="Any status"
              loading={statuses.loading}
              failed={statuses.failed}
            />
          </>
        }
        moreFilters={
          <>
            {/* Narrows to documents whose LINE ITEMS name this project. See
                `QUOTE_ID_FILTER_KEYS` for why it is not `projectId`, which
                matched nothing on any document in production. */}
            <FilterField
              label="Project"
              multi
              value={filters.lineProjectId}
              onChange={(value) => setFilter('lineProjectId', value)}
              options={withSelected(
                projectOptions,
                filters.lineProjectId,
                rowLabels.lineProjectId,
                'Project',
              )}
              allLabel="All projects"
              loading={projects.loading}
              failed={projects.failed}
              searchPlaceholder="Search projects…"
            />

            <FilterField
              label="Account lead"
              multi
              value={filters.accountLeadId}
              onChange={(value) => setFilter('accountLeadId', value)}
              options={withSelected(
                leadOptions,
                filters.accountLeadId,
                rowLabels.accountLeadId,
                'User',
              )}
              allLabel="Any account lead"
              loading={staff.loading}
              failed={staff.failed}
              searchPlaceholder="Search people…"
            />

            <FilterField
              label="Currency"
              value={filters.currency}
              onChange={(value) => setFilter('currency', value)}
              options={currencyPickerOptions}
              allLabel="Any currency"
              loading={currencies.loading}
              failed={currencies.failed}
              searchPlaceholder="Search currencies…"
            />

            {/*
             * ⚠️ **THE STAT CARDS SET THREE OF THESE FOUR KEYS, SO THE KEYS NEED
             * CONTROLS** — see the quotes section for why a counted filter with
             * no control on screen is the defect rather than the tidy version.
             * `unsettled` is the fourth and is set by the "Invoices pending
             * payment" card. **A card added to the Invoices row means a control
             * added here, in the same change.**
             *
             * ⚠️ **ALL OF THEM ARE READ OFF `is_settled` AND THE DUE DATE
             * SERVER-SIDE, NEVER OFF A STATUS NAME** — `"Unpaid"` contains
             * `"paid"`, and this codebase has been bitten three times by matching
             * one. They compose with the Status picker above rather than
             * replacing it.
             *
             * ⚠️ **"Unsettled" INCLUDES DRAFTS**, which is wider than "awaiting
             * payment"; and an invoice with **no due date cannot be overdue and
             * cannot be due in the next 30 days either**, so none of these
             * partitions the list. The labels say "unsettled" and "overdue"
             * rather than "outstanding" or "owed" for exactly that reason.
             */}
            <FilterField
              label="Unsettled"
              value={filters.unsettled}
              onChange={(value) => setFilter('unsettled', value)}
              options={FLAG_OPTIONS}
              allLabel="Any"
              loading={false}
              failed={false}
            />

            <FilterField
              label="Overdue"
              value={filters.overdue}
              onChange={(value) => setFilter('overdue', value)}
              options={FLAG_OPTIONS}
              allLabel="Any"
              loading={false}
              failed={false}
            />

            {/*
             * ⚠️ **A SUBSET OF "Overdue", NOT A SECOND OPINION ABOUT IT.**
             * Setting this one alone is the whole question; setting both is
             * redundant rather than contradictory, which is why they are two
             * pickers and not a three-way choice that would imply the wrong
             * relationship.
             */}
            <FilterField
              label="Overdue 90+ days"
              value={filters.overdue90Plus}
              onChange={(value) => setFilter('overdue90Plus', value)}
              options={FLAG_OPTIONS}
              allLabel="Any"
              loading={false}
              failed={false}
            />

            {/*
             * ⚠️ **FORWARD-LOOKING, so this and the two overdue keys are
             * DISJOINT** — an invoice already past due is not in it. Setting this
             * beside either of them can only answer nothing, which is the honest
             * result of the request rather than something to refuse here.
             */}
            <FilterField
              label="Due in 30 days"
              value={filters.dueIn30Days}
              onChange={(value) => setFilter('dueIn30Days', value)}
              options={FLAG_OPTIONS}
              allLabel="Any"
              loading={false}
              failed={false}
            />

            <TextFilterField
              label="Bill date from"
              type="date"
              value={filters.billDateAfter}
              onChange={(value) => setFilter('billDateAfter', value)}
            />
            <TextFilterField
              label="Bill date to"
              type="date"
              value={filters.billDateBefore}
              onChange={(value) => setFilter('billDateBefore', value)}
            />
            {/*
             * ⚠️ **A SECOND DATE WINDOW ON A SECOND COLUMN, NOT A RE-LABELLING OF
             * THE PAIR ABOVE IT.** The two sit together because a reader asking
             * "what did we raise in August" and one asking "what is owed to us in
             * August" are both here — and they are different questions over
             * `bill_date` and `due_date`. The labels carry the column, not the
             * business word "issue date", which `d_invoices` has no column for at
             * all.
             *
             * ⚠️ **These compose with the Overdue / Due in 30 days pickers above
             * rather than replacing them.** Those are server-derived flags against
             * the server's own clock; this is an explicit window somebody typed. No
             * date arithmetic happens on this page for either — see
             * `invoiceFilterParams`.
             *
             * ⚠️ **An invoice with NO due date is in NEITHER bound**, so a window
             * set here silently drops every undated invoice. That is the API's
             * shape and is not softened with an `OR IS NULL`; the Due date column
             * on the table is what shows a reader which rows have one.
             */}
            <TextFilterField
              label="Due date from"
              type="date"
              value={filters.dueDateAfter}
              onChange={(value) => setFilter('dueDateAfter', value)}
            />
            <TextFilterField
              label="Due date to"
              type="date"
              value={filters.dueDateBefore}
              onChange={(value) => setFilter('dueDateBefore', value)}
            />
            <div className="grid grid-cols-2 gap-3">
              <TextFilterField
                label="Total from"
                inputMode="decimal"
                value={filters.amountMin}
                onChange={(value) => setFilter('amountMin', value)}
              />
              <TextFilterField
                label="Total to"
                inputMode="decimal"
                value={filters.amountMax}
                onChange={(value) => setFilter('amountMax', value)}
              />
            </div>
          </>
        }
      />

      {/*
       * 🔴 **THE CREATE MODAL WAS MOUNTED HERE AND IS GONE, 2026-09-02.** *"The
       * SAME modal `/invoices`, `/invoices/:id`, `/quotes` and `/deals/:id`
       * mount"* — which is exactly why removing it costs nothing: those four
       * mounts are untouched and raising an invoice is unchanged everywhere else.
       *
       * It was opened by the header's "New invoice" button and by nothing else,
       * so leaving it behind the removed button would have left a modal that
       * could never open — dead in a way nothing reports.
       */}
    </>
  );
}

/* =========================================================================
 * FUTURE QUOTES AND INVOICES
 * ===================================================================== */

/*
 * The planned population: `GET /invoice-tracker?view=pending&source=standalone`
 * — rows somebody typed with no document behind them.
 *
 * ⚠️ *This block used to read "THE FILTER IS CLIENT-SIDE, AND THAT COSTS A PAGE
 * COUNT … closing that properly needs a `source` filter on `/invoice-tracker`,
 * which is a backend contract question." That filter now exists and is sent, so
 * the narrowing happens in SQL and `meta.total`, `firstRow`, `lastRow` and the
 * pager are all the server's own figures again.* The `.filter()` that used to
 * sit over `result.data` is gone deliberately — leaving it would silently hide
 * rows from an already-narrowed page and put the footer back out of step with
 * the table.
 *
 * ⚠️ **`source` IS NOT A SPELLING OF `view`.** `view: 'pending'` is TWO
 * populations — quotes awaiting an invoice, and these — so this is a real
 * narrowing rather than a restatement. The tracker's own Pending screen
 * deliberately sends no `source` and shows both.
 */
/*
 * 2026-10-09, owner's instruction: every funnelled column sorts on the SERVER.
 * The section is paginated, so a browser sort would order one page of N.
 * ⚠️ Every column `sortKey` below MUST appear here, or `asFutureSort` swaps it
 * for the default — the bug the two document sections carried until today.
 */
const FUTURE_SORT_KEYS: readonly InvoiceTrackerSort[] = [
  'kind',
  'party',
  'services',
  'project',
  'currency',
  'amount',
  'plannedFrom',
  'plannedTo',
  'comment',
  'addedBy',
];

/*
 * `party` ASC, as before: the company bands are honest only while the server
 * orders by party, so the default sort and the default banding are one
 * decision — the same rule `DEFAULT_QUOTE_SORT` records.
 */
const DEFAULT_FUTURE_SORT: InvoiceTrackerSort = 'party';
const DEFAULT_FUTURE_ORDER: ListOrder = 'asc';

function asFutureSort(key: string | undefined): InvoiceTrackerSort {
  return FUTURE_SORT_KEYS.includes(key as InvoiceTrackerSort)
    ? (key as InvoiceTrackerSort)
    : DEFAULT_FUTURE_SORT;
}

const FUTURE_COLUMNS: ColumnSpec<InvoiceTrackerRow>[] = [
  { key: 'kind', label: 'Type', get: (row) => row.kind, sortKey: 'kind' satisfies InvoiceTrackerSort },
  /*
   * ⚠️ **THE LABEL IS "Company name"; THE COLUMN KEY IS STILL `party`.** Owner's
   * rename, 2026-09-02: *"there should not be party name it should be company
   * name"*. It is a rename of the WORD ON SCREEN only. `key` identifies this
   * column to `useTableFilters` and to the mobile `FilterSheet`, and it is the
   * same string `InvoiceTrackerPage` uses — where it is ALSO the API's own
   * `InvoiceTrackerSort` member. Renaming keys to match a label is how a column
   * filter silently stops finding its own state.
   */
  {
    key: 'party',
    label: 'Company name',
    get: (row) => row.partyName,
    // Sorting on this is what puts the company bands on screen.
    sortKey: 'party' satisfies InvoiceTrackerSort,
  },
  { key: 'services', label: 'Services', get: (row) => row.services, sortKey: 'services' satisfies InvoiceTrackerSort },
  /*
   * ⚠️ **IMMEDIATELY AFTER SERVICES, AND THE POSITION IS THE POINT.** The two
   * answer adjacent questions — what is being sold, and what it is for — and
   * `FutureBillablesTrackerPage` puts it in the same place for the same reason.
   * ⛔ **The two column sets are deliberately one set** (that page's own header
   * says so in terms), so a key, heading or position that differs here is one
   * screen describing the same row differently.
   *
   * ⚠️ **`get` RETURNS THE NAME, which is exactly what the cell shows.** The
   * funnel groups on what `get` returns, so returning `row.project` or its id
   * would fill the popover with values no reader could match to a row.
   *
   * ⚠️ *SUPERSEDED 2026-10-09: "NO `sortKey`, and no column in this set carries
   * one".* Every column here sorts on the server now; see `FUTURE_SORT_KEYS`.
   *
   * ⛔ **`null` IS TWO DIFFERENT FACTS AND THIS COLUMN CANNOT TELL THEM APART**
   * — an ATTACHED row, whose project lives on the real document's lines and was
   * never on this column, or a standalone plan nobody has assigned one to. See
   * `InvoiceTrackerRow.project`. **So it renders as the ordinary not-recorded
   * dash and no copy anywhere may say "no project" flatly.**
   */
  {
    key: 'project',
    label: 'Project',
    get: (row) => row.project?.name ?? null,
    sortKey: 'project' satisfies InvoiceTrackerSort,
  },
  { key: 'currency', label: 'Currency', get: (row) => row.currency, sortKey: 'currency' satisfies InvoiceTrackerSort },
  // The raw decimal STRING, never `Number(row.amount)`.
  {
    key: 'amount',
    label: 'Amount',
    get: (row) => row.amount,
    numeric: true,
    sortKey: 'amount' satisfies InvoiceTrackerSort,
  },
  {
    key: 'plannedFrom',
    label: 'Planned from',
    get: (row) => row.plannedRaiseFrom,
    format: formatDateOnly,
    sortKey: 'plannedFrom' satisfies InvoiceTrackerSort,
  },
  {
    key: 'plannedTo',
    label: 'Planned to',
    get: (row) => row.plannedRaiseTo,
    format: formatDateOnly,
    sortKey: 'plannedTo' satisfies InvoiceTrackerSort,
  },
  { key: 'comment', label: 'Comment', get: (row) => row.comment, sortKey: 'comment' satisfies InvoiceTrackerSort },
];

const FUTURE_COLUMN_KEYS: readonly string[] = FUTURE_COLUMNS.map((column) => column.key);

/**
 * See `ColumnWidths`. **The sum is 1128px INCLUDING the 124px actions lane, and
 * that total is the whole reason these are the numbers they are.**
 *
 * 80+132+104+112+112+112+128+120+104 = 1004 data, + 124 actions = 1128
 *
 * ⚠️ *SUPERSEDED, the same sum over eight columns before Project landed
 * (2026-09-17): `80+148+136+112+112+144+128+144`.* **The TOTAL did not move and
 * must not.** Project's 112px was taken out of the other eight — comment −40,
 * services −32, party −16, plannedFrom −16, plannedTo −8 — rather than added to
 * the table, because 1128 is the number that fits the content area at 1440 and
 * a ninth column bolted on top is straight back to the lane over the comments.
 * **`services` took the largest share deliberately**: Project now carries part
 * of what a reader was reading Services for.
 *
 * 🔴 **THIS IS THE ONE TABLE ON THE PAGE THAT IS SIZED TO FIT RATHER THAN TO
 * READ WELL, AND THE REASON IS THE PINNED ACTIONS COLUMN.** Owner report,
 * 2026-09-02: *"the `Raise invoice` / `Raise quote` buttons SIT ON TOP OF the
 * Comment column"*. That is `TD pinned` — `sticky right-0` with an opaque
 * background — doing exactly what sticky does: while the table is scrolled left
 * of its far end, the lane floats over whatever column is under the scroller's
 * right edge, and Comment was last.
 *
 * ⚠️ **A STICKY LANE ALWAYS COVERS SOMETHING WHENEVER THE TABLE SCROLLS AT ALL.
 * There is no width, order or padding that avoids it** — the lane is positioned
 * against the scrollport, so at every scroll position short of the far right it
 * sits over live cells. The only two real answers are to stop pinning (and lose
 * the action off-screen, which is the bug `Table`'s own `pinned` note records
 * paying for on `/companies`) or to remove the scroll. **This removes the
 * scroll**: 1128px fits the content area at 1440 with the sidebar expanded
 * (~1136px) and at every width above it, so the lane sits in its own column and
 * overlaps nothing.
 *
 * ⚠️ **The honest residue, stated rather than hidden: below ~1128px of content
 * — a 1366-wide window with the sidebar open, say — the table scrolls again and
 * the lane covers the tail of "Planned to".** That is bounded (a date is about
 * 72px in a 128px cell), recoverable by scrolling, and it is the same behaviour
 * every other pinned table in this app has. Making it structurally impossible
 * needs an actions lane rendered OUTSIDE the scroller, which is a change to
 * `src/components/ui/Table.tsx`.
 *
 * ⚠️ **So Comment, Services and Project are narrower here than a finance reader
 * would choose.** All three are `line-clamp-2` with a `title`, which is what
 * keeps that survivable — nothing is unreachable, only abbreviated. Do NOT
 * widen one of these without taking the width from another: the total is the
 * property that matters, and a table at 1200px is straight back to a lane over
 * the comments.
 *
 * 🔴 **A COLUMN'S OWN HEADING IS ITS REAL FLOOR, AND THAT IS WHAT DECIDES WHICH
 * ONES ARE COMPRESSIBLE.** There is no `table-fixed` and no `<colgroup>` here,
 * so a `w-*` is a request the content may outvote — and `FilterableTH`'s label
 * span is `truncate` with **no `min-w-0`** (that component's own comment says
 * so), so a flex child that will not shrink below its content width. Heading
 * plus funnel plus `TH`'s `px-3` is therefore a hard minimum, and a request
 * below it does not shrink the column: it is silently overruled and the
 * shortfall is taken out of whichever neighbour CAN wrap. **So `kind` (80, for
 * "Type") and `currency` (112, for "Currency") have almost no slack despite
 * holding four characters and three, and `amount` has none at all** — its money
 * is `whitespace-nowrap`, so its min-content is the widest figure it must show.
 * The slack lives in the long-heading, wrappable columns, which is where the
 * 112px above was taken from.
 */
const FUTURE_WIDTHS: ColumnWidths = {
  kind: 'w-[80px]',
  party: 'w-[132px]',
  services: 'w-[104px]',
  project: 'w-[112px]',
  currency: 'w-[112px]',
  amount: 'w-[112px]',
  plannedFrom: 'w-[128px]',
  plannedTo: 'w-[120px]',
  comment: 'w-[104px]',
};

/** The reserved lane. See `SectionTableHead`'s `actionsWidth`. */
const FUTURE_ACTIONS_WIDTH = 'w-[124px]';

/** Its own scope — see `QUOTE_LAYOUT_SCOPE`. */
const FUTURE_LAYOUT_SCOPE = 'accounts.future';

/**
 * ⚠️ **THE ACTIONS LANE IS A LAYOUT KEY HERE AND NOWHERE ELSE ON THIS PAGE.**
 * It is the only one of the three tables that renders one, and it is listed in
 * the manager — disabled — rather than omitted, so its absence from the list
 * cannot read as the feature having lost it.
 */
const FUTURE_LAYOUT_KEYS: readonly string[] = [...FUTURE_COLUMN_KEYS, ACTIONS_COLUMN];

/**
 * ⛔ **ACTIONS IS LOCKED, AND IT IS THE ONLY ONE.** The raise "+" — ⚠️
 * *SUPERSEDED 2026-09-13: "Raise invoice" / "Raise quote"* — and the "Link to
 * document" recovery live in that one cell and have NO second home on this page
 * — the sub-`lg` card list renders its own copy, but a desktop reader who hid
 * the lane would have no way back to either. That is the `/companies` incident
 * this repo already paid for, where a view that hid the row actions was
 * reported as the feature having been DELETED.
 */
const FUTURE_LOCKED_KEYS: readonly string[] = [ACTIONS_COLUMN];

/**
 * The lane renders with `pinned`, so its edge is not the reader's to choose.
 *
 * ⚠️ **SEPARATE FROM `FUTURE_LOCKED_KEYS` EVEN THOUGH THEY HOLD THE SAME ONE
 * KEY TODAY, and they must not be collapsed into one constant.** Locked is about
 * hiding and reordering; pinned is about which edge a cell already occupies. The
 * day this table locks a second column — its identifying one, which is what
 * every other list page locks — that column must stay freezable, and a single
 * shared constant would silently take freeze-left away from exactly the column
 * the feature exists for.
 *
 * It is the same list `useFrozenOffsets` counts: the lane's 124px has to sit in
 * the offset of anything else frozen right, or that column lands underneath it.
 */
const FUTURE_PINNED_KEYS: readonly string[] = [ACTIONS_COLUMN];

/** Why the lane is locked, in the reader's terms — see `ColumnManager`. */
const FUTURE_LOCK_REASONS: Record<string, string> = {
  [ACTIONS_COLUMN]:
    'This column holds the only desktop route to raising a document, so it cannot be hidden',
};

/**
 * The widths the FLOOR is summed from, lane included.
 *
 * ⚠️ **`FUTURE_WIDTHS` ALONE WOULD UNDER-COUNT BY THE LANE'S 124px**, and this
 * is the one table whose floor is load-bearing rather than cosmetic — see
 * `FUTURE_WIDTHS`'s own header for why 1128 is the number it is.
 */
const FUTURE_FLOOR_WIDTHS: ColumnWidths = {
  ...FUTURE_WIDTHS,
  [ACTIONS_COLUMN]: FUTURE_ACTIONS_WIDTH,
};

const FUTURE_LABELS = new Map<string, string>([
  ...FUTURE_COLUMNS.map((column) => [column.key, column.label] as const),
  // Not a `ColumnSpec`, so it has no label to read — see `ACTIONS_COLUMN`.
  [ACTIONS_COLUMN, 'Actions'],
]);

function futureColumnLabel(key: string): string {
  return FUTURE_LABELS.get(key) ?? key;
}

/**
 * ⚠️ **`ColumnManager`'s own fallback is "This column cannot be hidden", which
 * is true here and explains nothing.** A bare disabled checkbox makes a locked
 * column and a broken switch identical; this says which of the two it is, and
 * why, in the terms the reader is standing in. It is a why-this-is-disabled
 * sentence, one of the four kinds the no-helper-text rule keeps.
 */
function futureLockReason(key: string): string | undefined {
  return FUTURE_LOCK_REASONS[key];
}

/**
 * Every `max-w` mirrors `FUTURE_WIDTHS` — see the note above `quoteCells`, and
 * that constant's header for why these numbers are as tight as they are.
 *
 * ⚠️ **`actionsFor` IS PASSED IN RATHER THAN IMPORTED**: it closes over the
 * section's `converting`, `raised` and `unlinked` state, which is what stops a
 * second row's button opening a second form — and this is a module-level
 * function precisely so a cell cannot reach that state any other way.
 */
function futureCells(
  row: InvoiceTrackerRow,
  overdue: OverdueLookup,
  actionsFor: (row: InvoiceTrackerRow) => React.ReactNode,
): Readonly<Record<string, SectionCell>> {
  return {
    kind: {
      content: (
        <Badge tone="neutral" variant="outline">{kindLabel(row.kind)}</Badge>
      ),
    },
    /*
     * ⚠️ **THE CHIP KEYS ON `row.company`, NEVER ON `row.partyName`, AND A ROW
     * WITH NO COMPANY GETS NONE.** A planned row may be typed against a
     * free-text name instead of a CRM account (`FutureDocumentDialog` labels
     * that box **Company name**), so `company` is null while `partyName` still
     * says who. There is no id in existence for those rows, and matching the
     * typed string against a company name would attribute one customer's debt
     * to another's row — production holds `Think Test Company` twice, as two
     * distinct Xero contacts. Linking the row to an account is what makes its
     * bracket appear, which is the honest behaviour.
     */
    party: {
      className: 'max-w-[132px] min-w-0',
      content: (
        <CompanyCell
          to={row.company ? `/companies/${row.company.id}` : null}
          name={row.partyName}
          overdue={overdueFor(overdue, row.company)}
        />
      ),
    },
    services: { className: 'max-w-[104px]', content: <TextCell value={row.services} /> },
    /*
     * ⚠️ **`TextCell`, LIKE ITS TWO NEIGHBOURS, SO A `null` DRAWS THE PAGE'S
     * ORDINARY NOT-RECORDED DASH** — never an empty cell, and never the word
     * "None". `row.project` is `null` for two different reasons this column
     * cannot tell apart (see `InvoiceTrackerRow.project`), so the dash — which
     * says nothing — is the only honest mark available; any label would be a
     * claim about one of the two.
     */
    project: { className: 'max-w-[112px]', content: <TextCell value={row.project?.name ?? null} /> },
    currency: {
      className: 'whitespace-nowrap text-xs tabular',
      content: row.currency ?? <NotRecorded />,
    },
    amount: {
      numeric: true,
      content: <InvoiceMoney value={row.amount} currency={row.currency} />,
    },
    plannedFrom: { content: <DateCell value={row.plannedRaiseFrom} /> },
    plannedTo: { content: <DateCell value={row.plannedRaiseTo} /> },
    comment: { className: 'max-w-[104px]', content: <TextCell value={row.comment} /> },
    /*
     * ⚠️ **`pinned` IS NOT SET HERE** — `SectionCells` applies it by key, so the
     * header and the body cannot disagree about which column sticks.
     */
    [ACTIONS_COLUMN]: {
      numeric: true,
      className: 'max-w-[124px]',
      content: (
        /*
         * ⚠️ `flex-wrap`, and it is what keeps the lane a FIXED WIDTH. The
         * ordinary row carries one button; a row whose document was raised but
         * not attached carries a second ("Link to document"). Without wrapping,
         * that second button pushes the lane wider — and every column left of it
         * narrower — the moment a recovery banner appears, so the table would
         * reflow because of an error somewhere else.
         */
        <div className="flex flex-wrap items-center justify-end gap-2">{actionsFor(row)}</div>
      ),
    },
  };
}

/**
 * ⚠️ *SUPERSEDED: `kind === 'invoice' ? 'Invoice' : 'Quote'`.* A two-arm
 * ternary over a union that gained `'unknown'` on 2026-09-11, which it labelled
 * **Quote** — including inside the Raise button, which would have read "Raise
 * quote" on a row nobody had decided about. `tsc` saw nothing: a ternary is not
 * an exhaustiveness check. See `KIND_NOUNS`, which is a `Record` so the next
 * member cannot do this again.
 *
 * ⚠️ **THE RAISE BUTTON IT NAMES IS GONE (2026-09-13) AND THE LESSON IS NOT.**
 * `ConvertPlannedAction` is a "+" menu now and the three-arm ternary that builds
 * its trigger label is the SAME hazard in the same file — it is written out over
 * `row.kind` rather than through a `Record` only because its third arm is a
 * sentence rather than a noun. **A fourth `kind` member must be taken to both.**
 */
function kindLabel(kind: InvoiceTrackerRow['kind']): string {
  return KIND_NOUNS[kind];
}

/**
 * Raises the real document a planned row describes, through the SAME form modal
 * the rest of the app uses.
 *
 * 🔴 **IT IS THE SHARED "+" MENU SINCE 2026-09-13 — owner's instruction:**
 * *"instead of raise invoice button give the plus sign (quote invoice) in
 * accounts dashboard in future billables."* ⚠️ *SUPERSEDED: one `secondary`
 * Button per raisable kind, labelled `Raise quote` / `Raise invoice`.* **The
 * buttons are GONE rather than kept beside it** — one affordance per action, so
 * nobody has to work out whether the two do the same thing.
 *
 * ⚠️ **THE MENU IS THE SAME COMPONENT `/companies` AND THE UP DASHBOARD MOUNT**,
 * and the items are handled HERE through its `onSelect` seam rather than
 * navigated: a planned row's basket cannot travel in a URL, so an item that
 * merely linked to `?companyId=N&new=1` would open an empty grid beside a row
 * that already says what is being billed. See the `href` beside each item for
 * what that fallback would honestly do.
 *
 * ⛔ **WHAT THE MENU OFFERS IS `raisableAs(row.kind)`, NOT ALWAYS BOTH.** The
 * server refuses a stated plan attached to the other kind, AFTER the document
 * has been created and pushed to Xero. Read the note at `options` below before
 * touching that list; it is the one change here that could not be undone.
 *
 * ⚠️ **THIS OPENS A PATH THAT CREATES A REAL FINANCIAL DOCUMENT AND, ON SAVE,
 * PUSHES IT TO XERO — AND XERO DOES NOT ENFORCE INVOICE-NUMBER UNIQUENESS.** A
 * second press is a second live demand for money against one customer and
 * nothing downstream refuses it. Four things hold that off, and none is
 * decoration:
 *
 *  1. **One modal for the whole section.** `converting` holds the ONE row being
 *     raised, so a second row's "+" cannot open a second form — every one in
 *     the section is disabled while any form is open.
 *  2. **The modal owns the request** and disables its own submit while in
 *     flight, the way every save in this app does.
 *  3. **The row leaves the list on success**, so there is nothing left to press.
 *  4. **`raised` remembers the key for the life of the page**, so a re-read that
 *     brings the row back cannot re-arm the control.
 *
 * ⚠️ **(4) IS NOW THE ONE THAT MATTERS MOST, AND IT IS DELIBERATELY SET BEFORE
 * THE ATTACH IS ATTEMPTED RATHER THAN AFTER IT.** The document exists the moment
 * the modal reports a save; whether `POST /invoice-tracker/{id}/raised` then
 * succeeds decides only whether the PLAN was retired. So the key is remembered
 * on save and the row is removed on attach — and a failed attach leaves the row
 * on screen with this action still disabled, because a second press would create
 * a SECOND live document and Xero does not enforce invoice-number uniqueness.
 *
 * ⚠️ *This block used to end "(3) and (4) are local, and a refresh brings the
 * planned row back … do not write copy claiming the plan was consumed." That is
 * no longer true.* The attach is server-side: the row changes population from
 * standalone to attached, leaves the pending set because that set no longer
 * admits it, and appears against the real document with its comment intact.
 * Nothing is deleted.
 */
function ConvertPlannedAction({
  row,
  blocked,
  onOpen,
}: {
  row: InvoiceTrackerRow;
  /**
   * Why this row cannot be raised right now, in the reader's terms, or `null`.
   *
   * 🔴 **A SENTENCE RATHER THAN A BOOLEAN, AND THE CHANGE IS NOT COSMETIC.**
   * *SUPERSEDED: `busy: boolean` and `disabled: boolean`.* The old pair drove a
   * spinner and a `disabled`, and the three states behind them — a form is
   * open, this row was already raised, the reader may not create — are wildly
   * different facts a person needs told apart. A button carried its own label
   * while it was disabled, so the state was at least legible; **an icon-only
   * "+" has no words of its own**, and a disabled one saying nothing is
   * indistinguishable from a broken one. The caller knows which of the three it
   * is and is the only thing that does; see `actionsFor`.
   *
   * ⚠️ **THE PERMISSION CASE IS NOT PASSED IN** — it is resolved here, per
   * document type, because it is per document type and the caller has no reason
   * to know that.
   */
  blocked: string | null;
  /**
   * WHICH DOCUMENT to raise, chosen by the item that was picked.
   *
   * ⚠️ **THE ARGUMENT IS NOT DERIVABLE FROM `row.kind` AND THAT IS WHY IT
   * EXISTS.** On a "Not known yet" plan the row says nothing, so the caller
   * cannot re-derive what was chosen — it has to be told. Reading `row.kind`
   * at the other end would silently open the quote form for both items.
   */
  onOpen: (as: 'quote' | 'invoice') => void;
}) {
  const { ability } = useAuth();

  /*
   * 🔴 **ONE ITEM ON A STATED PLAN, TWO ON A "Not known yet" ONE — AND THE
   * OWNER'S "quote invoice" IS ABOUT WHICH RECORDS THE MENU MAY OFFER, NOT
   * ABOUT OVERRIDING THIS.** It excludes **Deal**, which the companies menu
   * carries and this one must not: there is no deal in a plan's future and
   * `POST /invoice-tracker/{id}/raised` takes a `quoteId` or an `invoiceId` and
   * nothing else, so a deal raised here could never be attached to the plan.
   *
   * ⛔ **OFFERING BOTH ON A STATED PLAN WOULD BE A DOCUMENT NOBODY CAN UNMAKE.**
   * Measured in `invoice-tracker.service.ts` (`markRaised`): a row whose `kind`
   * is `'quote'` attached against an `invoiceId` is a **422**, and the check
   * runs *before* the document-exists check precisely so it cannot be worked
   * around. The raise happens FIRST — a real invoice, pushed to Xero — and the
   * attach is refused afterwards. *SUPERSEDED 2026-10-11: "for ever, because
   * `kind` is absent from the update schema and cannot be corrected."* `kind`
   * is editable now (Edit on the row, `FutureDocumentDialog`), so the row is
   * recoverable — but only by somebody noticing the refusal and editing the
   * kind, while a live demand for money sits beside an unraisable plan.
   * **`raisableAs` is still the guard; do not widen it to a literal
   * `['quote', 'invoice']`.**
   *
   * ⚠️ `'unknown'` yields BOTH, which is the whole behavioural consequence of
   * migration 161: the moment a plan is raised IS the decision, the server
   * exempts an unknown plan from that kind check, and `attachToDocument`
   * settles the row to whichever was named. **Not a default with a way to
   * change it** — picking one for them would put a real financial document
   * behind an item labelled for the other half of the time.
   *
   * ⚠️ **PERMISSION IS PER DOCUMENT TYPE**, so it is resolved per item. An
   * `advanced_user` who may raise quotes and not invoices is offered Quote
   * alone — DROPPED rather than shown disabled, which is the rule
   * `COMPANY_CREATE_TARGETS` already states for this menu. That is a change
   * from the two buttons this replaced, where the refused half rendered
   * disabled with its own sentence: a menu has no disabled item (every row is
   * Enter-selectable, so a decorative one becomes a pickable value), and the
   * whole-control refusal below is what keeps the reader told.
   */
  const options = raisableAs(row.kind);

  /*
   * The accessible name, from the PLAN'S OWN KIND and never from the filtered
   * item list.
   *
   * ⚠️ **DERIVING IT FROM `items` WOULD MAKE A CONTROL'S NAME DEPEND ON THE
   * READER'S PERMISSIONS**, so two colleagues hear two different buttons and
   * neither can be told which one to press — `RecordCreateMenu.triggerLabel`
   * forbids exactly that. `row.kind` is the same for everybody, so this is not
   * that: it says what the plan is, which is a fact on the row.
   *
   * ⚠️ **`kindLabel` IS NOT USED HERE.** It answers "Not known yet", which
   * reads as a sentence fragment inside a trigger name.
   */
  const triggerLabel =
    row.kind === 'quote'
      ? `Raise the planned quote for ${row.partyName}`
      : row.kind === 'invoice'
        ? `Raise the planned invoice for ${row.partyName}`
        : `Raise a quote or invoice for ${row.partyName}`;

  const items: RecordCreateMenuItem[] = [];
  /** The first denial we met, for the whole-control refusal below. */
  let refusal: string | null = null;

  for (const as of options) {
    const module = as === 'invoice' ? 'invoices' : 'quotes';
    if (!ability.canCreate(module)) {
      /*
       * ⚠️ **THE FIRST DENIAL WINS, AND ON A STATED PLAN THERE IS ONLY EVER
       * ONE.** Two arise only on a "Not known yet" row whose reader may create
       * neither — where both sentences say the same thing in the same words,
       * because `denialReason` answers about the ACTION and the tier rather
       * than about the module.
       */
      refusal ??=
        denialReason(ability, 'create', module) ??
        `You do not have permission to raise ${module}.`;
      continue;
    }
    items.push({
      key: as,
      /*
       * ⚠️ **BARE NOUNS, as the owner asked ("quote invoice") and as
       * `COMPANY_CREATE_TARGETS` spells its own.** The popover heads itself
       * "New for <party>", so "Quote" underneath reads as the whole sentence.
       * The deals menu says "Convert to quote" because its items CONVERT an
       * existing record; these open a create form, which is the asymmetry that
       * file records.
       */
      label: as === 'invoice' ? 'Invoice' : 'Quote',
      /*
       * ⚠️ **THE HONEST DESTINATION IF THE INTERCEPTION BELOW IS EVER REMOVED,
       * and it is deliberately NOT equivalent to it.** `?new=1` opens a plain
       * create form with the company locked where there is one — the
       * `COMPANY_CREATE_TARGETS` convention — and seeds **no lines**, because a
       * URL cannot carry a basket. It also attaches nothing: a document raised
       * that way leaves the plan standing. That is worse than this menu and
       * better than a dead item, which is exactly what `href` is for.
       *
       * ⚠️ **A PLANNED ROW MAY HAVE NO COMPANY.** `partyName` is always present
       * and `company` is not — a plan can be typed against a free-text name —
       * so the branch is on the OBJECT and never on an id read through it.
       * `?new=1` alone is a valid plain create link on both pages;
       * `?companyId=undefined&new=1` would be a filter for a company that does
       * not exist, which opens the form over an empty list.
       */
      href: row.company
        ? `/${module}?companyId=${row.company.id}&new=1`
        : `/${module}?new=1`,
      /*
       * ⚠️ **`true` MEANS HANDLED HERE, AND IT IS THE ONLY REASON THIS FEATURE
       * EXISTS AS A MENU RATHER THAN A LINK.** The section's own modals are
       * mounted below with `prefill={prefillOf(row)}`, which carries the
       * planned basket through `plannedProductLines` — the product, the
       * quantity, the unit price and the planned description of every line
       * somebody planned. Navigating instead would open an empty grid beside a
       * row that already says what is being billed.
       */
      onSelect: () => {
        onOpen(as);
        return true;
      },
    });
  }

  /*
   * ⛔ **A REFUSED OR BLOCKED ROW KEEPS A VISIBLE "+", DISABLED, AND NEVER LOSES
   * IT.** `RecordCreateMenu` renders `null` for an empty item list, which is
   * right where it is mounted (a trigger whose only outcome is an empty popover
   * is an affordance with one outcome) and wrong here twice over:
   *
   *  - **a reader who may not create cannot tell a gated control from a missing
   *    one**, and this page has no other way of telling them; and
   *  - **`UnlinkedRaiseBanner` says in terms that Raise "is already disabled on
   *    those rows"** — a vanished control falsifies the one sentence standing
   *    between somebody and a second live demand for money.
   *
   * ⚠️ **THE SENTENCE RIDES ON THE BUTTON'S `tooltip`.** It is a
   * why-this-is-disabled sentence, one of the four kinds the no-helper-text
   * rule keeps, and it is ROUTED to a styled tooltip rather than printed.
   * `Button` itself puts a disabled button's tooltip on a wrapper that
   * receives the hover, so the call site needs no span of its own; the
   * accessible name stays `triggerLabel`.
   *
   * *SUPERSEDED 2026-09-24: "THE SENTENCE IS ON A WRAPPER `<span>`, NOT ON THE
   * BUTTON … a `title` on it never renders."* True before `Button` learnt to
   * wrap a disabled control itself; a span `title` here would now show a
   * second label beside the tooltip.
   */
  const reason = items.length === 0 ? refusal : blocked;
  if (reason !== null) {
    return (
      <Button
        variant="ghost"
        size="sm"
        iconOnly
        disabled
        leadingIcon={<PlusIcon />}
        aria-label={triggerLabel}
        tooltip={reason}
      />
    );
  }

  return <RecordCreateMenu recordName={row.partyName} triggerLabel={triggerLabel} items={items} />;
}

/**
 * Corrects a planned row through the SAME dialog that created it.
 *
 * ⚠️ **NOTHING HERE CREATES A DOCUMENT, WHICH IS WHY IT IS GATED FAR MORE
 * LOOSELY THAN `ConvertPlannedAction` ABOVE IT.** That control raises a real
 * quote or invoice and pushes it to Xero; this one edits the tracker's own
 * overlay row and can produce nothing a customer ever sees. So it takes no part
 * in the `raised` bookkeeping and none of the four double-press guards — it is
 * disabled only while another of this section's dialogs is open, so there is one
 * form on screen at a time.
 *
 * ⚠️ **ONLY A STANDALONE ROW IS EDITABLE, AND THE ACTION IS ABSENT RATHER THAN
 * DISABLED ON THE OTHERS.** A row attached to a real quote or invoice reads its
 * party, currency, amount and basket THROUGH that document at query time, and
 * the server refuses every one of those keys against it — so there is nothing
 * this dialog could write. That refusal is about the SYSTEM and is identical for
 * everybody, which is the case where an affordance whose only outcome is an
 * error must not be shown at all (the same ruling as Xero's `sync` button).
 * Contrast the permission branch below, which is about THIS READER and therefore
 * must be visible and say so.
 *
 * ⚠️ **Belt and braces today**: `FutureSection` pins `source: 'standalone'` on
 * its request, so every row it holds already passes. It is tested here anyway
 * because that is where the rule belongs — `plannedProductLines` guards the same
 * thing in the same way, for the same reason.
 *
 * ⚠️ **`ability.canUpdate('invoices')`, NEVER the role and never
 * `/permissions/me` directly.** It is the gate the whole tracker writes behind —
 * `useTrackerRowWriter` on `InvoiceTrackerPage`, and this dialog's own 403
 * branch — and a second spelling of one question is how half a feature ends up
 * on the other side of a permission.
 */
function EditPlannedAction({
  row,
  disabled,
  onOpen,
}: {
  row: InvoiceTrackerRow;
  disabled: boolean;
  onOpen: () => void;
}) {
  const { ability } = useAuth();
  const canUpdate = ability.canWriteFutureBillables;
  const reason = futureBillablesDenialReason(ability);

  if (row.source !== 'standalone') return null;

  return (
    <Button
      size="sm"
      variant="secondary"
      /*
       * ⚠️ **DISABLED WITH A SENTENCE, NEVER HIDDEN.** A reader who may not write
       * cannot tell a gated button from a broken one, and this page has no other
       * way of telling them. Same treatment as `ConvertPlannedAction`.
       */
      disabled={!canUpdate || disabled}
      {...(canUpdate
        ? {}
        : { title: reason ?? 'You do not have permission to change planned documents.' })}
      onClick={onOpen}
    >
      Edit
    </Button>
  );
}

/**
 * A document that WAS raised whose plan could not be attached to it.
 *
 * ⚠️ **THE NAME IS `UnlinkedRaise` AND NOT `FailedRaise`, DELIBERATELY.** The
 * raise succeeded — there is a real quote or invoice, and somebody can go and
 * look at it. What failed is the bookkeeping that retires the plan. A type named
 * for the failure invites copy named for the failure, and copy named for the
 * failure is how a user comes to press the button a second time.
 */
interface UnlinkedRaise {
  /** The tracker row's `key`, which is what everything on this page is keyed by. */
  key: string;
  /** Kept whole so a retry can re-send exactly the request that failed. */
  row: InvoiceTrackerRow;
  /** The document that EXISTS. This is the value a retry must not re-derive. */
  documentId: number;
  /** The server's own sentence, where it gave one. */
  reason: string | null;
}

/**
 * The banner shown when a document was raised and its plan was not retired.
 *
 * ⚠️ **THE WORDING IS THE CONTROL, NOT A PREFERENCE, AND THE ORDER OF THE
 * CLAUSES IS THE WHOLE OF IT.** It leads with the document having been created,
 * because the natural response to "raising failed" is to raise it again — and a
 * second raise is a second live quote or invoice, which nothing downstream
 * refuses: Xero does not enforce invoice-number uniqueness, so two real demands
 * for money can exist against one customer with no error anywhere.
 *
 * The same ruling this API already carries twice, reached from two directions:
 * the create-cooldown message on `POST /xero/create/{entity}/{id}`, and
 * `xeroPush: 'failed'` on a save. Anybody shortening this later is removing a
 * safeguard rather than tightening prose.
 *
 * It names the document by id so somebody can go and confirm it, says plainly
 * that the row below is the plan and not a second attempt, and offers the ONE
 * safe action — retrying the link.
 */
function UnlinkedRaiseBanner({ entries }: { entries: readonly UnlinkedRaise[] }) {
  if (entries.length === 0) return null;

  return (
    <Notice
      tone="warning"
      role="alert"
      className="mb-3"
      title={
        entries.length === 1
          ? 'The document was created. Its planned row could not be retired.'
          : `${entries.length} documents were created. Their planned rows could not be retired.`
      }
    >
      <ul className="m-0 list-none p-0">
        {entries.map((entry) => (
          <li key={entry.key} className="mt-0.5">
            {`${kindLabel(entry.row.kind)} #${entry.documentId} for ${
              entry.row.partyName || 'this company'
            } exists.`}
            {entry.reason ? ` ${entry.reason}` : ''}
          </li>
        ))}
      </ul>
      <p className="m-0 mt-1.5">
        {/*
          The instruction, last and unmissable. "Raise" is already disabled on
          those rows; this says WHY, so the disabled control reads as deliberate
          rather than as the page being broken.
        */}
        {entries.length === 1 ? 'That row is' : 'Those rows are'} still listed below.{' '}
        <strong>Do not raise {entries.length === 1 ? 'it' : 'them'} again</strong> — use{' '}
        <em>Link to document</em> to try the link once more.
      </p>
    </Notice>
  );
}

/**
 * The Future Billables section's own flag filters.
 *
 * ⚠️ **A GROUP OF ONE, AND IT IS STILL A GROUP.** This section had no flag at
 * all until the "Due in the next 30 days" stat card arrived on 2026-09-03, and
 * the temptation is to read the single key inline the way `companyId` is read
 * below. It is a named group because `SECTION_FLAG_FILTER_KEYS` is what
 * `activeStatCard` and `selectCard` iterate — a section missing from that record
 * is a `tsc` error, where a section whose flags are spelled inline is a card that
 * silently checks itself against ANOTHER section's keys. That is precisely the
 * bug the binary `section === 'quotes' ? … : …` lookup used to have here, and it
 * was invisible only because the Future card carried no filter to check.
 */
const FUTURE_FLAG_FILTER_KEYS = ['plannedIn30Days'] as const;

/**
 * Every key this section owns, flags included.
 *
 * ⚠️ `companyId` is SHARED with the other two sections by name and means the
 * same thing in all three — the same `d_companies` id — which is what makes it
 * safe for a stat card to carry one across a section switch. See
 * `ALL_FLAG_FILTER_KEYS`.
 */
const FUTURE_FILTER_KEYS = ['companyId', ...FUTURE_FLAG_FILTER_KEYS] as const;

type FutureFilterKey = (typeof FUTURE_FILTER_KEYS)[number];

/*
 * ⚠️ **`onAdd` IS GONE (2026-09-02).** The create control moved to the page
 * header so it is reachable from all three sections; this component no longer
 * owns it. `reloadToken` stays and is how the page tells this section a planned
 * row was created — see `AccountsDashboardPage`.
 */
function FutureSection({
  reloadToken,
  overdue,
  summaryBar,
  onPreview,
}: {
  reloadToken: number;
  /** The page's one PDF viewer — "Create and preview" on the raise flow. */
  onPreview: (target: QuotePdfPreviewTarget) => void;
  overdue: OverdueLookup;
  /**
   * The page-built stat-card filter bar, passed straight through.
   *
   * ⚠️ **This section's *Planned* card carries no filter, so the bar is `null`
   * for it** — *Planned* IS this section unfiltered. Only *Due in the next 30
   * days* draws one here. See the page's `summaryBar`.
   */
  summaryBar: React.ReactNode;
}) {
  const [searchParams, setSearchParams] = useSearchParams();

  const applySavedFilters = useSavedFilterApply<FutureFilterKey>();
  useSavedFilters({
    scope: 'accounts-dashboard-future',
    /*
     * ⚠️ The whole key list, flags included — the same treatment the quotes and
     * invoices sections give theirs, where `awaitingSignature` and `unsettled`
     * have always been saved alongside the ids. A card-set flag is still a
     * filter the reader chose to be looking at.
     */
    keys: FUTURE_FILTER_KEYS,
    store: preferencesStore,
    apply: applySavedFilters,
  });

  const [result, setResult] = useState<Paginated<InvoiceTrackerRow> | null>(null);
  const [search, setSearch] = useRememberedState('future-billables-search', '');
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showMoreFilters, setShowMoreFilters] = useRememberedState(
    'future-billables-more-filters',
    false,
  );
  /** The one row whose form is open. `null` when none is. */
  /*
   * 🔴 **THE ROW *AND* WHICH DOCUMENT IT IS BEING RAISED AS.** *SUPERSEDED:
   * `useState<InvoiceTrackerRow | null>`, with the two form mounts choosing
   * between themselves on `row.kind`.*
   *
   * That worked while every plan stated a kind. Migration 161 lets one say
   * "Not known yet", and for those rows the row itself cannot say which form
   * to open — the BUTTON knows and nothing else does. So the choice is stored
   * beside the row at the moment it is made.
   *
   * ⚠️ **`as` IS NOT A COPY OF `row.kind` AND MUST NOT BE "SIMPLIFIED" BACK TO
   * ONE.** On a stated plan they agree, which is exactly what makes the
   * reduction look safe; on an unknown plan `row.kind` is `'unknown'` and `as`
   * is the answer. Collapsing them reopens the quote form for both buttons.
   */
  const [converting, setConverting] = useState<{
    row: InvoiceTrackerRow;
    as: 'quote' | 'invoice';
  } | null>(null);
  /**
   * The one row being EDITED, held for the whole section exactly as `converting`
   * is. `null` when none is.
   *
   * ⚠️ **ONE STATE AND ONE DIALOG MOUNT, NOT ONE PER ROW.** A dialog per row
   * would put N forms in the tree — each with its own company read, its own
   * product read and its own copy of a basket — and would make "only one form
   * open at a time" a thing every button had to remember rather than a property
   * of the section. It is the same reason the two form modals below are single
   * mounts chosen by `row.kind`.
   *
   * ⚠️ **The row is read by the dialog ONCE, on opening**, so this must go
   * through `null` before it can hold a different row. Nothing here swaps it
   * while open: the only writer that sets a row is the Edit button, which is
   * disabled while this is non-null.
   */
  const [editing, setEditing] = useState<InvoiceTrackerRow | null>(null);
  /** Keys already raised in this page's life. See `ConvertPlannedAction`. */
  const [raised, setRaised] = useState<readonly string[]>([]);
  /**
   * Documents that WERE created and whose plan could not be attached to them.
   *
   * ⚠️ **THIS IS NOT A LIST OF FAILED RAISES. Every entry is a real quote or
   * invoice that exists.** It is kept so the row can say so, so the retry can
   * re-send the id of the document that was actually created rather than
   * guessing at it, and above all so the raise action stays disabled — the one
   * thing that must not happen here is a second document.
   */
  const [unlinked, setUnlinked] = useState<readonly UnlinkedRaise[]>([]);
  /** The key whose attach is in flight, so its retry can show a spinner. */
  const [linking, setLinking] = useState<string | null>(null);

  const searchParamsKey = searchParams.toString();
  const companyId = useMemo(() => {
    return readIdList(new URLSearchParams(searchParamsKey).get('companyId'));
  }, [searchParamsKey]);
  /*
   * Exactly `FLAG_ON` and nothing else, the same reading the other two sections
   * give their flags: a URL carrying `?plannedIn30Days=maybe` narrows nothing
   * rather than 422ing the section and reading as an outage.
   */
  const plannedIn30Days = useMemo(
    () => (new URLSearchParams(searchParamsKey).get('plannedIn30Days') === FLAG_ON ? FLAG_ON : ''),
    [searchParamsKey],
  );
  const activeFilterCount = (companyId ? 1 : 0) + (plannedIn30Days ? 1 : 0);

  /*
   * The page as the SERVER narrowed it. There is no `.filter()` here any more:
   * `source: 'standalone'` is sent on the request, so re-filtering would only be
   * able to remove rows the server had already decided belong on this page, and
   * would put the footer back out of step with the table.
   *
   * Keyed on `result` so the identity is stable between renders — `?? []` is a
   * fresh array every time, which would make every memo below it do nothing.
   */
  const rows = useMemo(() => result?.data ?? [], [result]);
  const total = result?.meta.total ?? 0;
  // `meta.totalPages` is 0 (not 1) for an empty set, which would disable both
  // pager buttons and render "Page 1 of 0".
  const totalPages = Math.max(result?.meta.totalPages ?? 1, 1);
  const firstRow = total === 0 ? 0 : (page - 1) * PER_PAGE + 1;
  const lastRow = total === 0 ? 0 : firstRow + rows.length - 1;
  const initialLoad = loading && !result;

  const table = useTableFilters(rows, FUTURE_COLUMNS);

  /*
   * Its own layout, on its own scope — and the only one of the three carrying
   * the actions lane as a key.
   */
  const layout = useColumnLayout(
    FUTURE_LAYOUT_SCOPE,
    FUTURE_LAYOUT_KEYS,
    FUTURE_LOCKED_KEYS,
    FUTURE_PINNED_KEYS,
  );
  const headRowRef = useRef<HTMLTableRowElement | null>(null);
  /*
   * ⚠️ **`renderKeys` AND NOT `layout.visibleKeys`** — the lane is clamped last
   * however the stored order came back, because `pinned` is `sticky right-0` and
   * a lane that is not last floats over a live column. See `renderKeys`.
   */
  const renderedKeys = useMemo(() => renderKeys(layout.visibleKeys, true), [layout.visibleKeys]);
  /*
   * ⚠️ **THE MEASURER SEES THE LANE AS RIGHT-FROZEN AND THE CELLS DO NOT.** Its
   * 124px has to count into the offset of anything else frozen right, or a
   * second right-frozen column lands underneath it.
   *
   * ⚠️ *SUPERSEDED: a local `measureSideOf` wrapper built exactly that second
   * view.* **The hook takes `pinnedKeys` and builds it itself**, so the two
   * views differ by one argument here rather than by a helper three thousand
   * lines away.
   */
  const frozenOffsets = useFrozenOffsets(
    headRowRef,
    renderedKeys,
    layout.frozenSideOf,
    FUTURE_PINNED_KEYS,
  );
  const minWidth = tableMinWidth(FUTURE_FLOOR_WIDTHS, renderedKeys);
  /*
   * 🔴 **THE SKELETON'S DATA-CELL COUNT, WITH THE LANE TAKEN BACK OFF.**
   * `SectionPanel` adds the pinned actions cell itself behind `showActions`, so
   * passing the full count would render one cell too many and the skeleton would
   * be a column wider than the header it sits under.
   */
  const skeletonColumnCount = renderedKeys.length - 1;
  const visibleRows = table.visibleRows;
  const hiddenByColumns = rows.length - visibleRows.length;

  /*
   * ✅ **BANDED UNCONDITIONALLY, WHERE QUOTES AND INVOICES BAND ONLY WHILE THEIR
   * SORT SAYS SO.** *SUPERSEDED: "BANDED, UNLIKE QUOTES AND INVOICES … No
   * `COMPANY_SORT_AVAILABLE` gate here because there is nothing waiting on the
   * server."* **Those two band now** — see `COMPANY_SORT_KEY` — but they gate it
   * on the active sort, because their headers can reorder the list. This one
   * needs no gate for a different reason: it pins `sort: 'party'` in the request
   * above and **no column in `FUTURE_COLUMNS` carries a `sortKey`**, so nothing
   * on screen can move the rows out of company order. Give one a `sortKey` and
   * this comment becomes false and the bands become a lie — gate them the way
   * the other two sections do, in the same change.
   *
   * ⚠️ `row.partyName` is passed as the fallback and that is not tidiness: a
   * planned row may be typed against a free-text **Company name** rather than a
   * CRM account, so `company` is null on it while `partyName` still says who.
   * Without the fallback every one of those collapses into a single
   * "No company recorded" band holding unrelated customers. See
   * `companyGroupOf`.
   */
  /*
   * 🔴 *SUPERSEDED 2026-10-09: the paragraph above — "BANDED UNCONDITIONALLY …
   * no column in `FUTURE_COLUMNS` carries a `sortKey`".* Every column sorts on
   * the server now, so the bands are gated exactly as the other two sections
   * gate theirs: ONLY while the server is ordering by `party`. Read from
   * `sort`, the value about to be SENT, never from `table.sort?.key`. Any other
   * sort renders ONE unbanded group, so the JSX below keeps a single shape and
   * the band row/heading is simply withheld.
   */
  const sort = table.sort ? asFutureSort(table.sort.key) : DEFAULT_FUTURE_SORT;
  const order: ListOrder = table.sort?.direction ?? DEFAULT_FUTURE_ORDER;
  const banded = sort === DEFAULT_FUTURE_SORT;
  const groups = useMemo(
    () =>
      banded
        ? groupRows(visibleRows, (row) => companyGroupOf(row.company, 'company', row.partyName))
        : [{ key: 'all', label: '', companyId: null, rows: visibleRows }],
    [banded, visibleRows],
  );

  /* A new sort re-orders the whole list — see the quotes section's twin. */
  const sortToken = `${sort}:${order}`;
  const [lastSortToken, setLastSortToken] = useState(sortToken);
  if (lastSortToken !== sortToken) {
    setLastSortToken(sortToken);
    setPage(1);
  }

  const load = useCallback(
    (signal?: AbortSignal) => {
      setLoading(true);
      return api
        .listInvoiceTracker(
          {
            view: 'pending',
            // ⚠️ The narrowing that makes `meta.total` and the pager describe
            // the rows on screen. Sent unconditionally: this section IS the
            // typed rows, and dropping it would silently mix in every quote
            // awaiting an invoice.
            source: 'standalone',
            // Completed rows (ticked on the tracker, 2026-10-27) leave this list.
            plan: 'pending',
            /*
             * 🔴 **THE SORT IS WHAT MAKES THE COMPANY BANDS HONEST, AND IT IS
             * NOT COSMETIC.** The banner is drawn over CONSECUTIVE RUNS of the
             * rows the server sent, so it only describes a company when the
             * rows arrive grouped. Drop this and the section still renders —
             * with a banner above nearly every row, reading as *"every customer
             * has one planned document"*. The band and this key are one
             * feature; do not remove either alone.
             *
             * `party` is a real `InvoiceTrackerSort` member (measured from
             * `src/lib/types.ts`, 2026-09-02: `billDate | party | amount |
             * documentNumber | status`). The query schema is `.strict()`, so an
             * unrecognised value here would be a 422 emptying the section
             * rather than a silent fallback.
             *
             * ⚠️ It is sent as a FIXED value rather than through
             * `useTableFilters`: no column in `FUTURE_COLUMNS` carries a
             * `sortKey`, so there is no header control to fight with. Giving one
             * a `sortKey` later means gating the bands on it, exactly as
             * `ClientDeliverablesPage` gates its project headers on
             * `GROUPED_SORT` — otherwise a reader sorting by Amount gets
             * banners announcing sections that end one row later.
             *
             * ✅ *SUPERSEDED 2026-10-09: "sent as a FIXED value".* Every column
             * sorts now, so this is the chosen header (default `party` ASC),
             * and the bands are gated on it — see `banded` above.
             */
            sort,
            order,
            page,
            perPage: PER_PAGE,
            ...(search ? { search } : {}),
            ...(companyId ? { companyId: idListParam(companyId) } : {}),
            /*
             * ⚠️ The literal `true` or the key is absent — never
             * `Boolean(plannedIn30Days)`. `InvoiceTrackerListParams` types it as
             * the literal so `tsc` refuses the widened version rather than
             * letting a `?plannedIn30Days=false` reach a `.strict()` schema,
             * which is a 422 emptying this section rather than a no-op.
             */
            ...(plannedIn30Days ? { plannedIn30Days: true as const } : {}),
          },
          signal,
        )
        .then((data) => {
          setResult(data);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setError(
            caught instanceof Error ? caught.message : 'Failed to load planned documents',
          );
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [page, search, companyId, plannedIn30Days, sort, order],
  );

  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => void load(controller.signal), 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [load, reloadToken]);

  const companies = useCompanyOptions();

  const rowLabels = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of result?.data ?? []) {
      if (row.company) map.set(String(row.company.id), row.company.name);
    }
    return map;
  }, [result]);

  /*
   * ⚠️ **ONE WRITER FOR BOTH KEYS.** This used to be a `setCompanyId` closed
   * over the one key it wrote; the flag made a second one the obvious next step,
   * and two of them called in a row would silently lose the first — each starts
   * from `searchParamsKey`, the RENDER'S snapshot of the query string, which the
   * first write has not updated by the time the second reads it. That is the
   * same trap `selectCard` on the page carries a paragraph about.
   */
  const setFilter = useCallback(
    (key: FutureFilterKey, value: string) => {
      const next = new URLSearchParams(searchParamsKey);
      if (value) next.set(key, value);
      else next.delete(key);
      setSearchParams(next, { replace: true });
      setPage(1);
    },
    [searchParamsKey, setSearchParams],
  );

  /* Every key this section owns, in ONE write — see `setFilter`. */
  const clearFilters = useCallback(() => {
    const next = new URLSearchParams(searchParamsKey);
    for (const key of FUTURE_FILTER_KEYS) next.delete(key);
    setSearchParams(next, { replace: true });
    setPage(1);
  }, [searchParamsKey, setSearchParams]);

  function clearEverything() {
    setSearch('');
    table.clearAll();
    clearFilters();
  }

  /*
   * ⚠️ **REMEMBERING THE KEY AND DROPPING THE ROW ARE TWO SEPARATE ACTS NOW, AND
   * THE SPLIT IS THE SAFETY PROPERTY.** They used to be one, because there was
   * nothing between "the modal saved" and "the row is gone".
   *
   * There is now: the attach. The document exists the moment the modal reports a
   * save, so the key is remembered THEN — disabling the raise action for the
   * life of the page whatever happens next. The row is removed only once the
   * plan has actually been attached to it.
   *
   * Collapsing these back into one would mean a failed attach re-arming a button
   * whose only outcome is a second live quote or invoice.
   */
  const rememberRaised = useCallback((key: string) => {
    setRaised((current) => (current.includes(key) ? current : [...current, key]));
  }, []);

  const dropRow = useCallback((key: string) => {
    setResult((current) => {
      if (!current) return current;
      const data = current.data.filter((row) => row.key !== key);
      if (data.length === current.data.length) return current;
      // `meta.total` is decremented with the row so the footer and the table do
      // not disagree until the next read. Not recomputing `totalPages`: a page
      // boundary that moved is corrected by the reload below, and guessing at it
      // here would be a second implementation of the server's paging.
      return { ...current, data, meta: { ...current.meta, total: Math.max(current.meta.total - 1, 0) } };
    });
  }, []);

  /**
   * Attach a planned row to the document it just produced.
   *
   * ⚠️ **BY THE TIME THIS RUNS THE DOCUMENT EXISTS.** Nothing below can unmake
   * it, so nothing below may be worded as a failed raise — see `unlinked` and
   * the banner it feeds.
   *
   * The id sent is chosen by `row.kind`, because the server refuses the mismatch
   * with a 422: a planned quote may only be attached to a quote. Getting that
   * backwards would file the plan as the other kind and answer 200.
   */
  const attachRaised = useCallback(
    async (row: InvoiceTrackerRow, documentId: number) => {
      // Never null on a standalone row -- the row IS the record there. Guarded
      // rather than asserted, because a `!` here would be a runtime throw inside
      // a success path if the population ever widened.
      if (row.id === null) return;
      const rowId = row.id;

      setLinking(row.key);
      try {
        await api.raiseInvoiceTrackerRow(
          rowId,
          row.kind === 'invoice' ? { invoiceId: documentId } : { quoteId: documentId },
        );
        setUnlinked((current) => current.filter((entry) => entry.key !== row.key));
        dropRow(row.key);
      } catch (caught: unknown) {
        setUnlinked((current) => [
          ...current.filter((entry) => entry.key !== row.key),
          {
            key: row.key,
            row,
            documentId,
            reason: caught instanceof Error ? caught.message : null,
          },
        ]);
      } finally {
        setLinking(null);
      }
    },
    [dropRow],
  );

  /** What the modal hands back, turned into the one act this section owes. */
  const onDocumentSaved = useCallback(
    (row: InvoiceTrackerRow, documentId: number) => {
      // FIRST, and before the attach is attempted: the document exists, so the
      // raise action must not be offered again whatever happens next.
      rememberRaised(row.key);
      void attachRaised(row, documentId);
    },
    [attachRaised, rememberRaised],
  );

  const filtered = activeFilterCount > 0;
  // The SERVER's total now, not "on this page" -- `source: 'standalone'` means
  // `meta.total` counts exactly the rows this section shows.
  const countLabel = initialLoad
    ? 'Loading…'
    : `${total} planned ${total === 1 ? 'document' : 'documents'}${
        search ? ` matching “${search}”` : ''
      }${filtered ? ` (${activeFilterCount} ${activeFilterCount === 1 ? 'filter' : 'filters'})` : ''}`;

  /*
   * The prefill both modals take. Every key is a SEED and none is a lock —
   * whoever raises the document may change any of them before saving, which is
   * the point: a plan recorded months ago is exactly the thing most likely to
   * need correcting at the moment of billing.
   *
   * ⚠️ There is no amount key on either prefill, and there cannot be: the money
   * on a quote or invoice comes from its LINE ITEMS, and the header totals are
   * recomputed by the next line write. The planned amount travels in the
   * subject so a person can see what was intended while they build the lines.
   */
  /*
   * ✅ **THE PLANNED LINES ARE PREFILLED. Owner's request, 2026-09-02.**
   *
   * *SUPERSEDED, the ~40-line TODO that stood here: "🔴 THE PLANNED LINES ARE
   * NOT PREFILLED YET, AND THIS IS THE CALL SITE THAT WILL DO IT … BLOCKED on a
   * file this change does not own. `QuoteFormPrefill` … and its invoice twin
   * declare `subject`, `company`, `contact`, `deal` and `currency` and **no line
   * key at all** — measured 2026-09-02 … ⚠️ NOTHING IS BUILT AGAINST THE SHAPE
   * ABOVE ON PURPOSE. A mapper with no consumer is this repo's most-recorded bug
   * shape … When the prefill type gains its line key, the mapping is
   * `row.products` → that key, here."*
   *
   * **Both prefill types now carry `lines`, and this is that mapping.** The
   * TODO's own diagnosis was right on every point and is why the change landed
   * where it did rather than here: a line item cannot exist before its document,
   * so the key seeds the modal's DRAFT GRID and is written by the two-phase
   * create that was already there — document first, then each line, then a
   * report naming any that were refused.
   *
   * ⚠️ **`convertFrom` is still NOT the seam**, exactly as the TODO said. It
   * takes a whole `Deal` and posts to the deal-convert endpoint.
   *
   * ⚠️ **PREFILLING IS NOT RAISING, and that is still true.** The person sees
   * the modal, edits the rows and presses Save. Nothing on this path creates a
   * document without that.
   */
  const prefillOf = (row: InvoiceTrackerRow) => ({
    subject: row.services.trim() || row.partyName,
    ...(row.company ? { company: { id: row.company.id, name: row.company.name } } : {}),
    ...(row.currency ? { currency: row.currency } : {}),
    /*
     * ⚠️ **THE MAPPER, NOT A MAPPING WRITTEN HERE.** `plannedProductLines`
     * answers the one question a page must not answer for itself: a planned
     * quantity is a decimal STRING and a line quantity is an integer of at
     * least 1, so `"2.50"` is plannable and unsendable. It reports that as
     * `lineQuantity: null`, and the grid opens that row with an EMPTY quantity
     * box and blocks Save until a person supplies one. It is never rounded to
     * 1 — a figure nobody chose, on a document a customer is billed against.
     *
     * ⚠️ **`[]` for anything but a standalone row**, which is the mapper's own
     * rule: a row attached to a real quote or invoice has that document's own
     * lines, and they are the truth about what was sold. Every row this runs on
     * is by definition not raised yet, so the guard is belt and braces.
     *
     * ⚠️ **A FRESH ARRAY EVERY RENDER, and that is safe.** The grid reads it in
     * a `useState` initialiser and never again, and both remount keys
     * fingerprint the CONTENT rather than the identity — so this neither
     * re-seeds a row somebody deleted nor remounts the form on every keystroke.
     */
    lines: plannedProductLines(row),
  });

  /**
   * The per-row actions, defined ONCE and rendered by both the table and the
   * card list.
   *
   * ⚠️ **ONE DEFINITION BECAUSE THE TWO SURFACES MUST NOT DRIFT.** This codebase
   * has had a working feature reported as *deleted* because it was rendered only
   * on desktop, and the retry below is the only way back from a raised document
   * whose plan was not retired — a control that exists at one width only is one
   * a mobile user cannot reach at all.
   */
  const actionsFor = (row: InvoiceTrackerRow) => {
    const pending = unlinked.find((entry) => entry.key === row.key);
    return (
      <>
        <ConvertPlannedAction
          row={row}
          /*
           * ⚠️ **THE THREE BLOCKING STATES, TOLD APART, BECAUSE AN ICON-ONLY
           * CONTROL CANNOT SAY WHICH IT IS IN.** *SUPERSEDED: one `disabled`
           * boolean OR-ing all three, plus a `busy` spinner.* A disabled button
           * still carried its own label; a disabled "+" carries no words at
           * all, so the reason has to travel with it — see
           * `ConvertPlannedAction`'s `blocked`.
           *
           * ⚠️ **`raised` FIRST, because it is the one that matters.** It is set
           * when the modal saves, BEFORE the attach is attempted, so it holds
           * whether or not the attach then succeeded — the document exists
           * either way and a second raise would create a second live demand for
           * money against one customer, which nothing downstream refuses. The
           * other two are transient; this one is for the life of the page.
           *
           * ⚠️ **IT DOES NOT NAME *Link to document*.** That control appears
           * only where the attach actually failed, and a refusal pointing at a
           * button that is not on the row is worse than one that does not — see
           * `UnlinkedRaiseBanner`, which names it in the case where it is there.
           */
          blocked={
            raised.includes(row.key)
              ? 'A document has already been raised from this planned row, so raising it again ' +
                'would create a second one.'
              : converting !== null || editing !== null
                ? 'A form is already open. Close it before raising this document.'
                : null
          }
          onOpen={(as) => setConverting({ row, as })}
        />
        {/*
         * ⚠️ **SECOND, SO THE ORDINARY ROW'S CONTROLS NEVER MOVE.** Raise and
         * Edit are on every row; "Link to document" below appears only after a
         * document was created and its plan could not be retired, so it appends
         * rather than displacing either. A control that changes position when an
         * error appears elsewhere is one somebody has to re-find at the worst
         * moment.
         *
         * ⚠️ **NOT disabled on `raised`, unlike Raise.** That flag exists to stop
         * a SECOND live financial document, and editing an overlay row creates
         * nothing — so disabling here would be a claim ("there is nothing to
         * change") that is not true. The lane wraps, so the extra button costs
         * width from nothing: see the `flex-wrap` note on the actions cell.
         */}
        <EditPlannedAction
          row={row}
          disabled={converting !== null || editing !== null}
          onOpen={() => setEditing(row)}
        />
        {pending && (
          <Button
            size="sm"
            variant="secondary"
            loading={linking === row.key}
            disabled={linking !== null}
            title={
              `${kindLabel(row.kind)} #${pending.documentId} already exists. This links this ` +
              `planned row to it. It creates nothing.`
            }
            onClick={() => void attachRaised(row, pending.documentId)}
          >
            Link to document
          </Button>
        )}
      </>
    );
  };

  return (
    <>
      {/*
        Above the panel rather than inside it: `SectionPanel`'s own `error` slot
        belongs to the load failure, and this is not one — the read worked, and
        so did the raise. See `UnlinkedRaiseBanner` for why the wording is the
        control here.
      */}
      <UnlinkedRaiseBanner entries={unlinked} />

      <SectionPanel
        // Owner's rename, 2026-09-02. The heading carries the section's name so
        // it and the switcher chip cannot drift apart; `SECTION_LABEL` is the
        // one place the words live.
        title={SECTION_LABEL.future}
        countLabel={countLabel}
        summaryBar={summaryBar}
        moreFiltersId="accounts-future-more-filters"
        showMoreFilters={showMoreFilters}
        onToggleMoreFilters={() => setShowMoreFilters((open) => !open)}
        activeFilterCount={activeFilterCount}
        onClearFilters={clearFilters}
        columnActiveCount={table.activeCount}
        columnKeys={FUTURE_COLUMN_KEYS}
        headerFor={table.headerFor}
        clearColumns={table.clearAll}
        hiddenByColumns={hiddenByColumns}
        rowCount={rows.length}
        error={error}
        errorTitle="Couldn’t load planned documents."
        loading={loading}
        hasResult={result !== null}
        initialLoad={initialLoad}
        isEmpty={visibleRows.length === 0}
        /*
         * ⚠️ Summed from `FUTURE_FLOOR_WIDTHS` — `FUTURE_WIDTHS` PLUS the
         * lane — over the VISIBLE columns, and it is load-bearing rather than
         * cosmetic: this is the number that keeps the pinned actions lane off
         * the Comment column. Read that constant's header before changing it.
         *
         * ⚠️ **HIDING A COLUMN HERE ONLY EVER HELPS.** At full visibility this
         * is still exactly 1128 — the number that fits the content area at 1440
         * — and every hidden column takes it further below that, so the lane
         * overlaps less rather than more.
         */
        minWidth={minWidth}
        columnManager={
          /* `hidden lg:flex` — see the quotes section. */
          <ColumnManager
            layout={layout}
            labelOf={futureColumnLabel}
            lockedKeys={FUTURE_LOCKED_KEYS}
            lockReasonOf={futureLockReason}
            /*
             * ⚠️ **The lane's two freeze controls are ABSENT, not disabled.**
             * It is already `sticky right-0`, so there is no state a pressed or
             * greyed button would be describing — and offering "freeze left"
             * would put two conflicting position rules on one cell.
             */
            pinnedKeys={FUTURE_PINNED_KEYS}
            className="hidden lg:flex"
          />
        }
        columnCount={skeletonColumnCount}
        showActions
        page={page}
        totalPages={totalPages}
        onPage={setPage}
        /*
         * The server's own figures, like every other section here.
         *
         * ⚠️ *This used to read "the footer reports the rows on THIS page and
         * never a total — `meta.total` counts the whole pending population".
         * That was true while the narrowing was client-side; `source` is sent
         * now, so `meta.total` counts exactly these rows.*
         */
        firstRow={firstRow}
        lastRow={lastRow}
        total={total}
        emptyState={
          <EmptyStateSlot
            noun="planned documents"
            search={search}
            filtered={filtered}
            columnsHidAll={rows.length > 0 && visibleRows.length === 0}
            onClearColumns={table.clearAll}
            onClear={clearEverything}
            hasError={Boolean(error)}
            /*
             * ⚠️ Names the control by the label it actually wears, and that
             * label moved on 2026-09-02 — the button is **Add future billables**
             * now and it sits above this panel rather than in the filter row.
             * An empty state pointing at a control nobody can find is worse than
             * one that says nothing, and this is the only sentence on the page
             * telling a first-time reader where to start.
             */
            emptyDescription="Use “Add future billables” above to record work that has been agreed and not yet raised."
          />
        }
        tableHead={
          <SectionTableHead
            columns={FUTURE_COLUMNS}
            headerFor={table.headerFor}
            widths={FUTURE_WIDTHS}
            visibleKeys={renderedKeys}
            frozenSideOf={layout.frozenSideOf}
            frozenOffsets={frozenOffsets}
            headRowRef={headRowRef}
            actionsWidth={FUTURE_ACTIONS_WIDTH}
          />
        }
        tableBody={
          <>
            {/*
              ⚠️ **`colSpan` IS THE LIVE CELL COUNT, AND IT INCLUDES THE ACTIONS
              COLUMN.** *SUPERSEDED: `FUTURE_COLUMNS.length + 1`, "and the `+ 1`
              is the actions column".* The `+ 1` has not gone away — it is inside
              `renderedKeys`, which carries `ACTIONS_COLUMN` as a locked,
              never-hidden member, so this is still one more than the data
              columns and now shrinks as a reader hides them. Written as a
              constant the rule either stops short of the right edge or runs past
              it — which throws nothing, renders, and just looks like a broken
              table.
            */}
            {groups.map((group) => (
              <TBody key={group.key}>
                {banded && (
                  <CompanyBandRow
                    label={group.label}
                    count={group.rows.length}
                    colSpan={renderedKeys.length}
                    overdue={overdueFor(overdue, groupCompany(group))}
                  />
                )}
            {/*
              ⚠️ Keyed on `row.key`, NEVER on `row.id` — `id` is `null` on every
              row nobody has typed an overlay against, so an id-keyed list
              collapses half the table onto one React key.

              The row block below is still at the indentation it had before it
              was nested in `groups.map`, and that is deliberate rather than a
              mistake. ⚠️ *SUPERSEDED, the reason: "re-indenting would rewrite
              ~90 untouched lines and bury a three-line change in a diff nobody
              can review".* **Those ~90 lines are gone** — the cells moved into
              `futureCells`, so what sits here is nine. The reason that survives
              is the other half: this repo has mixed line endings, so a reflow
              touching lines nothing else in the change touches is the operation
              most likely to convert them silently.
            */}
            {group.rows.map((row) => (
              <TR key={row.key} interactive>
                <SectionCells
                  cells={futureCells(row, overdue, actionsFor)}
                  visibleKeys={renderedKeys}
                  frozenSideOf={layout.frozenSideOf}
                  frozenOffsets={frozenOffsets}
                />
              </TR>
            ))}
              </TBody>
            ))}
          </>
        }
        cards={
          <>
            {groups.map((group) => (
              <section key={group.key}>
                {banded && (
                  <CompanyBandHeading
                    label={group.label}
                    count={group.rows.length}
                    overdue={overdueFor(overdue, groupCompany(group))}
                  />
                )}
                <ul className="m-0 list-none p-0">
            {group.rows.map((row) => (
              <li key={row.key} className="border-b border-border px-4 py-3 last:border-b-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="m-0 truncate text-sm font-medium text-fg">{row.partyName}</p>
                    {/* Rendered at every width — see `QuoteRowCard`. Keyed on the
                        company, never the typed name — see the table cell above. */}
                    <OverdueChip
                      summary={overdueFor(overdue, row.company)}
                      className="mt-1"
                    />
                    <div className="mt-0.5 flex flex-wrap items-baseline gap-x-2 text-xs text-fg-muted">
                      <Badge tone="neutral" variant="outline">
                        {kindLabel(row.kind)}
                      </Badge>
                      {row.plannedRaiseFrom && (
                        <span className="tabular">{formatDateOnly(row.plannedRaiseFrom)}</span>
                      )}
                    </div>
                  </div>
                  <span className="shrink-0 text-sm">
                    <InvoiceMoney value={row.amount} currency={row.currency} />
                  </span>
                </div>

                <dl className="mt-2.5 grid gap-1.5 text-xs">
                  <div className="flex items-baseline gap-2">
                    <dt className="w-28 shrink-0 text-fg-subtle">Services</dt>
                    <dd className="m-0 min-w-0 flex-1">
                      <TextCell value={row.services} />
                    </dd>
                  </div>
                  {/*
                    ⚠️ **BETWEEN Services AND Comment, THE SAME ORDER THE TABLE
                    USES.** A column that exists at one width only is the shape
                    this codebase has already had reported as a DELETED feature
                    — see the actions note below, which is the same rule about
                    the same card.
                  */}
                  <div className="flex items-baseline gap-2">
                    <dt className="w-28 shrink-0 text-fg-subtle">Project</dt>
                    <dd className="m-0 min-w-0 flex-1">
                      <TextCell value={row.project?.name ?? null} />
                    </dd>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <dt className="w-28 shrink-0 text-fg-subtle">Comment</dt>
                    <dd className="m-0 min-w-0 flex-1">
                      <TextCell value={row.comment} />
                    </dd>
                  </div>
                </dl>

                {/*
                  ⚠️ **THE SAME ACTION AS THE TABLE, AT EVERY WIDTH.** This
                  codebase has already had a working feature reported as DELETED
                  because it was only rendered on desktop.
                */}
                <div className="mt-3 flex flex-wrap items-center gap-2">{actionsFor(row)}</div>
              </li>
            ))}
                </ul>
              </section>
            ))}
          </>
        }
        filters={
          <>
            <Field label="Search">
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  type="search"
                  placeholder="Search…"
                  leadingIcon={<SearchIcon />}
                  aria-describedby={describedBy}
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                />
              )}
            </Field>

            <FilterField
              label="Company name"
              value={companyId}
              onChange={(value) => setFilter('companyId', value)}
              options={withSelected(companies.options, companyId, rowLabels, 'Company')}
              allLabel="All companies"
              loading={companies.loading}
              failed={companies.failed}
              multi
              searchPlaceholder="Search companies…"
            />

            {/*
             * ⚠️ **THE "Due in the next 30 days" STAT CARD SETS THIS KEY, SO THE
             * KEY NEEDS A CONTROL** — see the quotes section for why a filter
             * counted in the header and reachable from nowhere on screen is the
             * defect rather than the tidy version.
             *
             * ⚠️ **THE WINDOW IS THE SERVER'S.** Nothing here compares
             * `plannedRaiseFrom` to a date built in the browser: the API decides
             * what "the next 30 days" means, and a second definition living in
             * this file would disagree with the card's own figure on the boundary
             * day, in some time zones only, with nothing on screen saying so.
             */}
            <FilterField
              label="Due in the next 30 days"
              value={plannedIn30Days}
              onChange={(value) => setFilter('plannedIn30Days', value)}
              options={FLAG_OPTIONS}
              allLabel="Any"
              loading={false}
              failed={false}
            />

            {/*
             * ⚠️ **THE "Add planned document" BUTTON USED TO SIT HERE AND WAS
             * MOVED TO THE PAGE HEADER ON 2026-09-02**, renamed **Add future
             * billables**. Owner's instruction: *"this add planned document
             * remove it from there keep it right below invoice quotes future
             * billables tab so we dont have to go to that page for this
             * specifically"*. It is reachable from all three sections now, so
             * raising a planned document no longer means switching section
             * first.
             *
             * ⚠️ **DO NOT PUT IT BACK.** A second copy here would be two
             * controls opening one dialog, and the header one is the only one
             * visible while Quotes or Invoices is on screen — so a reader who
             * learned this one would find it missing on two of the three
             * sections, which is the shape this codebase has already had
             * reported as a deleted feature.
             */}
          </>
        }
        /*
         * ⚠️ **NO `moreFilters` NOTE — owner's ruling, 2026-09-05.** It read
         * "Raising a planned document moves this row onto it, keeping the
         * comment…", which was an explanation rather than a refusal and went
         * with the rest of this app's helper text. The behaviour is unchanged:
         * the row still moves onto the quote or invoice and still keeps its
         * comment.
         */
      />

      {/*
        * ⚠️ **ONE modal for the whole section, chosen by `row.kind`.** Mounting
        * one per row would put N forms in the tree and give the double-press
        * hazard N places to occur. Both are mounted unconditionally and closed:
        * each unmounts its own form while closed, so neither fetches anything on
        * an ordinary page load.
        */}
      <QuoteFormModal
        open={converting?.as === 'quote'}
        onClose={() => setConverting(null)}
        {...(converting?.as === 'quote' ? { prefill: prefillOf(converting.row) } : {})}
        /*
         * ⚠️ **THE SAVED DOCUMENT'S ID IS THE ARGUMENT, AND IT IS THE ONLY PLACE
         * IT IS AVAILABLE.** `onSaved` hands back the `Quote` the server
         * created; there is no second read that could recover it afterwards, so
         * dropping it here would leave the plan permanently unattachable.
         */
        onSaved={(quote) => {
          if (converting) onDocumentSaved(converting.row, quote.id);
          setConverting(null);
        }}
        /*
         * "Create and preview" (owner, 2026-10-25). The modal calls this INSTEAD
         * of `onSaved`, so it does the same raise step first — the
         * `POST /invoice-tracker/:id/raised` attach — and only then opens the
         * page's viewer. ⚠️ **IN PLACE, NOT `navigate(quotePreviewHref(…))`**
         * as other callers do: the attach is in flight and its failure is
         * reported in this section's `unlinked` banner, which leaving the page
         * would throw away.
         */
        onCreatedPreview={(quote) => {
          if (converting) onDocumentSaved(converting.row, quote.id);
          setConverting(null);
          onPreview(quotePreviewTarget(quote));
        }}
      />
      <InvoiceFormModal
        open={converting?.as === 'invoice'}
        onClose={() => setConverting(null)}
        {...(converting?.as === 'invoice' ? { prefill: prefillOf(converting.row) } : {})}
        onSaved={(invoice) => {
          if (converting) onDocumentSaved(converting.row, invoice.id);
          setConverting(null);
        }}
      />

      {/*
       * ⚠️ **THE EDIT MOUNT. The page has the CREATE one** — behind *Add future
       * billables*, above the section switcher — and passing no `row` is what
       * makes that one a create. They are two mounts of one dialog rather than
       * one lifted to the page because the row being edited lives here: lifting
       * it would mean the page holding a row it never reads, and the section
       * telling it which one through a second piece of state.
       *
       * ⚠️ **`open` IS DERIVED FROM `editing`, and `row` is only spread when it
       * is set.** The dialog reads `row` once, at mount, and unmounts itself
       * while closed — so this is a fresh seed per opening and there is no state
       * to reset. Holding it at `open={false}` with a live `row` would be the one
       * arrangement its own contract warns against.
       *
       * ⚠️ **`onCreated` FIRES ON AN EDIT TOO — the name is the dialog's and is
       * kept.** What it means is "the list is out of date", and the answer to
       * that here is the same `load` the page's create path reaches by bumping
       * `reloadToken`: this section's one reader, re-run against the current
       * page, search and filters. A local splice would be a second refresh path
       * and would put an edited row wherever the browser thought it belonged
       * rather than where the server's own `sort: 'party'` puts it — which, since
       * an edit can change the party, is exactly the field most likely to move
       * it.
       *
       * `setEditing(null)` here as well as in `onClose`: the dialog documents
       * that it closes after a successful write, but a dialog holding a stale row
       * is not something to leave resting on another component's call order.
       */}
      <FutureDocumentDialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        {...(editing ? { row: editing } : {})}
        onCreated={() => {
          setEditing(null);
          void load();
        }}
      />
    </>
  );
}

/* =========================================================================
 * The switchers
 * ===================================================================== */

/**
 * Two or three buttons in a group, matching the Table/Board control on
 * `/companies`, `/deals`, `/quotes`, `/invoices`, `/tasks`,
 * `/client-deliverables` and `/invoice-tracker`.
 *
 * ⚠️ **`SegmentedControl` exists in `src/components/ui` and is deliberately NOT
 * used** — several page comments claim there is no such primitive and they are
 * simply wrong, so this is a choice rather than an omission. It is built for a
 * TABLE CELL: `w-full` with `flex-1` segments and a reserved tick box on every
 * option, sized so a fixed-width grid cell cannot clip the longer label. In
 * `PageHeader`'s actions slot that stretches the choice across the header beside
 * the other controls. The button group is what every other page-level view
 * switch in this app already is, and matching seven of them is worth more than
 * adopting a primitive shaped for somewhere else.
 *
 * `role="group"` with `aria-pressed`, NOT `role="tab"`: these switch what the
 * panel below IS, rather than labelling panels that share one tabstop.
 *
 * ⚠️ **Every option renders at every width, always.** This codebase has already
 * had a working feature reported as *deleted* when it had merely become
 * unreachable behind a view toggle.
 */
function ButtonGroupToggle<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: readonly { value: T; label: string }[];
  onChange: (next: T) => void;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className="inline-flex max-w-full flex-wrap items-center gap-0.5 rounded-md border border-border bg-surface-sunken p-0.5"
    >
      {options.map((option) => (
        <Button
          key={option.value}
          size="sm"
          variant={value === option.value ? 'secondary' : 'ghost'}
          aria-pressed={value === option.value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </Button>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------
 * The mirror's freshness, and the button that forces it
 * --------------------------------------------------------------------- */

/**
 * When the mirror last ran, in words.
 *
 * 🔴 **THIS EXISTS BECAUSE A WORKING SYSTEM WAS REPORTED AS BROKEN.** Owner,
 * 2026-09-02: a planned row was added, the spreadsheet opened, the row was not
 * in it, and the sync was reported dead. Nothing was wrong — the worker ticks
 * every ten minutes and had not ticked. The screen said nothing either way, so
 * *"saved, not yet mirrored"* and *"the mirror is dead"* looked identical.
 *
 * ⚠️ **SO EVERY BRANCH HERE SAYS SOMETHING, AND NONE OF THEM IS A BLANK.** A
 * missing timestamp rendered as empty space reads as "fine", which is the exact
 * failure being closed, one layer up. `null` is NEVER SYNCED and is said in
 * those words; an absent key is "cannot tell" and is said in those words too,
 * because claiming "never" about a server that simply does not report it would
 * be a confident wrong answer of our own.
 */
function mirrorFreshnessText(sheet: SheetState): string {
  if (sheet.loading) return 'Checking when this was last synced…';
  if (!sheet.lastSyncedKnown) return 'Last synced: not reported by this server.';
  if (sheet.lastSyncedAt === null) return 'Not yet synced — this mirror has never run.';
  return `Last synced ${formatDateTime(sheet.lastSyncedAt)}.`;
}

/**
 * The mirror, with its freshness line as the CARD'S OWN FOOTER.
 *
 * 🔴 **THE FRESHNESS LINE USED TO FLOAT LOOSE ABOVE WHATEVER PANEL WAS ON
 * SCREEN, AND ON THE TABLE VIEW IT DESCRIBED A DOCUMENT THAT WAS NOT THERE.**
 * Owner-reported, 2026-09-02: bare grey text under the *Add future billables*
 * button, belonging to nothing, above a table card — *"it looks submerged"*.
 * It is now inside the mirror card and it renders on the SHEET VIEW ONLY.
 *
 * ⚠️ *SUPERSEDED, and it was the argument that put it on both views: "ON BOTH
 * VIEWS, not only the Sheet tab. The question it answers — 'why is my row not
 * in the spreadsheet' — is asked by somebody who pressed **Open in Google
 * Sheets** and went to look, and that button is on both views."* That is a real
 * cost and it was taken knowingly: a line about a mirror, rendered where no
 * mirror is visible, reads as page furniture and gets skipped by the person it
 * was written for. Somebody who wants the answer switches to **Sheet**, which
 * is one click and is where the document actually is.
 *
 * 🔴 **THE `Sync now` BUTTON WAS REMOVED ON 2026-09-02 — owner's instruction,
 * in his words: *"we only support auto sync … remove this sync button section
 * also no needed"*. THE FRESHNESS LINE IS NOT PART OF THAT REMOVAL AND MUST
 * STAY.** They arrived in the same change and are easy to mistake for one
 * feature; they answer opposite questions. The button was *"run it now"*; this
 * line is *"has it run"* — and this line is the whole reason the feature
 * exists, because a working mirror was reported as dead when it simply had not
 * ticked yet. Removing it would rebuild the original complaint with the fix
 * already in the file.
 *
 * ⚠️ **`POST /accounts-dashboard/sheet/sync` still exists** and is unchanged;
 * it is the operator recovery path. Nothing in this app calls it any more. Do
 * not re-add a control on the grounds that the route is still there.
 *
 * ── HOW IT GETS INSIDE A PANEL THIS FILE DOES NOT OWN ──────────────────────
 *
 * ⚠️ `TrackerSheetPanel` lives in `src/components/tracker-sheet/`, renders its
 * own `Panel`, and takes no children — so the line cannot be passed into it.
 * The card is JOINED instead: the panel's bottom rounding is dropped and the
 * strip below overlaps its bottom border by one pixel, so the two share a
 * single hairline and read as one card with a footer.
 *
 * ⚠️ **ONLY THE ROUNDING IS OVERRIDDEN — never the background or the border.**
 * `TrackerSheetPanel` goes `fixed inset-0` in full screen and paints the whole
 * window; a `bg-transparent` or `border-0` reaching it from out here would make
 * that overlay see-through, which is a regression in a state this file cannot
 * observe. `[&>section]` beats the panel's own `rounded-lg` on specificity
 * (0,2,1 against 0,1,0), and in full screen the panel already sets
 * `rounded-none`, so the override is a no-op there rather than a fight.
 */
function MirrorPanel({ sheet, children }: { sheet: SheetState; children: React.ReactNode }) {
  return (
    <div className="[&>section]:rounded-b-none">
      {children}
      <p className="m-0 -mt-px rounded-b-lg border border-border bg-surface px-4 py-2.5 text-xs text-fg-muted">
        {/*
         * ⚠️ **THE TIMESTAMP ONLY — owner's ruling, 2026-09-05.** The cadence
         * clause that used to follow it ("EMERGE rewrites this sheet on its own,
         * about every ten minutes.") was an explanation and went with the rest
         * of this app's helper text. The timestamp stays because it is a fact
         * off the response — it separates *"it has not ticked"* from *"it is
         * dead"* — and `mirrorFreshnessText` states each of its four states
         * plainly rather than shimmering.
         */}
        {mirrorFreshnessText(sheet)}
      </p>
    </div>
  );
}

/**
 * The sheet's place, held while the mirror's address is still being read.
 *
 * ✅ **BROUGHT ACROSS FROM `ClientDeliverablesPage` ON 2026-09-02**, which is the
 * page the owner asked this one to match. Without it, arriving on
 * `?view=sheet` renders the TABLE first — `canShowSheet` is false until the link
 * read settles, so `view` computes to `table` — and swaps to the frame a moment
 * later. That swap is visible, was not asked for, and on the Quotes section it
 * also fires a list request for a table nobody is going to look at.
 *
 * ⚠️ **THE HEADER IS `TrackerSheetPanel`'S HEADER, VERBATIM**, so nothing moves
 * when the real panel takes over. That is an invariant NOTHING CHECKS — the two
 * strings are typed out in two files, and it has already been broken once on the
 * Client Deliverables copy, where the placeholder went on promising a read-only
 * sheet after the panel stopped saying so. **Change both in the same edit.**
 *
 * ⚠️ It is NOT rendered once the mirror turns out to be unconfigured — that is a
 * settled answer, not a pending one, and it lands on the table. A placeholder
 * that resolved to nothing would be a shimmer promising a view never coming.
 */
function SheetPlaceholderPanel() {
  return (
    <Panel>
      <PanelHeader
        title="Google Sheets mirror"
        description="EMERGE rewrites this sheet automatically — edit the record in EMERGE, not here."
      />
      <div aria-busy className="border-b border-border p-4">
        <Skeleton className="h-[70vh] min-h-[360px] w-full" />
      </div>
    </Panel>
  );
}

/* =========================================================================
 * Filtering the SHEET
 * ===================================================================== */

/**
 * `withSelected`'s row-derived label map, for a view that has no rows.
 *
 * The sheet branch never fetches a list, so the only label source the table has
 * for a retired master row is not available here. A selected id the option lists
 * cannot name therefore renders as `Company #12` rather than as an empty
 * trigger — which would read as "no filter" beside a filtered sheet.
 */
const NO_ROW_LABELS: Map<string, string> = new Map();

/**
 * The chrome around the sheet's filter controls: the two buttons, and the one
 * line that says what state the FRAME is in.
 *
 * 🔴 **APPLY IS EXPLICIT, AND THAT IS NOT A STYLE CHOICE.** Every press is a
 * Google API round trip plus a full iframe reload — the frame remounts, so the
 * spreadsheet reloads from scratch. Live-on-change would fire one of those per
 * keystroke in the amount and date boxes, against a quota that belongs to the
 * whole business.
 *
 * ⛔ **NOTHING HERE MAY CALL THE FILTER PRIVATE.** A Google *saved* filter view
 * is stored in the document and is listed for every reader who opens it; it
 * changes nobody else's default, which is the half that matters, and that is
 * exactly as far as the wording may go. Google's genuinely per-reader filter has
 * no URL and cannot be created through an API at all.
 *
 * ⚠️ **THE STATUS LINE IS NOT HELPER TEXT AND A SWEEP MUST NOT TAKE IT.** Every
 * branch of it names a state the reader cannot otherwise see: a sheet that
 * ignored a filter and a sheet whose filter matched everything are the same
 * rectangle. In particular the failure branch is the one that runs today — the
 * mirror's service account has never been granted access to the spreadsheet — so
 * "we could not filter it, it is showing everything" is the ordinary render, and
 * it must never be worded as though the filter took.
 */
function SheetFilterShell({
  filterView,
  activeFilterCount,
  summaryBar,
  onApply,
  onClear,
  children,
}: {
  filterView: AccountsSheetFilterViewApi;
  activeFilterCount: number;
  /**
   * The stat-card filter bar — the same node the table view's `SectionPanel`
   * renders, in the same position relative to the heading and the filters.
   *
   * 🔴 **ITS WORDING IS WHY IT IS SAFE HERE.** The bar says *"Filtered by"* —
   * a statement about the filter this page holds — and never *"Showing"*, which
   * would be a claim about the cross-origin frame below. **The status line in
   * this shell stays the only thing that speaks for what the sheet is
   * displaying**, and it must keep outranking the bar: a card press can be in
   * force while Google refused the apply, which is the ordinary failure here.
   */
  summaryBar: React.ReactNode;
  onApply: () => void;
  onClear: () => void;
  children: React.ReactNode;
}) {
  const { applying, error, notWired, filterViewId } = filterView;
  const showClear = activeFilterCount > 0 || filterViewId !== null;

  /*
   * ⚠️ Ordered by what a reader most needs to know, and the two "the sheet is
   * unfiltered" branches come FIRST — a stale success line above a failed apply
   * would be the one arrangement that reports a filter as applied when it is
   * not. `notWired` outranks `error` because it is not a fault at all.
   */
  const status = notWired
    ? 'This server cannot filter the sheet, so the sheet below is showing everything.'
    : error !== null
      ? `The sheet could not be filtered, so it is showing everything. ${error}`
      : applying
        ? 'Filtering the sheet…'
        : filterViewId !== null
          ? /*
             * 🔴 **IT SAYS THE VIEW WAS CREATED, NOT THAT THE SHEET BELOW IS
             * SHOWING IT — CORRECTED 2026-09-03.** *SUPERSEDED: "The sheet
             * below is filtered. Anyone who opens this spreadsheet can see
             * this filter view in its filter list."*
             *
             * ⚠️ **The old sentence claimed something this app cannot know.**
             * All a `200` proves is that Google STORED a filter view. The frame
             * beside it is cross-origin, so nothing here can read what it is
             * displaying, and whether it honours `#fvid=` on load has never
             * been measured — nor whether a reader who is not an editor of the
             * protected range can activate a saved view at all. The owner
             * reported the symptom: companies whose invoices are all paid still
             * on screen under "Companies overdue", beneath a sentence saying
             * the sheet was filtered.
             *
             * Textbook proxy field: a value written to answer *was the view
             * created* read as the answer to *is the reader looking at it*. The
             * two agree whenever it works, which is why it survived review.
             *
             * ⚠️ **The sharing caveat is KEPT and is not decoration** — a saved
             * filter view is visible in everyone's filter list, so a person
             * narrowing this sheet is doing something other people can see.
             * That was true before and is unaffected by the correction.
             */
            'A filter view was created on the sheet. If the sheet below still shows everything, open its filter list and pick the view named for you — and note that anyone with the spreadsheet can see it there.'
          : activeFilterCount > 0
            ? 'These filters are not on the sheet yet — press Apply to sheet.'
            : null;

  return (
    <Panel className="mb-3">
      <PanelHeader title="Filter this sheet" />
      {summaryBar}
      <div className="px-4 py-3">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">{children}</div>

        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3">
          <Button variant="primary" size="sm" loading={applying} onClick={onApply}>
            Apply to sheet
          </Button>
          {/*
           * Absent rather than disabled while there is nothing to clear: a
           * control whose only outcome is nothing happening is the affordance
           * this app removes everywhere else. An EMPTY criteria set is what
           * clears the view server-side, and the `null` it answers with is a
           * success rather than a failure.
           */}
          {showClear && (
            <Button variant="ghost" size="sm" disabled={applying} onClick={onClear}>
              Clear
            </Button>
          )}
          {status !== null && <p className="m-0 text-xs text-fg-muted">{status}</p>}
        </div>
      </div>
    </Panel>
  );
}

/**
 * The Quotes tab's filters, on the sheet view.
 *
 * ⚠️ **THE SAME `FilterField` / `TextFilterField` AND THE SAME OPTION HOOKS THE
 * TABLE USES — not a second set.** Two banks of controls over one URL would
 * agree the day they were written and drift on the first key anybody adds, and
 * what would drift is which filters a reader can reach depending on which view
 * they happen to be on.
 *
 * ⚠️ **NOTHING IS DOUBLE-MOUNTED.** This renders only on the sheet branch and
 * `QuotesSection` only on the table branch, so the option reads fire once
 * either way.
 *
 * ⚠️ **THERE IS NO SEARCH BOX, and its absence is the contract rather than an
 * omission.** `?search=` is a server-side text match across a document's fields;
 * the sheet's filter criteria are per column, so there is nothing to send it as.
 * Offering the box and dropping the value would be a filter that appears to work.
 */
function QuoteSheetFilters({
  filterView,
  summaryBar,
}: {
  filterView: AccountsSheetFilterViewApi;
  /** The page-built stat-card filter bar, passed straight through. */
  summaryBar: React.ReactNode;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsKey = searchParams.toString();
  const filters = useMemo(
    () => readQuoteFilters(new URLSearchParams(searchParamsKey)),
    [searchParamsKey],
  );
  const activeFilterCount = QUOTE_FILTER_KEYS.filter((key) => filters[key] !== '').length;

  const companies = useCompanyOptions();
  const projects = useProjectOptions();
  const stages = useQuoteStageOptions();
  const currencies = useCurrencyOptions();
  const staff = useUserOptions(true, true);

  const stageOptions = useMemo<ComboboxOption[]>(
    () =>
      stages.stages.map((stage) => ({
        value: String(stage.id),
        label: stage.isActive ? stage.name : `${stage.name} (retired)`,
      })),
    [stages.stages],
  );
  const projectOptions = useMemo<ComboboxOption[]>(
    () => projects.projects.map((project) => ({ value: String(project.id), label: project.name })),
    [projects.projects],
  );
  const currencyPickerOptions = useMemo<ComboboxOption[]>(
    () => currencyOptions(currencies.currencies, filters.currency || null),
    [currencies.currencies, filters.currency],
  );
  const leadOptions = useMemo<ComboboxOption[]>(
    () =>
      staff.byGroup.account_lead.users.map((user) => ({
        value: String(user.id),
        label: user.name.trim() || user.email,
      })),
    [staff.byGroup.account_lead.users],
  );

  const setFilter = useCallback(
    (key: QuoteFilterKey, value: string) => {
      const next = new URLSearchParams(searchParamsKey);
      if (value) next.set(key, value);
      else next.delete(key);
      setSearchParams(next, { replace: true });
    },
    [searchParamsKey, setSearchParams],
  );

  return (
    <SheetFilterShell
      filterView={filterView}
      activeFilterCount={activeFilterCount}
      summaryBar={summaryBar}
      onApply={() => filterView.apply('quotes', quoteSheetCriteria(filters))}
      onClear={() => {
        const next = new URLSearchParams(searchParamsKey);
        for (const key of QUOTE_FILTER_KEYS) next.delete(key);
        setSearchParams(next, { replace: true });
        /*
         * ⚠️ An EMPTY criteria set, not a skipped call: the view lives in
         * Google's document, so clearing the URL alone would leave the frame
         * pointed at a filter view that is still there.
         */
        filterView.apply('quotes', {});
      }}
    >
      <FilterField
        label="Company"
        value={filters.companyId}
        onChange={(value) => setFilter('companyId', value)}
        options={withSelected(companies.options, filters.companyId, NO_ROW_LABELS, 'Company')}
        allLabel="All companies"
        loading={companies.loading}
        failed={companies.failed}
        multi
        searchPlaceholder="Search companies…"
      />
      <FilterField
        label="Stage"
        multi
        value={filters.stageId}
        onChange={(value) => setFilter('stageId', value)}
        options={withSelected(stageOptions, filters.stageId, NO_ROW_LABELS, 'Stage')}
        allLabel="Any stage"
        loading={stages.loading}
        failed={stages.failed}
      />
      <FilterField
        label="Project"
        multi
        value={filters.lineProjectId}
        onChange={(value) => setFilter('lineProjectId', value)}
        options={withSelected(projectOptions, filters.lineProjectId, NO_ROW_LABELS, 'Project')}
        allLabel="All projects"
        loading={projects.loading}
        failed={projects.failed}
        searchPlaceholder="Search projects…"
      />
      <FilterField
        label="Account lead"
        multi
        value={filters.accountLeadId}
        onChange={(value) => setFilter('accountLeadId', value)}
        options={withSelected(leadOptions, filters.accountLeadId, NO_ROW_LABELS, 'User')}
        allLabel="Any account lead"
        loading={staff.loading}
        failed={staff.failed}
        searchPlaceholder="Search people…"
      />
      <FilterField
        label="Currency"
        value={filters.currency}
        onChange={(value) => setFilter('currency', value)}
        options={currencyPickerOptions}
        allLabel="Any currency"
        loading={currencies.loading}
        failed={currencies.failed}
        searchPlaceholder="Search currencies…"
      />
      {/*
       * ⚠️ **THE THREE FLAG KEYS NEED CONTROLS HERE FOR THE REASON THEY NEED
       * THEM ON THE TABLE, PLUS ONE MORE: the stat cards set them on THIS view
       * now.** A card press narrows the sheet, so the key it set has to be
       * visible and undoable somewhere other than the card itself.
       */}
      <FilterField
        label="Draft"
        value={filters.draft}
        onChange={(value) => setFilter('draft', value)}
        options={FLAG_OPTIONS}
        allLabel="Any"
        loading={false}
        failed={false}
      />
      <FilterField
        label="Awaiting signature"
        value={filters.awaitingSignature}
        onChange={(value) => setFilter('awaitingSignature', value)}
        options={FLAG_OPTIONS}
        allLabel="Any"
        loading={false}
        failed={false}
      />
      <FilterField
        label="Accepted, not invoiced"
        value={filters.acceptedUnbilled}
        onChange={(value) => setFilter('acceptedUnbilled', value)}
        options={FLAG_OPTIONS}
        allLabel="Any"
        loading={false}
        failed={false}
      />
      <TextFilterField
        label="Issued from"
        type="date"
        value={filters.issueDateAfter}
        onChange={(value) => setFilter('issueDateAfter', value)}
      />
      <TextFilterField
        label="Issued to"
        type="date"
        value={filters.issueDateBefore}
        onChange={(value) => setFilter('issueDateBefore', value)}
      />
      <TextFilterField
        label="Valid till after"
        type="date"
        value={filters.validTillAfter}
        onChange={(value) => setFilter('validTillAfter', value)}
      />
      <TextFilterField
        label="Valid till before"
        type="date"
        value={filters.validTillBefore}
        onChange={(value) => setFilter('validTillBefore', value)}
      />
      <TextFilterField
        label="Total from"
        inputMode="decimal"
        value={filters.amountMin}
        onChange={(value) => setFilter('amountMin', value)}
      />
      <TextFilterField
        label="Total to"
        inputMode="decimal"
        value={filters.amountMax}
        onChange={(value) => setFilter('amountMax', value)}
      />
    </SheetFilterShell>
  );
}

/** The Invoices tab's filters, on the sheet view. See `QuoteSheetFilters`. */
function InvoiceSheetFilters({
  filterView,
  summaryBar,
}: {
  filterView: AccountsSheetFilterViewApi;
  /** The page-built stat-card filter bar, passed straight through. */
  summaryBar: React.ReactNode;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsKey = searchParams.toString();
  const filters = useMemo(
    () => readInvoiceFilters(new URLSearchParams(searchParamsKey)),
    [searchParamsKey],
  );
  /*
   * 🔴 **COUNTED OFF THE CRITERIA THIS BANK WILL ACTUALLY SEND, NOT OFF
   * `INVOICE_FILTER_KEYS` — and the difference is one reachable defect rather
   * than tidiness.** The due-date pair is a LIST filter the sheet cannot express
   * (see `invoiceSheetCriteria`) and it lives in the SAME query string, so a
   * reader who set one on the table view and then switched here arrives holding
   * it. Counted, `SheetFilterShell` would render *"These filters are not on the
   * sheet yet — press Apply to sheet"* over an EMPTY criteria set: Apply deletes
   * the view, the server answers `filterViewId: null` — a success — and the same
   * sentence comes straight back. **An Apply button that appears to do nothing,
   * every press, for ever.**
   *
   * ⚠️ Derived rather than a second hand-written key list: a list would agree the
   * day it was written and drift on the first key either side gains. The count is
   * identical to the old one for every key the sheet does express.
   *
   * ⚠️ **`onClear` below still clears EVERY key**, including the pair this does
   * not count — clearing more than a bank shows is safe, and leaving a filter
   * behind that the table view will apply is not.
   */
  const sheetCriteria = useMemo(() => invoiceSheetCriteria(filters), [filters]);
  const activeFilterCount = Object.keys(sheetCriteria).length;

  const companies = useCompanyOptions();
  const projects = useProjectOptions();
  const statuses = useInvoiceStatusOptions();
  const currencies = useCurrencyOptions();
  const staff = useUserOptions(true, true);

  const statusOptions = useMemo<ComboboxOption[]>(
    () =>
      statuses.statuses.map((status) => ({
        value: String(status.id),
        label: status.isActive ? status.name : `${status.name} (retired)`,
      })),
    [statuses.statuses],
  );
  const projectOptions = useMemo<ComboboxOption[]>(
    () => projects.projects.map((project) => ({ value: String(project.id), label: project.name })),
    [projects.projects],
  );
  const currencyPickerOptions = useMemo<ComboboxOption[]>(
    () => currencyOptions(currencies.currencies, filters.currency || null),
    [currencies.currencies, filters.currency],
  );
  const leadOptions = useMemo<ComboboxOption[]>(
    () =>
      staff.byGroup.account_lead.users.map((user) => ({
        value: String(user.id),
        label: user.name.trim() || user.email,
      })),
    [staff.byGroup.account_lead.users],
  );

  const setFilter = useCallback(
    (key: InvoiceFilterKey, value: string) => {
      const next = new URLSearchParams(searchParamsKey);
      if (value) next.set(key, value);
      else next.delete(key);
      setSearchParams(next, { replace: true });
    },
    [searchParamsKey, setSearchParams],
  );

  return (
    <SheetFilterShell
      filterView={filterView}
      activeFilterCount={activeFilterCount}
      summaryBar={summaryBar}
      onApply={() => filterView.apply('invoices', sheetCriteria)}
      onClear={() => {
        const next = new URLSearchParams(searchParamsKey);
        for (const key of INVOICE_FILTER_KEYS) next.delete(key);
        setSearchParams(next, { replace: true });
        filterView.apply('invoices', {});
      }}
    >
      <FilterField
        label="Company"
        value={filters.companyId}
        onChange={(value) => setFilter('companyId', value)}
        options={withSelected(companies.options, filters.companyId, NO_ROW_LABELS, 'Company')}
        allLabel="All companies"
        loading={companies.loading}
        failed={companies.failed}
        multi
        searchPlaceholder="Search companies…"
      />
      <FilterField
        label="Status"
        multi
        value={filters.statusId}
        onChange={(value) => setFilter('statusId', value)}
        options={withSelected(statusOptions, filters.statusId, NO_ROW_LABELS, 'Status')}
        allLabel="Any status"
        loading={statuses.loading}
        failed={statuses.failed}
      />
      <FilterField
        label="Project"
        multi
        value={filters.lineProjectId}
        onChange={(value) => setFilter('lineProjectId', value)}
        options={withSelected(projectOptions, filters.lineProjectId, NO_ROW_LABELS, 'Project')}
        allLabel="All projects"
        loading={projects.loading}
        failed={projects.failed}
        searchPlaceholder="Search projects…"
      />
      <FilterField
        label="Account lead"
        multi
        value={filters.accountLeadId}
        onChange={(value) => setFilter('accountLeadId', value)}
        options={withSelected(leadOptions, filters.accountLeadId, NO_ROW_LABELS, 'User')}
        allLabel="Any account lead"
        loading={staff.loading}
        failed={staff.failed}
        searchPlaceholder="Search people…"
      />
      <FilterField
        label="Currency"
        value={filters.currency}
        onChange={(value) => setFilter('currency', value)}
        options={currencyPickerOptions}
        allLabel="Any currency"
        loading={currencies.loading}
        failed={currencies.failed}
        searchPlaceholder="Search currencies…"
      />
      <FilterField
        label="Unsettled"
        value={filters.unsettled}
        onChange={(value) => setFilter('unsettled', value)}
        options={FLAG_OPTIONS}
        allLabel="Any"
        loading={false}
        failed={false}
      />
      <FilterField
        label="Overdue"
        value={filters.overdue}
        onChange={(value) => setFilter('overdue', value)}
        options={FLAG_OPTIONS}
        allLabel="Any"
        loading={false}
        failed={false}
      />
      <FilterField
        label="Overdue 90+ days"
        value={filters.overdue90Plus}
        onChange={(value) => setFilter('overdue90Plus', value)}
        options={FLAG_OPTIONS}
        allLabel="Any"
        loading={false}
        failed={false}
      />
      <FilterField
        label="Due in 30 days"
        value={filters.dueIn30Days}
        onChange={(value) => setFilter('dueIn30Days', value)}
        options={FLAG_OPTIONS}
        allLabel="Any"
        loading={false}
        failed={false}
      />
      <TextFilterField
        label="Bill date from"
        type="date"
        value={filters.billDateAfter}
        onChange={(value) => setFilter('billDateAfter', value)}
      />
      <TextFilterField
        label="Bill date to"
        type="date"
        value={filters.billDateBefore}
        onChange={(value) => setFilter('billDateBefore', value)}
      />
      <TextFilterField
        label="Total from"
        inputMode="decimal"
        value={filters.amountMin}
        onChange={(value) => setFilter('amountMin', value)}
      />
      <TextFilterField
        label="Total to"
        inputMode="decimal"
        value={filters.amountMax}
        onChange={(value) => setFilter('amountMax', value)}
      />
    </SheetFilterShell>
  );
}

/* =========================================================================
 * The stat cards, and what each one asks the page to show
 * ===================================================================== */

/**
 * Every filter key any section owns, in one list.
 *
 * ⚠️ **`setSection` AND THE CARD HANDLER BOTH CLEAR EXACTLY THIS SET**, and they
 * have to: the three sections share key names (`companyId`, `currency`,
 * `amountMin`), so a key carried across a section switch narrows the wrong list
 * silently. Two hand-written lists would agree the day they were written and
 * drift on the first key anybody adds.
 */
const ALL_FILTER_KEYS: readonly string[] = [
  ...QUOTE_FILTER_KEYS,
  ...INVOICE_FILTER_KEYS,
  ...FUTURE_FILTER_KEYS,
];

/**
 * The FLAG filters each section owns, keyed by section.
 *
 * 🔴 **A RECORD, NOT A TERNARY, AND THAT IS THE FIX FOR A LIVE BUG.**
 * `activeStatCard` used to look its siblings up with
 * `section === 'quotes' ? QUOTE_FLAG_FILTER_KEYS : INVOICE_FLAG_FILTER_KEYS` —
 * a BINARY choice over THREE sections. It was harmless only because the one
 * Future card carried no filter and returned before the lookup was reached. The
 * Future row has a filtered card now, so that branch would have checked a
 * planned-documents card against the INVOICE flag keys: it would have lit, or
 * failed to light, for a reason unconnected to what is on screen, with nothing
 * erroring and `tsc` perfectly happy.
 *
 * ⚠️ **EXHAUSTIVE OVER `Section`**, so a fourth section is a compile error here
 * rather than a card quietly measuring itself against another section's keys.
 */
const SECTION_FLAG_FILTER_KEYS: Readonly<Record<Section, readonly string[]>> = {
  quotes: QUOTE_FLAG_FILTER_KEYS,
  invoices: INVOICE_FLAG_FILTER_KEYS,
  future: FUTURE_FLAG_FILTER_KEYS,
};

/**
 * Every flag key on the page, derived from the record above so it cannot drift.
 *
 * ⚠️ **THE THREE GROUPS ARE DISJOINT BY NAME**, which is what makes clearing all
 * of them the same act as clearing "this section's flags plus any stale one left
 * behind by another section". Nothing here relies on that — deleting a key a
 * section does not own is a no-op — but it is why the uniform rule is also the
 * narrow one. **If a flag key is ever shared between two sections, this stops
 * being true and `selectCard`'s rule has to be re-derived.**
 */
const ALL_FLAG_FILTER_KEYS: readonly string[] = Object.values(
  SECTION_FLAG_FILTER_KEYS,
).flat();

/**
 * Every key a section owns, flags included — the counterpart to
 * `SECTION_FLAG_FILTER_KEYS`, and the thing the summary bar subtracts them from.
 *
 * ⚠️ **EXHAUSTIVE OVER `Section`** for the reason its sibling is: a fourth
 * section is a compile error here rather than a bar that quietly reports another
 * section's narrowings.
 */
const SECTION_FILTER_KEYS: Readonly<Record<Section, readonly string[]>> = {
  quotes: QUOTE_FILTER_KEYS,
  invoices: INVOICE_FILTER_KEYS,
  future: FUTURE_FILTER_KEYS,
};

/**
 * What to call each NON-FLAG filter in the summary bar's *"also filtered by …"*
 * clause — added 2026-09-14 with the bar.
 *
 * 🔴 **THIS IS WHAT ACCOUNTS FOR THE FIGURE GAP.** A stat card counts the whole
 * business (`/accounts/stats` takes no parameters), while a card press
 * deliberately KEEPS the reader's company, date, currency and amount filters —
 * that is the whole point of the 2026-09-03 change. So *"Invoices pending
 * payment: 41"* legitimately sits over a table of 6, and until now the only thing
 * saying why was a bare *"2 filters applied"* line below the filter bank.
 *
 * ⚠️ **A DATE PAIR IS ONE NOUN, NOT TWO.** `dueDateAfter` and `dueDateBefore` are
 * two halves of one window a reader set once, so both map to *"due date"* and the
 * builder de-duplicates. Listing them separately would report *"due date and due
 * date"* on the ordinary case of a bounded range.
 *
 * ⚠️ **`billDate` AND `dueDate` KEEP SEPARATE NOUNS**, because they are separate
 * columns answering separate questions — when the invoice was RAISED against when
 * it must be PAID. Folding them into one word would be the substitution this file
 * already records as a clean `200` over the wrong population.
 *
 * ⚠️ **EXHAUSTIVE OVER EVERY NON-FLAG KEY ON THE PAGE**, by construction: the key
 * type is the three sections' filter unions with the flag unions removed, so a
 * filter added to any section with no entry here is a `tsc` error naming it — not
 * a narrowing that silently stops being reported, which would leave the bar
 * claiming to account for the gap while omitting a term of it.
 *
 * ⚠️ **The DECLARATION ORDER is the reading order** in the finished sentence, so
 * the ids a reader is most likely to have set come first.
 */
type FlagFilterKey =
  | (typeof QUOTE_FLAG_FILTER_KEYS)[number]
  | (typeof INVOICE_FLAG_FILTER_KEYS)[number]
  | (typeof FUTURE_FLAG_FILTER_KEYS)[number];

type NarrowingFilterKey = Exclude<
  QuoteFilterKey | InvoiceFilterKey | FutureFilterKey,
  FlagFilterKey
>;

const NARROWING_FILTER_LABEL: Readonly<Record<NarrowingFilterKey, string>> = {
  companyId: 'company',
  contactId: 'contact',
  accountLeadId: 'account lead',
  /* The summary bar says "project", not "line project": the reader picked a
     project and that is the word for it. The KEY is what had to be exact. */
  lineProjectId: 'project',
  stageId: 'stage',
  statusId: 'status',
  quoteId: 'quote',
  currency: 'currency',
  issueDateAfter: 'issue date',
  issueDateBefore: 'issue date',
  validTillAfter: 'expiry',
  validTillBefore: 'expiry',
  billDateAfter: 'bill date',
  billDateBefore: 'bill date',
  dueDateAfter: 'due date',
  dueDateBefore: 'due date',
  amountMin: 'amount',
  amountMax: 'amount',
};

/**
 * The other narrowings in force on this section, worded, de-duplicated, in
 * declaration order.
 *
 * ⚠️ **IT READS THE URL, NOT A SECTION'S PARSED FILTERS.** The three sections own
 * their own parsing and are not mounted together — only one renders at a time —
 * so the page cannot ask the section on screen what it holds without threading
 * state back up through it. The URL is what every one of them derives from, which
 * makes it the honest common source.
 *
 * ⚠️ **THE COUNT CAN THEREFORE DIFFER BY ONE FROM A SECTION'S OWN
 * `activeFilterCount`**, which counts a key a section parsed and REJECTED (a
 * malformed date, say) as absent where this counts it as present. That is the
 * conservative direction: the bar over-reports a narrowing rather than claiming
 * to have accounted for one it never mentioned. **Do not "fix" it by counting
 * here too** — the bar names nouns and the line below counts keys, and they are
 * deliberately answering different questions.
 *
 * ⚠️ **A blank value is NOT a filter.** `listQuery` drops empty keys and an empty
 * value is a 422 on these endpoints, so a key sitting in the URL with nothing
 * after it narrows nothing and must not be named.
 */
function narrowingFilterLabels(params: URLSearchParams, section: Section): string[] {
  const owned = new Set<string>(SECTION_FILTER_KEYS[section]);
  const out: string[] = [];
  for (const [key, label] of Object.entries(NARROWING_FILTER_LABEL)) {
    if (!owned.has(key)) continue;
    const raw = params.get(key);
    if (raw === null || raw.trim() === '') continue;
    if (!out.includes(label)) out.push(label);
  }
  return out;
}

/**
 * The one flag filter that makes the list say what a card's label says, per
 * card. `null` where the card's own section, unfiltered, IS the answer.
 *
 * 🔴 **AN EXHAUSTIVE `Record<AccountsStatKey, …>`, AND KEEPING IT ONE IS THE
 * POINT.** A card added to `AccountsStatCards` with no entry here is a `tsc`
 * error naming the key. Splitting this into three per-section objects — which
 * looks like the natural move now that the cards are per section — would give up
 * exactly that: a card missing from its section's object would compile, render,
 * and do nothing when pressed.
 *
 * ⚠️ **IT DELIBERATELY DOES NOT SAY WHICH SECTION A CARD OPENS.**
 * `accountsStatSection()` answers that, off the `section` field declared beside
 * the card's own label and reader. Two declarations of one fact drift, and what
 * would drift here is a card that lights on one section and navigates to
 * another.
 *
 * ⚠️ **THE KEYS ARE `AccountsStatKey`, WHICH ARE NOT THE FIELD NAMES ON
 * `AccountsStats`** (`quotesPending` reads `quotesPendingSignature`).
 * `AccountsStatCards` owns that mapping; this owns only where a click goes.
 *
 * ⚠️ **`futureBillables` CARRIES NO FILTER, AND THAT IS THE CONTRACT RATHER THAN
 * A GAP.** "Planned" IS the Future Billables section with nothing narrowing it —
 * it is the "all" card standing beside "Due in the next 30 days", which is why
 * clicking it CLEARS that one rather than setting anything. See `selectCard`.
 */
const STAT_CARD_FILTER: Readonly<Record<AccountsStatKey, string | null>> = {
  quotesDraft: 'draft',
  quotesPending: 'awaitingSignature',
  quotesAcceptedUnbilled: 'acceptedUnbilled',
  invoicesPending: 'unsettled',
  companiesOverdue: 'overdue',
  invoicesOverdue90Plus: 'overdue90Plus',
  invoicesDueIn30Days: 'dueIn30Days',
  futureBillables: null,
  futureBillablesNext30Days: 'plannedIn30Days',
};

/**
 * Which card, if any, the current URL actually reflects.
 *
 * 🔴 **A MATCHING SECTION IS NOT ENOUGH — EXACTLY ONE of the section's flags has
 * to be on, and it has to be this card's.** Three things this closes:
 *
 * - `quotesPending` must not light merely because the Quotes section is open.
 *   The card claims the list below is narrowed to quotes awaiting signature, and
 *   an unfiltered register is not that list.
 * - **Two flags can be on at once** — only by hand, since every card clears the
 *   set before setting its own — and where they nest (`overdue` inside
 *   `unsettled`, `overdue90Plus` inside `overdue`) the list is the narrower one.
 *   Lighting both cards would put two different claims over one table, so
 *   **neither** lights.
 * - **`futureBillables` ("Planned") IS THE UNFILTERED SECTION**, so it lights
 *   only while NONE of its section's flags is on. Reading it as "active on the
 *   section alone" — which is what the earlier `filter === null` early return
 *   did — would have lit it beside "Due in the next 30 days", two cards claiming
 *   one table again.
 *
 * ⚠️ **THE SIBLING SET COMES FROM `SECTION_FLAG_FILTER_KEYS`, NEVER FROM A
 * TERNARY.** See that record for the binary lookup this replaces and why it was
 * silent.
 *
 * ⚠️ **A NON-FLAG NARROWING DOES NOT UNLIGHT A CARD.** Somebody narrowing to one
 * company is still looking at that card's population, inside that company — which
 * is exactly the arrangement a card click now preserves. See `selectCard`.
 *
 * `null` is the ordinary state and the one an arriving reader sees.
 */
function activeStatCard(params: URLSearchParams, section: Section): AccountsStatKey | null {
  const flagsOn = SECTION_FLAG_FILTER_KEYS[section].filter(
    (flag) => params.get(flag) === FLAG_ON,
  );

  for (const key of accountsStatKeysFor(section)) {
    const filter = STAT_CARD_FILTER[key];
    if (filter === null) {
      /* The "all" card: lit only while nothing in this section is narrowing. */
      if (flagsOn.length === 0) return key;
      continue;
    }
    if (flagsOn.length === 1 && flagsOn[0] === filter) return key;
  }
  return null;
}

/* =========================================================================
 * Page
 * ===================================================================== */

export function AccountsDashboardPage() {
  const { ability } = useAuth();

  /*
   * The permission check. A hidden sidebar row is an optimisation and never a
   * guarantee — the URL is typeable, the command palette offers it, and a
   * permission set can change under an access token that lasts days — so the
   * page answers for itself.
   *
   * ⚠️ This DEGRADES OPEN — see `NAV_SURFACE_MODULE` in `@/lib/permissions`.
   * Only an explicit `none` scope denies; an unseeded catalogue, an older API or
   * a failed `/permissions/me` all leave the page visible.
   *
   * ⚠️ **Hiding a page is not refusing a request.** The endpoints behind this
   * dashboard answer the same to everybody, so nothing here may be worded as
   * though the data were protected. This is a view control.
   *
   * ⚠️ Computed here rather than as an early return, because the return has to
   * sit below every hook. The existing `canCreate` / `canUpdate` gates further
   * up this file are WRITE gates and are untouched: they answer a different
   * question, on modules the API really does enforce.
   */
  const canReadSurface = ability.canRead(NAV_SURFACE_MODULE.dashboardAccounts);

  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsKey = searchParams.toString();
  const section = readSection(new URLSearchParams(searchParamsKey));

  /*
   * ⚠️ **ONE LAYOUT PER SECTION, ALL THREE CALLED HERE, UNCONDITIONALLY** — the
   * hooks must sit above the early return, and a section chip press must not
   * change the hook order. Each section's Table view has two movable blocks: the
   * stat-card row and the section's own panel. The SHEET view is not managed: the
   * embedded sheet is a fixed full-height frame, which leaves one movable block,
   * so it keeps today's fixed arrangement and shows no Edit layout control.
   * Block ids are stored per person — never rename them.
   */
  const quotesLayout = usePageLayout('accounts-dashboard.quotes', [
    { id: 'summary', label: 'Summary' },
    { id: 'list', label: SECTION_LABEL.quotes },
  ]);
  const invoicesLayout = usePageLayout('accounts-dashboard.invoices', [
    { id: 'summary', label: 'Summary' },
    { id: 'list', label: SECTION_LABEL.invoices },
  ]);
  const futureLayout = usePageLayout('accounts-dashboard.future', [
    { id: 'summary', label: 'Summary' },
    { id: 'list', label: SECTION_LABEL.future },
  ]);
  const sectionLayout =
    section === 'invoices' ? invoicesLayout : section === 'future' ? futureLayout : quotesLayout;

  const [addOpen, setAddOpen] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  /*
   * The page's ONE quote PDF viewer — see the `QuotePdfPreview` import. Mark
   * Sent and Send to Xero change a quote, so the quotes list re-reads; nothing
   * the Future billables section shows depends on a quote's stage.
   */
  const [quotesReloadToken, setQuotesReloadToken] = useState(0);
  const quotePreview = useQuotePdfPreview({
    onChanged: () => setQuotesReloadToken((token) => token + 1),
  });

  /*
   * 🔴 **THE SECTION-CHANGE RESET WENT WITH THE "New invoice" / "New quote"
   * BUTTON, 2026-09-02.** *SUPERSEDED: an `openNew` opener held here, dropped
   * during render whenever `section` changed, because an opener outliving the
   * section that registered it is a callback into an unmounted form wearing the
   * new section's label.*
   *
   * ⚠️ **THAT REASONING IS NOT RETIRED, ONLY UNEMPLOYED.** Anything else this
   * header ever lifts out of a section — an opener, a selection, a count — needs
   * the same reset, and needs it during render rather than in an effect, which
   * would commit one frame with the stale value on screen first. It also has to
   * cover the Back button, which moves `section` without going through
   * `setSection`.
   */

  const sheet = useAccountsSheetLink(section);

  /*
   * The reader's own filter view over the embedded sheet — see the hook's header
   * for what a `null` id means and why a `404` is calm.
   */
  const filterView = useAccountsSheetFilterView();
  const { apply: applyFilterView } = filterView;

  /**
   * 🔴 **A FILTER VIEW BELONGS TO ONE TAB, SO IT IS ONLY USED ON ITS OWN
   * SECTION.** The three sections share this page and one hook; without this
   * guard, applying on Quotes and then pressing the Invoices chip would hang the
   * Quotes view's id on the Invoices tab's link. Google resolves a real id
   * against the wrong tab into *something* rather than an error, so the failure
   * would be a frame quietly showing the wrong rows.
   */
  const activeFilterViewId = filterView.section === section ? filterView.filterViewId : null;

  /**
   * 🔴 **THE FRAME'S IDENTITY, AND IT INCLUDES THE FILTER VIEW.** Applying a
   * filter changes the embed's URL in its **FRAGMENT ONLY** (`#gid=…&fvid=…`),
   * and a fragment-only change to a live iframe's `src` is not reliably a
   * navigation — the frame keeps showing what it had while the attribute says
   * otherwise. That is the identical trap `key={section}` already exists for,
   * arriving on a second fragment key: without the id in here, **Apply appears
   * to do nothing** and the only symptom is a sheet that did not change.
   *
   * ⚠️ `'unfiltered'` rather than `''` so the two states cannot produce the same
   * key by accident, and so the value reads as a state rather than as a missing
   * one.
   */
  const sheetFrameKey = `${section}:${activeFilterViewId ?? 'unfiltered'}`;

  /**
   * Apply the filters spelled by `params` to `target`'s tab.
   *
   * ⚠️ **IT TAKES THE PARAMS RATHER THAN READING THE URL**, because its callers
   * hold a `URLSearchParams` they have just written and not yet committed —
   * `searchParamsKey` is this render's snapshot and does not know about it. That
   * is the same trap `selectCard`'s own header describes for two `setSearchParams`
   * calls, arriving one layer down.
   *
   * ⚠️ **`future` HAS NO FILTER VIEW AND MUST NOT GET ONE.** The endpoint's
   * section is `'quotes' | 'invoices'`; the Future Billable tab is mirrored but
   * its filter set was never part of this contract, so a press there narrows the
   * URL and leaves the sheet alone rather than 422ing on a section name.
   */
  const applySheetFilters = useCallback(
    (params: URLSearchParams, target: Section) => {
      const sheetSection: AccountsSheetFilterSection | null =
        target === 'quotes' ? 'quotes' : target === 'invoices' ? 'invoices' : null;
      if (sheetSection === null) return;
      applyFilterView(
        sheetSection,
        sheetSection === 'quotes'
          ? quoteSheetCriteria(readQuoteFilters(params))
          : invoiceSheetCriteria(readInvoiceFilters(params)),
      );
    },
    [applyFilterView],
  );

  /*
   * ⚠️ THE TAB IS GATED ON THE PREVIEW URL, NOT ON `link`, AND NOT ON
   * `configured`. Three states collapse to "there is nothing to embed": no
   * mirror configured, the answer not known yet, and — the one this exists for —
   * a server that predates `spreadsheetPreviewUrl` and does not send it. This
   * app deploys separately from the API and can be newer than it, so that last
   * case is ordinary rather than broken, and it must degrade to the table rather
   * than to a tab that opens a frame pointed at nothing.
   *
   * ⚠️ **AND SINCE 2026-09-02 IT IS ALSO GATED ON THIS SECTION'S GID.** The
   * mirror writes one tab per section; the embed used to be the spreadsheet's
   * default tab whatever section was on screen, so switching to Invoices or
   * Future Billable left Quotes framed under the new heading — owner-reported,
   * and silent. Without a gid there is no honest embed to show, so the Sheet
   * toggle disappears for that section and the view falls back to the table.
   *
   * **The cost, stated rather than hidden:** against an API that does not yet
   * send gids the Sheet tab is not offered at all. That is deliberate — the
   * only alternative is framing one section's tab under another's name, which
   * is the defect — and it self-heals the moment the API ships. It is also
   * near-free today: this mirror has never run against live Google, so
   * `configured` is false in production and the tab is already absent.
   */
  const sectionGid = sheet.tabs[section];
  const sheetUrl = sheet.loading ? null : sheetHrefFor(sheet.previewLink, sectionGid);
  const canShowSheet = sheetUrl !== null;

  /**
   * 🔴 **WHAT ACTUALLY GOES IN THE FRAME: THE EDIT URL — OWNER'S INSTRUCTION,
   * 2026-09-02.** He asked five times for this page to look like the Client
   * Deliverables mirror, and the difference was exactly this: that page frames
   * `spreadsheetUrl` (the full Sheets application — document title, menu bar,
   * toolbar, column letters), this one framed `spreadsheetPreviewUrl` (a bare
   * read-only grid).
   *
   * ⚠️ **THIS REVERSES A DELIBERATE DECISION, IT DOES NOT FIX AN OVERSIGHT — and
   * that is written here so nobody restores the preview url later believing they
   * are repairing a bug.** *SUPERSEDED, from this call site and from
   * `TrackerSheetPanel`'s own header: "**STILL THE PREVIEW URL HERE, and that is
   * a decision rather than a rename left half-done** … the owner's instruction
   * named that tracker, and this is a different document … Widening it here would
   * have been a second, unasked change to what people may type into, arriving as
   * a side effect of a prop rename."* **He has now asked for it here, on this
   * document, explicitly. His spreadsheet, his call — do not re-argue it.**
   *
   * ⚠️ **The consequences travel with the address and are unchanged**, so they
   * are not restated here: they are written up once at the top of
   * `TrackerSheetPanel` (comments survive; edits inside the written rectangle are
   * refused by Google, visibly; **anything typed OUTSIDE it is erased on the next
   * tick, silently**). Google decides per viewer whether this reader may type at
   * all — this app cannot see that answer and must never claim one.
   *
   * ⚠️ **`?? sheetUrl` IS NOT TIDINESS, and this pair reads backwards from the
   * gate above ON PURPOSE.** `spreadsheetUrl` is non-optional on the response and
   * `spreadsheetPreviewUrl` is the one an older API omits — so the edit address
   * is the one that is always there, and the preview is the fallback for a server
   * whose shape we cannot predict. Same construction as `ClientDeliverablesPage`.
   *
   * ⚠️ **NEVER DERIVE ONE FROM THE OTHER BY SWAPPING `/edit` FOR `/preview`.**
   * The shape of a Google address is a fact the API owns, and a bad swap does not
   * error — it paints an empty frame that looks exactly like a permissions
   * problem.
   *
   * ⚠️ **THE GID GATE SURVIVES INTACT, and that is why this goes through
   * `sheetHrefFor` rather than falling back to the bare document the way
   * `sheetOpenUrl` does.** An embed with no `#gid=` opens the spreadsheet's FIRST
   * tab — Quotes — under whatever heading is on screen, which is the exact
   * owner-reported defect this section's deep linking exists to fix. No gid means
   * no embed, and both halves of this expression are gid-bearing or null.
   */
  /*
   * ⚠️ **`activeFilterViewId` IS ON BOTH HALVES, NOT ONLY THE FIRST.** The
   * fallback is what an older API's response lands on, and a filter silently
   * dropped there would be the one deployment where Apply appears to do nothing.
   * `sheetUrl` above is left fvid-free deliberately: it is the GATE, and whether
   * a section can be embedded at all has nothing to do with a filter.
   */
  const sheetEmbedUrl = sheet.loading
    ? null
    : (sheetHrefFor(sheet.link, sectionGid, activeFilterViewId) ??
      sheetHrefFor(sheet.previewLink, sectionGid, activeFilterViewId));

  /*
   * ⚠️ Falls back to the DOCUMENT when this section's gid is unknown, unlike
   * the embed above, and the asymmetry is the point. An iframe makes a silent
   * claim — "this is Future Billable" — where a link hands somebody to Google,
   * which shows its own tab strip with the active tab named. Landing on the
   * wrong tab there is visible and one click from right; landing on it inside
   * our frame, under our heading, is not.
   */
  /*
   * ⚠️ It carries the filter view too, so somebody who filtered the frame and
   * then opened the document gets the same rows rather than a wider list that
   * reads as the filter having been lost. It still falls back to the bare
   * document when the gid is unknown, and `sheetHrefFor` will not append an
   * `fvid` without one.
   */
  const sheetOpenUrl = sheetHrefFor(sheet.link, sectionGid, activeFilterViewId) ?? sheet.link;

  /*
   * 🔴 **THE SHEET IS THE DEFAULT AND `?view=table` IS THE OPT-IN** — owner's
   * instruction, 2026-09-02. The inverse of what stood here, and the same shape
   * `ClientDeliverablesPage` already uses.
   *
   * ⚠️ **A URL THAT NAMES A VIEW STILL WINS, IN BOTH DIRECTIONS.** `?view=table`
   * renders the table and `?view=sheet` renders the sheet; only an ABSENT (or
   * unrecognised) key falls to the default. So every link anybody has already
   * shared — including the `?view=sheet` ones this page has been minting since it
   * shipped — still resolves to the view it names, and a shared address keeps
   * meaning one thing.
   *
   * ⚠️ **NOTHING IS WRITTEN INTO THE URL ON ARRIVAL, and that is what keeps the
   * default clearable.** `table` is expressible as a value, so choosing it writes
   * `?view=table` and that sticks through a reload, the Back button and a shared
   * link. A resolved view written on arrival would buy nothing and would put a
   * second writer on the query string.
   *
   * ⚠️ `canShowSheet` is applied on the READ, so a link to `?view=sheet` on a box
   * with no mirror lands on the table rather than on a blank frame beside a tab
   * that is not rendered — **and so does the new default, which is the whole
   * reason an unconfigured deployment is unaffected by this change.** The param
   * is left in the URL untouched: the reader did not type it, rewriting
   * somebody's link on arrival is that second writer again, and the answer
   * changes as soon as the mirror is configured.
   */
  const wantsTable = searchParams.get('view') === 'table';
  const view: TrackerView = !wantsTable && canShowSheet ? 'sheet' : 'table';

  /*
   * ⚠️ **The mirror's address is not known on the first paint**
   * (`useAccountsSheetLink` reads it over the network), so `canShowSheet` is
   * false for that window and the resolution above would answer `table` —
   * rendering a whole table, firing its list request, and swapping it for the
   * frame a moment later.
   *
   * That flash did not matter while the table WAS the default and the answer only
   * ever added a tab. It matters now: it is the default view arriving as a
   * visible replacement of another one, which reads as the page changing its
   * mind. So the undecided case is its own state.
   *
   * ⚠️ **`view` cannot answer this on its own** — it computes to `table` during
   * exactly the window the placeholder exists to cover — so this is tested
   * against the raw param, and only when the reader has NOT asked for the table,
   * because that request needs no network answer and must never wait on one.
   */
  const resolvingSheet = !wantsTable && sheet.loading;

  const setSection = useCallback(
    (next: Section) => {
      const params = new URLSearchParams(searchParamsKey);
      // `quotes` is the default, spelled by the key's ABSENCE — one canonical
      // URL per section.
      if (next === 'quotes') params.delete('section');
      else params.set('section', next);
      /*
       * ⚠️ Every section's filters live in the SAME query string, and their key
       * names overlap (`companyId`, `currency`, `amountMin`). Carrying them
       * across a section switch would apply one section's narrowing to another's
       * list, silently. So the switch clears every filter key any section owns
       * and leaves only `view`.
       */
      for (const key of ALL_FILTER_KEYS) params.delete(key);
      setSearchParams(params, { replace: true });
    },
    [searchParamsKey, setSearchParams],
  );

  const { stats, overdue, statsLoading, statsError } = useAccountsStats();

  const activeCard = useMemo(
    () => activeStatCard(new URLSearchParams(searchParamsKey), section),
    [searchParamsKey, section],
  );

  /**
   * 🔴 **ONE `URLSearchParams`, EVERY WRITE, ONE `setSearchParams`.** Calling
   * `setSection` and then a section's `setFilter` would look right and would
   * WIPE THE FILTER: `setSection` deletes every filter key any section owns, and
   * the second write starts from `searchParamsKey` — the render's snapshot of
   * the query string, which the first write has not updated yet — so the section
   * change is what survives and the narrowing silently does not. Nothing errors;
   * the reader gets the right table with the wrong contents. **`view` goes into
   * the same write for the same reason** — see the sheet branch below.
   *
   * 🔴 **WHAT A CLICK CLEARS: EVERY FLAG FILTER ON THE PAGE, AND NOTHING ELSE.**
   * Owner's change, 2026-09-03. *SUPERSEDED: `for (const key of ALL_FILTER_KEYS)
   * params.delete(key)` — every filter any section owns, ids and dates and
   * amounts included.*
   *
   * The rule, and why it is the smallest one that is correct:
   *
   * - **The section's own flags MUST go**, or two cards claim one table. They
   *   nest rather than compose (`overdue90Plus` ⊂ `overdue` ⊂ `unsettled`), so a
   *   leftover sibling means the list is narrower than the card that lit says,
   *   and `activeStatCard` then lights neither — a click that appears to do
   *   nothing.
   * - **The OTHER sections' flags go too**, and that is the cheap half: a flag
   *   left behind is inert on the section on screen and re-applies the moment
   *   somebody walks back to its own section by any route that does not clear —
   *   the Back button, a shared link, a card click. Since the three flag groups
   *   are disjoint by name, clearing all of them IS clearing "mine, plus
   *   anything stale". One rule, no per-section branching.
   * - **Everything else stays: `companyId`, `currency`, the dates, the amount
   *   bounds.** This is the point of the change — narrowing to one school and
   *   then pressing "Overdue" gives that school's overdue invoices, which is the
   *   question somebody actually has. **It is safe across a section switch
   *   because no non-flag key means two different things in two sections**: the
   *   shared ones (`companyId`, `contactId`, `projectId`, `accountLeadId`,
   *   `currency`, `amountMin`, `amountMax`) are the same id or code everywhere,
   *   and the rest are spelled per section (`stageId` vs `statusId`,
   *   `issueDateAfter` vs `billDateAfter`) so they sit inert until their own
   *   section is back. **Give two sections one key with two meanings and this
   *   stops holding.**
   *
   * ⚠️ **THE ACCEPTED COST, STATED RATHER THAN BURIED: the card's figure and the
   * list's count can now disagree.** "Invoices pending payment: 41" over a table
   * reading "6 invoices (2 filters)" is the correct render of a filtered
   * question — the counts come from `/accounts/stats`, which takes no parameters
   * at all and describes the whole business. The filter row above the table says
   * how many filters are applied, which is what explains the gap. **Do not close
   * it by making the cards follow the filters** — that would need a per-filter
   * aggregate this API does not have, and deriving one here is a request per
   * company against a shared rate limit.
   *
   * ⚠️ **THE SECTION CHIP STILL CLEARS EVERYTHING** (`setSection`), and the
   * difference is deliberate: the chip means *go somewhere else*, a card means
   * *show me this population*. A chip press that silently carried a company
   * filter into a section whose panel is collapsed is the case that comment was
   * written for and it has not changed.
   *
   * ⚠️ **CLICKING THE ACTIVE CARD CLEARS ITS FILTER AND LEAVES THE SECTION
   * ALONE**, so a person is never stranded in a filtered list whose only way out
   * is a control inside a collapsed "More filters" panel.
   *
   * ✅ **"Planned" IS NO LONGER A PERMANENTLY-LIT NO-OP.** *SUPERSEDED: "a second
   * click there is deliberately a no-op".* It carries no filter, so the ordinary
   * path — clear every flag, set nothing — is exactly "show the Future Billables
   * section unfiltered", which CLEARS `plannedIn30Days`. The two future cards are
   * an all/subset pair and both do something. It is still a no-op when it is
   * already the active card, which is what any "all" selector does when it is
   * already chosen; the difference is that it is now lit only when it is true.
   *
   * `{ replace: true }` because narrowing a list is not a navigation — a history
   * entry per card press would make Back mean "undo one filter".
   */
  const selectCard = useCallback(
    (key: AccountsStatKey) => {
      const filter = STAT_CARD_FILTER[key];
      const target = accountsStatSection(key);
      const params = new URLSearchParams(searchParamsKey);

      /*
       * 🔴 **A CARD PRESS NOW FILTERS THE SHEET IN PLACE. IT NO LONGER LEAVES
       * IT.** Owner's instruction, 2026-09-03.
       *
       * ⚠️ *SUPERSEDED, the whole rule that stood here and the argument under
       * it: "**ON THE SHEET VIEW A CLICK IS 'SHOW ME THAT LIST', NEVER A
       * TOGGLE.** A card cannot filter an embedded Google Sheet — the frame is
       * somebody else's document and this app has no say in what it displays —
       * so the only honest thing a press can do there is take the reader to the
       * view that CAN answer it, with the filter already applied. That also
       * settles the toggle: on the sheet the card is not governing what is on
       * screen, so 'press it again to clear' is not the gesture anybody is
       * making … ⚠️ **ONLY WHEN THE SHEET IS ACTUALLY SHOWING.** `?view=table`
       * is the one value this page writes … so writing `view=table` while the
       * table is already up would add a param the reader never typed."*
       *
       * **What changed is the premise, not the reasoning.** The frame IS still
       * somebody else's document, but the API can now create this reader's own
       * Google saved filter view over it and the frame's link carries its
       * `fvid` — so a card CAN narrow what is on screen, which is what the
       * superseded rule said was impossible. With that gone, its two
       * conclusions go with it: there is nothing to leave, so no `view=table` is
       * written, and the card IS governing the sheet, so pressing the lit one
       * clears it exactly as it does on the table.
       *
       * ⚠️ **THE `params` WRITE AND THE APPLY ARE ONE ACT, IN THAT ORDER, AND
       * THE APPLY READS `params` RATHER THAN THE URL.** `searchParamsKey` is
       * this render's snapshot and does not yet know about the write above it,
       * so an apply that re-read the URL would send the PREVIOUS filter set —
       * a sheet narrowed to whatever the last press asked for, one press behind,
       * with nothing failing. Same trap this comment's own header describes for
       * two `setSearchParams` calls.
       *
       * ⚠️ **THE APPLY CAN FAIL AND THE URL WRITE CANNOT.** The two are not a
       * transaction and must not be made into one: the filter belongs in the URL
       * whichever way Google answers, and `SheetFilterShell` is what tells the
       * reader the frame beside it is unfiltered.
       *
       * 🔴 **CORRECTED 2026-09-03 BY THE OWNER: THE SPREADSHEET IS SHARED WITH
       * THE SERVICE ACCOUNT.** *SUPERSEDED: "**That is the path that runs
       * today** — the mirror's service account has never been granted access to
       * the spreadsheet — so the ordinary outcome of a card press on the sheet
       * is a lit card, a written param, and a sentence saying the sheet could
       * not be filtered."*
       *
       * ⚠️ **That was a claim about somebody else's Google account, written
       * here, and nothing in this repo could ever have failed on it.** It made
       * the failure path read as the NORMAL path, so a reader debugging this
       * would start from "of course it did not filter" — which is the wrong end
       * entirely once the sheet is shared.
       *
       * 🔴 **WHAT IS STILL UNPROVEN, AND IS THE LIVE QUESTION:** a `200` from
       * the apply says Google CREATED the filter view. It does not say the
       * embedded frame is DISPLAYING it — the frame is cross-origin, so this app
       * can never read that, and `#fvid=` being honoured on load has never been
       * measured. Two further unknowns sit behind it: the written range is
       * protected with the service account as its only editor, and whether a
       * non-editor can activate a saved filter view over a protected range is
       * recorded as UNMEASURED in the backend's own notes (Sheets may offer such
       * a reader a PERSONAL filter view instead, which no `fvid` can name).
       *
       * ⛔ **So `SheetFilterShell` must not word its success as "the sheet below
       * is filtered".** That is the proxy-field shape this codebase documents at
       * length: a field written to answer *was the view created* read as the
       * answer to *is the reader looking at it*. Say what is known — the filter
       * view was created, and name it — and let the reader see the frame.
       */
      const onSheet = view === 'sheet';

      if (activeCard === key) {
        if (filter === null) return;
        params.delete(filter);
        setSearchParams(params, { replace: true });
        if (onSheet) applySheetFilters(params, target);
        return;
      }

      // `quotes` is the default, spelled by the key's ABSENCE — see `setSection`.
      if (target === 'quotes') params.delete('section');
      else params.set('section', target);

      for (const flagKey of ALL_FLAG_FILTER_KEYS) params.delete(flagKey);
      if (filter !== null) params.set(filter, FLAG_ON);

      setSearchParams(params, { replace: true });
      if (onSheet) applySheetFilters(params, target);
    },
    [activeCard, applySheetFilters, searchParamsKey, setSearchParams, view],
  );

  /**
   * The bar that says what the list below is narrowed to — 2026-09-14, owner's
   * instruction that a card press should be prominent.
   *
   * 🔴 **BUILT ONCE HERE AND PASSED DOWN AS A NODE, rather than each section
   * rendering its own.** Only one section is mounted at a time, so a per-section
   * copy would be three renders of one sentence that have to agree about the
   * wording, the tone rule and what Clear means — and the thing that would drift
   * is a bar claiming one population over a table showing another. The page is
   * also the only place that holds `activeCard` and `selectCard`.
   *
   * ⛔ **`null` WHEN THE PRESSED CARD CARRIES NO FILTER.** Future Billables'
   * *Planned* IS the section unfiltered, so a bar announcing it would be a
   * sentence about the absence of a narrowing — noise on the ordinary arrival,
   * and a claim that something is filtered when nothing is. `STAT_CARD_FILTER` is
   * the test, not the card's identity, so an "all" card added to another section
   * needs no change here.
   *
   * ⚠️ **`onClear` IS `selectCard(activeCard)` — the SAME CALL a second press on
   * the lit card makes**, deliberately. Two controls, one meaning, and one code
   * path: it drops this card's flag, keeps the company and date filters, and on
   * the sheet view re-applies the narrowed set to the frame. A bespoke clear here
   * would be a second writer of the same query string that could fall out of step
   * with the card the moment either changed.
   *
   * ⚠️ **The tone asks the CARD, not the figure** — see `accountsStatWarns`.
   */
  const summaryBar = useMemo(() => {
    if (activeCard === null || STAT_CARD_FILTER[activeCard] === null) return null;
    return (
      <AccountsFilterSummaryBar
        cardLabel={accountsStatLabel(activeCard)}
        tone={accountsStatWarns(activeCard) ? 'danger' : 'neutral'}
        alsoFiltered={narrowingFilterLabels(new URLSearchParams(searchParamsKey), section)}
        onClear={() => selectCard(activeCard)}
      />
    );
  }, [activeCard, searchParamsKey, section, selectCard]);

  const setView = useCallback(
    (next: TrackerView) => {
      const params = new URLSearchParams(searchParamsKey);
      /*
       * 🔴 **THE SHEET IS THE DEFAULT NOW, spelled by the key's ABSENCE — the
       * inverse of what stood here.** One canonical URL per view: `?view=table`
       * is the only value this writes, and choosing the sheet clears the key
       * rather than writing `?view=sheet`.
       *
       * ⚠️ **READING still accepts `?view=sheet`** (see the resolution above), so
       * the links this page minted before 2026-09-02 keep working. It is only
       * the WRITE that stopped producing them.
       */
      if (next === 'table') params.set('view', 'table');
      else params.delete('view');
      setSearchParams(params, { replace: true });
    },
    [searchParamsKey, setSearchParams],
  );

  /*
   * ⚠️ Below every hook, so the early return cannot change the hook order. The
   * refusal is rendered here rather than by the route, which carries no guard —
   * every signed-in route in this app is an ordinary `Shell` route, so a reader
   * arriving by URL must get a sentence rather than an error or a blank screen.
   */
  if (!canReadSurface) {
    return (
      <>
        <PageHeader eyebrow="Dashboards" title="Accounts" />
        <Panel className="min-w-0">
          <EmptyState
            title="This dashboard isn’t available to your account"
            description="Your permission set does not include this. An administrator can change that."
          />
        </Panel>
      </>
    );
  }

  /*
   * The placeholder and the sheet are the two branches the layout manager does
   * not touch; everything else is the section's Table view, laid out by
   * `sectionLayout`. Same two conditions as the ternary below — change both.
   */
  const onSheetBranch =
    resolvingSheet || (view === 'sheet' && sheetUrl !== null && sheetEmbedUrl !== null);

  const statCards = (
    <AccountsStatCards
      section={section}
      sectionLabel={SECTION_LABEL[section]}
      stats={stats}
      loading={statsLoading}
      error={statsError}
      active={activeCard}
      onSelect={selectCard}
    />
  );

  return (
    <>
      <PageHeader
        eyebrow="Dashboards"
        title="Accounts"
        actions={
          <>
            <ButtonGroupToggle
              label="Accounts dashboard section"
              value={section}
              options={SECTION_OPTIONS}
              onChange={setSection}
            />
            {/*
             * The toggle renders only when there is a second view to reach. A
             * lone "Table" chip with nothing to switch to would be an affordance
             * whose only outcome is nothing happening.
             */}
            {canShowSheet && (
              <ButtonGroupToggle
                label="Accounts dashboard view"
                value={view}
                options={VIEW_OPTIONS}
                onChange={setView}
              />
            )}
            {/*
             * 🔴 **"New invoice" / "New quote" WAS HERE AND IS GONE — owner's
             * instruction, 2026-09-02.** *SUPERSEDED, along with the paragraphs
             * arguing for its placement: it rendered on the Table view of the
             * Quotes and Invoices sections, opening the section's own create
             * modal through an opener the section registered on mount.*
             *
             * ⚠️ **RAISING A QUOTE OR AN INVOICE IS NOT GONE WITH IT.** `/quotes`
             * and `/invoices` both carry their own New button, mounting the same
             * two form modals. This removed one entry point on a dashboard, not
             * the ability — say so rather than letting somebody read the absence
             * as the feature having been withdrawn.
             *
             * ⚠️ **THE WHOLE OPENER CHAIN WENT WITH IT, AND THAT IS THE POINT.**
             * The `openNew` state here, the `registerNew` callback, the `onNew`
             * prop on both sections and the effect each used to register itself
             * existed for this button and for nothing else. Left in place they
             * would be a mechanism with no caller — and this file already carries
             * the lesson that half-dismantling one is worse than either end of
             * it. Restoring the button means restoring the chain, not just the
             * markup.
             *
             * ⚠️ **"Add future billables" BELOW IS A DIFFERENT ACT AND STAYS.**
             * It records work nobody has raised yet and touches no financial
             * system, where this button created a real document whose save
             * pushes to Xero. The two were deliberately never alike, and removing
             * one is not an argument for removing the other.
             */}
            {/*
             * ⚠️ KEPT ON BOTH VIEWS, DELIBERATELY — and it matters MORE since
             * the sheet became the default view.
             *
             * ⚠️ *SUPERSEDED: "The frame is read-only, so the way to actually
             * work in the sheet is this button".* The frame is the EDIT url now,
             * so that is no longer why this exists. The reason that replaces it
             * is stronger: **a cross-origin frame that fails to paint reports
             * nothing**, and the sheet is what a reader now lands on — so for
             * anybody whose Google account cannot see this spreadsheet, this
             * button and `TrackerSheetPanel`'s own fallback line are the only
             * two things on screen that lead anywhere. Removing it on the Sheet
             * tab would make a blank rectangle a dead end.
             *
             * On the Table tab it keeps the document one click away without
             * anybody having to discover the tab first.
             *
             * Absent, never disabled, and never present-then-gone: three states
             * collapse to one piece of markup — no mirror configured, the answer
             * not yet known, and the read having failed — and all three mean the
             * same thing to a reader.
             */}
            {!sheet.loading && sheetOpenUrl !== null && (
              <Button
                size="sm"
                variant="secondary"
                href={sheetOpenUrl}
                target="_blank"
                trailingIcon={<ExternalTabIcon />}
              >
                Open in Google Sheets
                <span className="sr-only"> (opens in a new tab)</span>
              </Button>
            )}
            {!onSheetBranch && <LayoutToolbar layout={sectionLayout} />}
          </>
        }
      />

      {/*
       * 🔴 **"Add future billables" — DIRECTLY UNDER THE SECTION SWITCHER, ON
       * EVERY SECTION.** Owner's instruction, 2026-09-02: *"keep it right below
       * invoice quotes future billables tab so we dont have to go to that page
       * for this specifically and name it as add future billable into some
       * different good color"*. It used to live inside the Future Billable
       * section's filter bank, so recording planned work meant switching section
       * first.
       *
       * ⚠️ **IT SITS UNDER A ROW OF SECTION CHIPS AND MUST NOT READ AS A FOURTH
       * ONE.** A coloured pill in this position is exactly what somebody clicks
       * expecting to NAVIGATE. Three things keep it a button: it is
       * `variant="primary"` — the repo's solid accent idiom, the same one
       * "Save"-class actions use, which no chip in `ButtonGroupToggle` ever
       * takes (those are `secondary`/`ghost` inside a bordered sunken group); it
       * carries a `+` icon, which no section chip has; and it sits in its own
       * row below, outside the group's border, rather than beside them.
       *
       * ⚠️ **THE COLOUR IS A TOKEN, NEVER A HEX.** `variant="primary"` resolves
       * to `bg-accent-brand` / `text-fg-on-accent`, which are defined for both
       * themes. A literal colour here is the standard way to ship something
       * unreadable in one of the two.
       *
       * ⚠️ It is deliberately NOT in `PageHeader`'s `actions` slot, which is
       * where the switcher lives: dropping it in there would put it INSIDE the
       * chip row, which is the one arrangement the paragraph above rules out.
       */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Button variant="primary" size="sm" leadingIcon={<PlusIcon />} onClick={() => setAddOpen(true)}>
          Add future billables
        </Button>
      </div>

      {/*
       * 🔴 **ABOVE THE VIEW SWITCH, NOT INSIDE IT — owner's instruction.** They
       * sit outside the table/sheet ternary below, so they are on screen on both
       * views: the counts describe the whole business, not the table's current
       * page, and hiding them on the Sheet view would put them behind a toggle in
       * the one view that cannot filter.
       *
       * ⛔ **DO NOT MOVE THEM INTO THE SECTION COMPONENTS NOW THAT THEY ARE PER
       * SECTION.** That is the obvious-looking tidy-up and it would break them:
       * `QuotesSection`, `InvoicesSection` and `FutureSection` are all on the
       * far side of `view === 'sheet' ? <TrackerSheetPanel/> : …` below, **and
       * the sheet is the DEFAULT view** — so the cards would vanish on the view
       * most readers open, which is exactly the half of the owner's instruction
       * that is easiest to lose. The row switches on `section` HERE, above the
       * view ternary, and that placement is the feature.
       *
       * ⛔ **AND DO NOT MOUNT `useAccountsStats` PER SECTION EITHER.** Both its
       * endpoints answer the whole dashboard whatever is on screen, so a
       * per-section mount would re-fire the pair on every chip press against a
       * budget shared across the office, for two answers that did not change.
       * The hook's own header carries the long version.
       *
       * ⚠️ *SUPERSEDED: "a 120-per-60s budget … rate-limiting whoever opens the
       * app next rather than whoever was clicking."* It is **12,000/60s**
       * (corrected 2026-09-17), so that consequence no longer follows. The
       * refusal stands on the waste, not on the arithmetic.
       *
       * ⚠️ **FULL SCREEN COVERS THEM, AND THAT IS CORRECT.** `TrackerSheetPanel`
       * goes `fixed inset-0` when expanded, so the cards are painted over rather
       * than stacked above — which is what "full screen" means. Nothing here
       * handles it and nothing should.
       *
       * ⚠️ **`statsLoading` AND `statsError`, NEVER THE COMBINED PAIR.**
       * `useAccountsStats` reads two independent endpoints and reports each
       * separately for exactly this reason: the combined `loading` stays true
       * while the OVERDUE list is in flight, so the cards would shimmer over
       * figures they already hold, and the combined `error` joins both messages,
       * so a failed overdue read would print a failure strip across four cards
       * that are perfectly correct. The overdue read's own failure is reported
       * where it belongs — by the chip's absence on a row, which is also what a
       * company with nothing overdue looks like, deliberately.
       *
       * ⚠️ **RENDERED IN EVERY STATE, INCLUDING WHEN THE ROUTES 404.** A `404`
       * is not an error here — this app deploys separately from the API and can
       * be newer than it — so `statsError` is null, `stats` is null, and the
       * cards draw a dash and "Not available" per card rather than four zeroes.
       * **Four zeroes and an outage are the same screen**, which is the whole
       * reason that distinction is held all the way from the hook to the tile.
       */}
      {/*
       * ⚠️ **`section` AND `sectionLabel` ARE DERIVED FROM ONE VALUE** (see
       * `statCards` above), so the row and the word above it cannot arrive
       * describing two different sections. `SECTION_LABEL` stays the one place a
       * section's name is spelled — see its own header.
       *
       * ⚠️ **STILL ON BOTH VIEWS.** On the sheet branch the row renders here,
       * fixed, exactly as before; on the Table view it is the `summary` block of
       * the section's layout below. Either way it is outside the section
       * components, which is the placement the paragraph above insists on.
       */}
      {onSheetBranch && <div className="mb-3">{statCards}</div>}

      {/*
       * ⚠️ **`key={section}` FORCES A FRESH IFRAME PER SECTION.** The only
       * difference between two sections' embeds is the `#gid=` FRAGMENT, and a
       * fragment-only change to a live iframe's `src` is not reliably a
       * navigation — the frame can keep showing the tab it was already on while
       * the attribute says otherwise. That is this feature's own bug surviving
       * its own fix, so the frame is remounted rather than re-pointed.
       */}
      {/*
       * ⚠️ **THE PLACEHOLDER IS TESTED FIRST, and only it can be reached while
       * the mirror's address is unknown** — so the table below is what a settled
       * "no mirror" answer falls to, never what an unsettled one flashes
       * through. Same ordering as `ClientDeliverablesPage`.
       */}
      {resolvingSheet ? (
        /*
         * ⚠️ The placeholder is wrapped in the SAME `MirrorPanel` the real one
         * is, so the card does not gain a footer strip a moment after it
         * paints. `mirrorFreshnessText` has its own `loading` branch — it reads
         * *"Checking when this was last synced…"* here — so the line says
         * something truthful rather than shimmering.
         */
        <MirrorPanel sheet={sheet}>
          <SheetPlaceholderPanel />
        </MirrorPanel>
      ) : view === 'sheet' && sheetUrl !== null && sheetEmbedUrl !== null ? (
        /*
         * 🔴 **THE EDIT URL, NOT THE PREVIEW — see `sheetEmbedUrl` above for the
         * owner's reversal and why it must not be undone.** `sheetUrl` is still
         * re-tested beside it because it is the GATE (and what narrows the type);
         * `sheetEmbedUrl` is what is framed.
         */
        /*
         * `key={section}` is load-bearing and is not a React housekeeping
         * detail: the deep link differs only in its `#gid=` FRAGMENT, and a
         * fragment-only change to a live iframe src is not reliably a
         * navigation. Without the key the frame keeps whichever tab it loaded
         * first, under the heading for a different one -- which is this
         * feature's own bug surviving its own fix.
         */
        /*
         * ⚠️ The key is on BOTH the wrapper and the panel since the freshness
         * footer moved inside the card. React does reconcile a single keyed
         * child, so the inner one alone would still remount the frame — but the
         * whole point of that key is that a MISSED remount is invisible (the
         * frame simply keeps the tab it had, under the new heading), and that is
         * not a property to leave resting on a reconciliation detail.
         */
        /*
         * 🔴 **THE KEY IS `sheetFrameKey`, NOT `section` — it carries the filter
         * view id too.** Applying a filter moves only the `#…&fvid=` FRAGMENT, so
         * without it the frame keeps the rows it had and Apply reads as a dead
         * button. See `sheetFrameKey` above.
         */
        <>
          {/*
           * ⚠️ **QUOTES AND INVOICES ONLY.** `future` is mirrored to a tab but
           * its filter set is not part of the filter-view contract
           * (`AccountsSheetFilterViewInput['section']` is the two), so it gets no
           * bar rather than one whose Apply could only ever 422.
           */}
          {section === 'quotes' ? (
            <QuoteSheetFilters filterView={filterView} summaryBar={summaryBar} />
          ) : section === 'invoices' ? (
            <InvoiceSheetFilters filterView={filterView} summaryBar={summaryBar} />
          ) : null}
          <MirrorPanel key={sheetFrameKey} sheet={sheet}>
            <TrackerSheetPanel
              frameTitle={`Accounts dashboard — ${SECTION_LABEL[section]} — in Google Sheets`}
              key={sheetFrameKey}
              embedUrl={sheetEmbedUrl}
              openUrl={sheetOpenUrl}
            />
          </MirrorPanel>
        </>
      ) : (
        /*
         * `gap="sm"` (gap-3) is the `mb-3` the stat-card row used to carry, so
         * the default layout is pixel-identical to the old stack.
         */
        <LayoutGrid
          layout={sectionLayout}
          gap="sm"
          render={{
            summary: () => statCards,
            list: () =>
              section === 'invoices' ? (
                <InvoicesSection overdue={overdue} summaryBar={summaryBar} />
              ) : section === 'future' ? (
                <FutureSection
                  reloadToken={reloadToken}
                  overdue={overdue}
                  summaryBar={summaryBar}
                  onPreview={quotePreview.open}
                />
              ) : (
                <QuotesSection
                  overdue={overdue}
                  summaryBar={summaryBar}
                  onPreview={quotePreview.open}
                  reloadToken={quotesReloadToken}
                />
              ),
          }}
        />
      )}

      {/*
       * ⚠️ **`onCreated` RE-READS rather than inserting the new row.** Where it
       * belongs is the server's answer: the list is sorted, paginated and
       * narrowed, so a row spliced in at the top would sit outside the server's
       * own ordering and a row that does not match the active filters would
       * appear on a page it has no business being on.
       *
       * Mounted unconditionally — the dialog unmounts its own form while closed,
       * so it reads nothing on an ordinary page load.
       *
       * 🔴 **`setSection('future')` ON SUCCESS, AND IT IS THE WHOLE REASON THE
       * MOVED BUTTON IS SAFE.** Since 2026-09-02 this dialog is reachable from
       * Quotes and Invoices too, and it creates a FUTURE BILLABLE row — which
       * appears in neither of those tables. Left as a bare reload, pressing it
       * from Quotes would close the dialog onto an unchanged Quotes table, and
       * the honest reading of that screen is *"my row was not saved"*. The
       * likely next move is to type it again, and a planned row has no natural
       * key, so the tracker would then hold the same expected money twice with
       * nothing anywhere refusing it.
       *
       * ⚠️ Chosen over wording the success message *"added to Future Billable"*
       * and staying put: this dialog closes on success, so that sentence would
       * have nowhere to live longer than a moment, and a person who missed it is
       * back at the same wrong reading. **Showing them the row beats telling
       * them where it went.**
       *
       * ⚠️ `setSection` also clears every other section's filter keys, which is
       * wanted here — the Future section shares the `companyId` key with the
       * other two, and carrying one in would land on a list narrowed to a
       * company that may not be the one just typed, hiding the new row at the
       * exact moment it is meant to be visible. On the Future section already,
       * it is the no-op it looks like.
       */}
      <FutureDocumentDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={() => {
          setAddOpen(false);
          setSection('future');
          setReloadToken((token) => token + 1);
        }}
      />

      {quotePreview.element}
    </>
  );
}
